// APEX v8.3 - Bright White Header + 8 Themes + Interactive Charts + TradingView Guide
import ReportIssue from './ReportIssue';
import GoLiveTracker from './GoLiveTracker';
import PaperTrades from './PaperTrades';
import ApexJournal from './ApexJournal';
import { TradingHUD, SignalScoreGauge, PositionProgressCard, MobileNav } from './components/TradingEnhancements';
import { MarkovStatusBar, TradeJournalTab as MarkovJournalTab, WinRateByRegime, AlertPanel } from './components/MarkovDashboard';
import React, { useState, useEffect, useRef, memo, useCallback } from 'react';

const API = '';
const THEMES = {
  dark: {
    bg:'#111111',panel:'#1a1a1a',border:'#2a2a2a',
    accent:'#f5f5f5',green:'#00c853',red:'#ff3d00',
    yellow:'#ffd600',purple:'#aa00ff',text:'#f5f5f5',
    muted:'#757575',dim:'#0a0a0a',dark:'#050505',name:'DARK'
  },
  midnight: {
    bg:'#0a1628',panel:'#101e36',border:'#1a2e4e',
    accent:'#0078d7',green:'#00e676',red:'#ff1744',
    yellow:'#ffab00',purple:'#7c4dff',text:'#e0e8f0',
    muted:'#5a7a9a',dim:'#060e1c',dark:'#040a14',name:'MIDNIGHT'
  },
  slate: {
    bg:'#18181b',panel:'#27272a',border:'#3f3f46',
    accent:'#22d3ee',green:'#4ade80',red:'#f87171',
    yellow:'#fbbf24',purple:'#a78bfa',text:'#fafafa',
    muted:'#71717a',dim:'#09090b',dark:'#050505',name:'SLATE'
  },
  charcoal: {
    bg:'#1c1c1e',panel:'#2c2c2e',border:'#3a3a3c',
    accent:'#0a84ff',green:'#30d158',red:'#ff453a',
    yellow:'#ffd60a',purple:'#bf5af2',text:'#f2f2f7',
    muted:'#8e8e93',dim:'#0e0e10',dark:'#050506',name:'CHARCOAL'
  }
}
let C = THEMES.dark; // default - updated by theme toggle
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&family=Bebas+Neue&family=Rajdhani:wght@600;700&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
body{background:${C.bg};color:${C.text};font-family:'Rajdhani',sans-serif}
.mono{font-family:'JetBrains Mono',monospace}
.card{background:${C.panel};border:1px solid ${C.border};border-radius:8px;padding:14px}
@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}
@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
@keyframes slideIn{from{opacity:0;transform:translateX(16px)}to{opacity:1;transform:translateX(0)}}
@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:${C.bg}}::-webkit-scrollbar-thumb{background:${C.border}}
.mobile-nav{display:none!important}@media(max-width:768px){.mobile-nav{display:flex!important}}
.light-mode{--lbg:#f0f4f8;--lpanel:#ffffff;--lborder:#d1dce8;--ltext:#1a1a2e;--lmuted:#6b7c93}
.light-mode .card{background:#ffffff!important;border-color:#d1dce8!important;color:#1a1a2e!important}
.light-mode .mono{color:#1a1a2e}
body.light{background:#f0f4f8;color:#1a1a2e}
button,input,select{font-family:'Rajdhani',sans-serif}input,select{outline:none}
select option{background:${C.panel};color:${C.text}}
`;

function beep(type){
  if(typeof window==='undefined')return;
  try{
    const ctx=new(window.AudioContext||window.webkitAudioContext)();
    if(type==='CALL'){
      // CALL: Rising 3-note chime (positive, ascending)
      [523,659,784].forEach((freq,i)=>{
        const o=ctx.createOscillator(),g=ctx.createGain();
        o.connect(g);g.connect(ctx.destination);
        o.type='sine';
        o.frequency.setValueAtTime(freq,ctx.currentTime+i*0.15);
        g.gain.setValueAtTime(0,ctx.currentTime+i*0.15);
        g.gain.linearRampToValueAtTime(0.25,ctx.currentTime+i*0.15+0.02);
        g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+i*0.15+0.3);
        o.start(ctx.currentTime+i*0.15);
        o.stop(ctx.currentTime+i*0.15+0.3);
      });
    }else{
      // PUT: Low double-pulse alert (urgent, descending)
      [440,330].forEach((freq,i)=>{
        const o=ctx.createOscillator(),g=ctx.createGain();
        o.connect(g);g.connect(ctx.destination);
        o.type='square';
        o.frequency.setValueAtTime(freq,ctx.currentTime+i*0.2);
        g.gain.setValueAtTime(0,ctx.currentTime+i*0.2);
        g.gain.linearRampToValueAtTime(0.15,ctx.currentTime+i*0.2+0.02);
        g.gain.exponentialRampToValueAtTime(0.001,ctx.currentTime+i*0.2+0.18);
        o.start(ctx.currentTime+i*0.2);
        o.stop(ctx.currentTime+i*0.2+0.2);
      });
    }
  }catch(e){}
}

function ema(c,p){if(!c?.length)return 0;const k=2/(p+1);let e=c[0];for(let i=1;i<c.length;i++)e=c[i]*k+e*(1-k);return e;}
function rsi(c,p=14){if(!c||c.length<p+1)return 50;let g=0,l=0;for(let i=c.length-p;i<c.length;i++){const d=c[i]-c[i-1];d>0?g+=d:l-=d;}return 100-100/(1+g/(l||0.0001));}
function vwap(cs){if(!cs?.length)return 0;let cv=0,ct=0;cs.forEach(c=>{const t=(c.h+c.l+c.c)/3;ct+=t*(c.v||1);cv+=(c.v||1);});return cv>0?ct/cv:0;}
function macd(c){return ema(c,12)-ema(c,26);}
function boll(c,p=20){if(!c||c.length<p)return{upper:0,mid:0,lower:0};const sl=c.slice(-p),mid=sl.reduce((a,b)=>a+b,0)/p,std=Math.sqrt(sl.reduce((a,b)=>a+(b-mid)**2,0)/p);return{upper:mid+2*std,mid,lower:mid-2*std};}

function mlScore(price,v,e9,e21,r,m,b,changePct,candles,allInd){
  let score=50;const signals=[];
  const now=new Date();
  const hour=now.getHours();
  const min=now.getMinutes();
  const timeScore=hour*60+min;
  const isPrimeWindow=(timeScore>=600&&timeScore<=720);
  const isOpenWindow=(timeScore>=570&&timeScore<=600);
  const isLunchChop=(timeScore>720&&timeScore<780);

  // Time of day
  if(isPrimeWindow){score+=18;signals.push('Prime window (9:45am-12:30pm) - highest win rate');}
  else if(isOpenWindow){score+=8;signals.push('Market open - wait for direction');}
  else if(isLunchChop){score-=15;signals.push('Lunch chop (12-1pm) - avoid new entries');}
  else if(hour>=13&&hour<16){score+=5;signals.push('Afternoon - valid but lower conviction');}

  // VWAP analysis
  const vd=((price-v)/v)*100;
  if(Math.abs(vd)<0.05){score+=20;signals.push('At VWAP - ideal reclaim/retest zone');}
  else if(Math.abs(vd)<0.15){score+=15;signals.push('Near VWAP - watch for reclaim');}
  else if(price>v&&vd<0.5){score+=12;signals.push('Above VWAP - bullish structure');}
  else if(price>v){score+=8;signals.push('Above VWAP - bullish bias');}
  else if(price<v&&Math.abs(vd)<0.3){score+=5;signals.push('Below VWAP - watch for reclaim');}
  else{score-=10;signals.push('Extended from VWAP - wait for retest');}

  // EMA alignment
  if(e9>e21&&price>e9){score+=18;signals.push('EMA9>21 + price above - strong uptrend');}
  else if(e9<e21&&price<e9){score+=18;signals.push('EMA9<21 + price below - strong downtrend');}
  else if(e9>e21){score+=10;signals.push('EMA9 crossed above 21 - turning bullish');}
  else if(e9<e21){score+=5;signals.push('EMA9 below 21 - bearish structure');}

  // RSI
  if(r>70){score+=12;signals.push('RSI overbought ('+r.toFixed(0)+') - put setup');}
  else if(r<30){score+=12;signals.push('RSI oversold ('+r.toFixed(0)+') - call reversal');}
  else if(r>=45&&r<=65){score+=10;signals.push('RSI momentum zone ('+r.toFixed(0)+')');}
  else{score+=5;}

  // MACD
  if(m>0&&m>0.1){score+=8;signals.push('MACD positive - buyers in control');}
  else if(m<0&&m<-0.1){score+=8;signals.push('MACD negative - sellers in control');}
  else{score+=3;}

  // Bollinger
  if(b?.lower&&price<b.lower){score+=8;signals.push('Below lower BB - oversold bounce likely');}
  else if(b?.upper&&price>b.upper){score+=8;signals.push('Above upper BB - overextended');}

  //  NEW: VOLUME SPIKE DETECTION 
  if(candles&&candles.length>=20){
    const recent=candles.slice(-20);
    const avgVol=recent.reduce((a,c)=>a+(c.v||0),0)/recent.length;
    const lastVol=candles[candles.length-1]?.v||0;
    const volRatio=avgVol>0?lastVol/avgVol:1;
    if(volRatio>2.5){score+=12;signals.push('VOLUME SPIKE '+volRatio.toFixed(1)+'x avg - strong conviction');}
    else if(volRatio>1.8){score+=8;signals.push('Above avg volume '+volRatio.toFixed(1)+'x - good confirmation');}
    else if(volRatio<0.5){score-=8;signals.push('LOW VOLUME '+volRatio.toFixed(1)+'x - weak signal, reduce size');}
  }

  //  NEW: SPY/QQQ CORRELATION FILTER 
  if(allInd){
    const spySignal=allInd['SPY']?.signal?.type;
    const qqqSignal=allInd['QQQ']?.signal?.type;
    if(spySignal&&qqqSignal){
      if(spySignal===qqqSignal&&spySignal!=='NEUTRAL'){
        score+=10;signals.push('SPY+QQQ aligned ('+spySignal+') - high conviction');
      }else if(spySignal!==qqqSignal&&spySignal!=='NEUTRAL'&&qqqSignal!=='NEUTRAL'){
        score-=12;signals.push('SPY/QQQ DIVERGENCE - reduced confidence');
      }
    }
  }

  //  NEW: MULTI-TIMEFRAME (5-min trend from last 5 candles) 
  if(candles&&candles.length>=10){
    const c5=candles.filter((_,i)=>i%5===0).slice(-5);
    if(c5.length>=3){
      const fiveMinTrend=c5[c5.length-1].c-c5[0].c;
      const oneMinTrend=price-(candles[candles.length-3]?.c||price);
      // If 1-min signal agrees with 5-min trend, boost
      if((fiveMinTrend>0&&oneMinTrend>0)||(fiveMinTrend<0&&oneMinTrend<0)){
        score+=8;signals.push('Multi-timeframe aligned - 1m+5m agree');
      }else if((fiveMinTrend>0&&oneMinTrend<0)||(fiveMinTrend<0&&oneMinTrend>0)){
        score-=6;signals.push('1m vs 5m conflict - lower probability');
      }
    }
  }

  // Momentum
  if(Math.abs(changePct)>1.5){score+=5;signals.push('Strong momentum ('+changePct.toFixed(1)+'%)');}

  // Time-of-day caps (prevents conflicting signals)
  if(isLunchChop){score=Math.min(score,35);} // Lunch: hard cap at 35
  if(isOpenWindow){score=Math.min(score,40);} // First 30 min: cap at 40
  if(timeScore>945){score=Math.min(score,30);} // After 3:45: cap at 30

  const clamped=Math.min(95,Math.max(15,score));
  const grade=clamped>=80?'A':clamped>=65?'B':clamped>=50?'C':'D';
  const label=clamped>=80?'HIGH CONVICTION':clamped>=65?'MODERATE':clamped>=50?'LOW':'WEAK';
  const color=clamped>=80?C.green:clamped>=65?C.yellow:clamped>=50?C.accent:C.muted;
  return{score:clamped,grade,label,color,signals:signals.slice(0,4)};
}

function getSignal(price,v,e9,e21,r,m,b,candles){
  let bull=0,bear=0;
  if(price>v)bull++;else bear++;
  if(e9>e21)bull++;else bear++;
  if(r<40)bull++;else if(r>62)bear++;
  if(m>0)bull++;else bear++;
  if(b?.lower&&price<b.lower)bull++;
  if(b?.upper&&price>b.upper)bear++;
  // Volume confirmation
  if(candles&&candles.length>=20){
    const avg=candles.slice(-20).reduce((a,c)=>a+(c.v||0),0)/20;
    const cur=candles[candles.length-1]?.v||0;
    if(avg>0&&cur/avg>1.8){bull>bear?bull++:bear++;}
    if(avg>0&&cur/avg<0.4){bull=Math.max(bull-1,0);bear=Math.max(bear-1,0);}
  }
  if(bull>=3)return{type:'CALL',label:'CALL',color:C.green,score:bull};
  if(bear>=3)return{type:'PUT',label:'PUT',color:C.red,score:bear};
  return{type:'NEUTRAL',label:'WAIT',color:C.muted,score:0};
}

function getSetup(type,price,v,e9,r){
  if(type==="CALL"){
    const entry=price+0.02;
    const stopDist=price*0.003;
    const stop=entry-stopDist;
    const t1=entry+stopDist*1.5;
    const t2=entry+stopDist*3;
    return{entry:entry.toFixed(2),stop:stop.toFixed(2),t1:t1.toFixed(2),t2:t2.toFixed(2),risk:stopDist.toFixed(2),rr:"1.5:1 / 3:1",quality:r<50?"STRONG":r<60?"MODERATE":"WEAK",note:"Enter above $"+price.toFixed(2)+". Scale 50% at T1, trail to T2."};
  }else if(type==="PUT"){
    const entry=price-0.02;
    const stopDist=price*0.003;
    const stop=entry+stopDist;
    const t1=entry-stopDist*1.5;
    const t2=entry-stopDist*3;
    return{entry:entry.toFixed(2),stop:stop.toFixed(2),t1:t1.toFixed(2),t2:t2.toFixed(2),risk:stopDist.toFixed(2),rr:"1.5:1 / 3:1",quality:r>55?"STRONG":r>45?"MODERATE":"WEAK",note:"Enter below $"+price.toFixed(2)+". Scale 50% at T1, trail to T2."};
  }
  return null;
}

function calc(candles){
  if(!candles?.length)return null;
  const closes=candles.map(c=>c.c);
  const price=closes[closes.length-1]||0,v=vwap(candles);
  const e9=ema(closes,9),e21=ema(closes,21),r=rsi(closes),m=macd(closes),b=boll(closes);
  const sig=getSignal(price,v,e9,e21,r,m,b,candles);
  const open=candles[0]?.o||price,chg=((price-open)/open)*100;
  const ml=mlScore(price,v,e9,e21,r,m,b,chg,candles,null);
  const setup=sig.type!=='NEUTRAL'?getSetup(sig.type,price,v,e9,r):null;
  return{price,vwap:v,ema9:e9,ema21:e21,rsi:r,macd:m,bb:b,signal:sig,changePct:chg,ml,setup};
}

//  Backtester Engine 
function runBacktest(candles, params) {
  if (!candles?.length || candles.length < 30) return null;
  const { rrRatio = 1.5, stopPct = 0.2, minRsi = 35, maxRsi = 65, requireVwap = true } = params;
  const trades = [];
  let inTrade = null;

  for (let i = 30; i < candles.length - 1; i++) {
    const window = candles.slice(Math.max(0, i - 50), i + 1);
    const closes = window.map(c => c.c);
    const price = closes[closes.length - 1];
    const v = vwap(window);
    const e9 = ema(closes, 9);
    const e21 = ema(closes, 21);
    const r = rsi(closes);
    const m = macd(closes);
    const b = boll(closes);

    // Exit logic
    if (inTrade) {
      const { type, entry, stop, target } = inTrade;
      if (type === 'CALL') {
        if (price <= stop) { trades.push({ ...inTrade, exit: price, result: 'LOSS', pnl: -(entry - stop), exitBar: i }); inTrade = null; }
        else if (price >= target) { trades.push({ ...inTrade, exit: price, result: 'WIN', pnl: target - entry, exitBar: i }); inTrade = null; }
      } else {
        if (price >= stop) { trades.push({ ...inTrade, exit: price, result: 'LOSS', pnl: -(stop - entry), exitBar: i }); inTrade = null; }
        else if (price <= target) { trades.push({ ...inTrade, exit: price, result: 'WIN', pnl: entry - target, exitBar: i }); inTrade = null; }
      }
      // Force exit after 15 bars
      if (inTrade && i - inTrade.entryBar >= 15) {
        const pnl = inTrade.type === 'CALL' ? price - inTrade.entry : inTrade.entry - price;
        trades.push({ ...inTrade, exit: price, result: pnl >= 0 ? 'WIN' : 'LOSS', pnl, exitBar: i, forced: true });
        inTrade = null;
      }
    }

    if (inTrade) continue;

    const sig = getSignal(price, v, e9, e21, r, m, b, window);
    if (sig.type === 'NEUTRAL') continue;
    if (r < minRsi || r > maxRsi) {
      if (sig.type === 'CALL' && r < minRsi) {}
      else if (sig.type === 'PUT' && r > maxRsi) {}
      else continue;
    }
    if (requireVwap) {
      if (sig.type === 'CALL' && price < v) continue;
      if (sig.type === 'PUT' && price > v) continue;
    }

    const stopDist = price * (stopPct / 100);
    const targetDist = stopDist * rrRatio;
    const stop_price = sig.type === 'CALL' ? price - stopDist : price + stopDist;
    const target_price = sig.type === 'CALL' ? price + targetDist : price - targetDist;

    inTrade = {
      type: sig.type, entry: price, stop: stop_price, target: target_price,
      entryBar: i, entryTime: candles[i].t, rsi: r, vwap: v
    };
  }

  if (!trades.length) return null;

  const wins = trades.filter(t => t.result === 'WIN');
  const losses = trades.filter(t => t.result === 'LOSS');
  const totalPnl = trades.reduce((s, t) => s + t.pnl, 0);
  const avgWin = wins.length ? wins.reduce((s, t) => s + t.pnl, 0) / wins.length : 0;
  const avgLoss = losses.length ? Math.abs(losses.reduce((s, t) => s + t.pnl, 0) / losses.length) : 0;
  const profitFactor = avgLoss > 0 ? (wins.length * avgWin) / (losses.length * avgLoss) : wins.length > 0 ? 999 : 0;
  const winRate = (wins.length / trades.length) * 100;
  const maxDD = Math.min(...trades.map((_, i) => trades.slice(0, i + 1).reduce((s, t) => s + t.pnl, 0)));

  return {
    trades, totalTrades: trades.length, wins: wins.length, losses: losses.length,
    winRate: winRate.toFixed(1), totalPnl: totalPnl.toFixed(2),
    avgWin: avgWin.toFixed(2), avgLoss: avgLoss.toFixed(2),
    profitFactor: profitFactor.toFixed(2), maxDD: maxDD.toFixed(2),
    expectancy: ((winRate / 100) * avgWin - (1 - winRate / 100) * avgLoss).toFixed(2)
  };
}

//  Backtester UI 
function BacktesterTab({ symbols, candlesRef }) {
  window.addEventListener('apexBugReport', () => setShowReportIssue(true));
  const [showReportIssue, setShowReportIssue] = useState(false);
  const [sym, setSym] = useState(symbols[0] || 'SPY');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [histCandles, setHistCandles] = useState([]);
  const [params, setParams] = useState({ rrRatio: 1.5, stopPct: 0.2, minRsi: 30, maxRsi: 70, requireVwap: true });
  const [activeTab, setActiveTab] = useState('summary');

  const fetchHistory = useCallback(async (symbol) => {
    setLoading(true);
    setResult(null);
    try {
      const r = await fetch(`${API}/api/finnhub/token`);
      const d = await r.json();
      if (!d.token) { setLoading(false); return; }
      const to = Math.floor(Date.now() / 1000);
      const from = to - 30 * 24 * 60 * 60;
      const cr = await fetch(`${API}/api/candles?symbol=${symbol}&resolution=5&from=${from}&to=${to}`);
      const cd = await cr.json();
      if (cd.s === 'ok' && cd.t?.length > 10) {
        const candles = cd.t.map((t, i) => ({ t, o: cd.o[i], h: cd.h[i], l: cd.l[i], c: cd.c[i], v: cd.v[i] }));
        setHistCandles(candles);
        const res = runBacktest(candles, params);
        setResult(res);
      } else {
        // Use sim data as fallback
        const sim = candlesRef.current[symbol] || [];
        if (sim.length > 30) {
          setHistCandles(sim);
          setResult(runBacktest(sim, params));
        }
      }
    } catch (e) {
      const sim = candlesRef.current[symbol] || [];
      if (sim.length > 30) { setHistCandles(sim); setResult(runBacktest(sim, params)); }
    }
    setLoading(false);
  }, [params]);

  const runTest = () => fetchHistory(sym);

  const statBox = (label, value, color, sub) => (
    <div style={{ background: C.dim, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14, textAlign: 'center' }}>
      <div style={{ fontSize: 9, color: C.muted, fontFamily: 'JetBrains Mono', letterSpacing: 2, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'JetBrains Mono', color }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ animation: 'fadeIn .3s ease' }}>
      {/* Controls */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 14 }}>BACKTEST CONFIGURATION</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>SYMBOL</div>
            <select value={sym} onChange={e => setSym(e.target.value)} style={{ width: '100%', padding: '8px 12px', borderRadius: 4, background: C.dim, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: 'JetBrains Mono' }}>
              {symbols.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>R:R RATIO</div>
            <select value={params.rrRatio} onChange={e => setParams(p => ({ ...p, rrRatio: parseFloat(e.target.value) }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 4, background: C.dim, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: 'JetBrains Mono' }}>
              {[1, 1.5, 2, 2.5, 3].map(v => <option key={v} value={v}>{v}:1</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>STOP LOSS %</div>
            <select value={params.stopPct} onChange={e => setParams(p => ({ ...p, stopPct: parseFloat(e.target.value) }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 4, background: C.dim, border: `1px solid ${C.border}`, color: C.text, fontSize: 13, fontFamily: 'JetBrains Mono' }}>
              {[0.1, 0.15, 0.2, 0.25, 0.3, 0.5].map(v => <option key={v} value={v}>{v}%</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>REQUIRE VWAP</div>
            <button onClick={() => setParams(p => ({ ...p, requireVwap: !p.requireVwap }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 4, border: `1px solid ${params.requireVwap ? C.accent : C.border}`, background: params.requireVwap ? `${C.accent}18` : 'transparent', color: params.requireVwap ? C.accent : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>
              {params.requireVwap ? 'YES - VWAP REQUIRED' : 'NO - ANY SIGNAL'}
            </button>
          </div>
        </div>
        <button onClick={runTest} disabled={loading} style={{ width: '100%', padding: '12px 0', borderRadius: 6, border: 'none', background: loading ? C.muted : `linear-gradient(135deg, ${C.accent}, ${C.purple})`, color: C.dark, fontSize: 13, fontWeight: 900, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'JetBrains Mono', letterSpacing: 2 }}>
          {loading ? 'R RUNNING BACKTEST...' : ' RUN BACKTEST - LAST 30 DAYS'}
        </button>
      </div>

      {loading && (
        <div className="card" style={{ textAlign: 'center', padding: 48 }}>
          <div style={{ fontSize: 32, color: C.accent, animation: 'spin 1s linear infinite', display: 'inline-block', marginBottom: 16 }}>R</div>
          <div style={{ fontSize: 14, color: C.muted }}>Fetching 30 days of {sym} data and simulating trades...</div>
        </div>
      )}

      {result && !loading && (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10, marginBottom: 16 }}>
            {statBox('WIN RATE', `${result.winRate}%`, parseFloat(result.winRate) >= 50 ? C.green : C.red, `${result.wins}W / ${result.losses}L`)}
            {statBox('TOTAL P&L', `$${result.totalPnl}`, parseFloat(result.totalPnl) >= 0 ? C.green : C.red, `${result.totalTrades} trades`)}
            {statBox('PROFIT FACTOR', result.profitFactor, parseFloat(result.profitFactor) >= 1.5 ? C.green : parseFloat(result.profitFactor) >= 1 ? C.yellow : C.red, '> 1.5 is good')}
            {statBox('EXPECTANCY', `$${result.expectancy}`, parseFloat(result.expectancy) >= 0 ? C.green : C.red, 'per trade')}
            {statBox('AVG WIN', `$${result.avgWin}`, C.green, 'per winning trade')}
            {statBox('AVG LOSS', `$${result.avgLoss}`, C.red, 'per losing trade')}
            {statBox('MAX DRAWDOWN', `$${result.maxDD}`, C.red, 'consecutive losses')}
            {statBox('TOTAL TRADES', result.totalTrades, C.accent, '30-day period')}
          </div>

          {/* Strategy Grade */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 6 }}>STRATEGY ASSESSMENT - {sym} VWAP/EMA STRATEGY</div>
                <div style={{ fontSize: 13, color: C.text, lineHeight: 1.8 }}>
                  {parseFloat(result.winRate) >= 55 && parseFloat(result.profitFactor) >= 1.5
                    ? `v PROFITABLE STRATEGY: ${result.winRate}% win rate with ${result.profitFactor} profit factor. This strategy has a positive edge on ${sym}. Continue trading with discipline.`
                    : parseFloat(result.profitFactor) >= 1
                    ? `~ MARGINAL EDGE: Profitable but thin edge. Consider tightening entry filters or increasing R:R ratio. Try requiring VWAP confirmation.`
                    : `x NEEDS REFINEMENT: This parameter set is not profitable over the last 30 days. Try adjusting stop loss, R:R ratio, or adding VWAP filter.`}
                </div>
              </div>
              <div style={{ fontSize: 48, fontFamily: 'Bebas Neue', color: parseFloat(result.winRate) >= 55 ? C.green : parseFloat(result.winRate) >= 45 ? C.yellow : C.red, marginLeft: 20 }}>
                {parseFloat(result.winRate) >= 55 ? 'A' : parseFloat(result.winRate) >= 45 ? 'B' : 'C'}
              </div>
            </div>
          </div>

          {/* Trade List */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2 }}>TRADE LOG ({result.trades.length} trades)</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {['all', 'wins', 'losses'].map(t => (
                  <button key={t} onClick={() => setActiveTab(t)} style={{ padding: '4px 12px', borderRadius: 4, border: `1px solid ${activeTab === t ? C.accent : C.border}`, background: activeTab === t ? `${C.accent}18` : 'transparent', color: activeTab === t ? C.accent : C.muted, fontSize: 10, cursor: 'pointer', fontFamily: 'JetBrains Mono', textTransform: 'uppercase' }}>{t}</button>
                ))}
              </div>
            </div>
            <div style={{ maxHeight: 320, overflowY: 'auto' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '80px 80px 80px 80px 80px 60px 60px', gap: 0, padding: '6px 8px', borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, background: C.panel }}>
                {['TYPE', 'ENTRY', 'EXIT', 'TARGET', 'STOP', 'P&L', 'RESULT'].map(h => (
                  <div key={h} className="mono" style={{ fontSize: 8, color: C.muted, letterSpacing: 1 }}>{h}</div>
                ))}
              </div>
              {result.trades
                .filter(t => activeTab === 'all' || (activeTab === 'wins' && t.result === 'WIN') || (activeTab === 'losses' && t.result === 'LOSS'))
                .slice(0, 50)
                .map((t, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 80px 80px 80px 80px 60px 60px', gap: 0, padding: '7px 8px', borderBottom: `1px solid ${C.border}22`, background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,.01)' }}>
                    <div className="mono" style={{ fontSize: 11, color: t.type === 'CALL' ? C.green : C.red, fontWeight: 700 }}>{t.type === 'CALL' ? '+' : '-'}{t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</div>
                    <div className="mono" style={{ fontSize: 11, color: C.text }}>${t.entry?.toFixed(2)}</div>
                    <div className="mono" style={{ fontSize: 11, color: C.text }}>${t.exit?.toFixed(2)}</div>
                    <div className="mono" style={{ fontSize: 11, color: C.green }}>${t.target?.toFixed(2)}</div>
                    <div className="mono" style={{ fontSize: 11, color: C.red }}>${t.stop?.toFixed(2)}</div>
                    <div className="mono" style={{ fontSize: 11, color: t.pnl >= 0 ? C.green : C.red, fontWeight: 700 }}>{t.pnl >= 0 ? '+' : ''}${t.pnl?.toFixed(2)}</div>
                    <div className="mono" style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, textAlign: 'center', background: t.result === 'WIN' ? `${C.green}18` : `${C.red}18`, color: t.result === 'WIN' ? C.green : C.red }}>{t.result}</div>
                  </div>
                ))}
            </div>
          </div>
        </>
      )}

      {!result && !loading && (
        <div className="card" style={{ padding: 48, textAlign: 'center' }}>
          <div style={{ fontSize: 32, color: C.muted, marginBottom: 12 }}></div>
          <div style={{ fontSize: 14, color: C.muted, marginBottom: 8 }}>Configure your parameters above and click Run Backtest</div>
          <div style={{ fontSize: 11, color: C.dim }}>Tests VWAP + EMA9/21 + RSI strategy against 30 days of 5-minute candles</div>
        </div>
      )}
    </div>
  );
}

//  Webull Order Panel 
function WebullOrderPanel({ sym, type, price, setup, onClose }) {
  const [chain, setChain] = useState([]);
  const [expirations, setExpirations] = useState([]);
  const [expiry, setExpiry] = useState('');
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [orderType, setOrderType] = useState('limit');
  const [limitPrice, setLimitPrice] = useState('');
  const [loading, setLoading] = useState(true);
  const col = type === 'CALL' ? C.green : C.red;

  useEffect(() => {
    fetch(`${API}/api/options/expirations?symbol=${sym}&sandbox=false`)
      .then(r => r.json()).then(d => {
        const dates = d.expirations || [];
        setExpirations(dates);
        if (dates[0]) setExpiry(dates[0]);
      }).catch(() => setLoading(false));
  }, [sym]);

  useEffect(() => {
    if (!expiry) return;
    setLoading(true);
    fetch(`${API}/api/options/chain?symbol=${sym}&expiration=${expiry}&sandbox=false`)
      .then(r => r.json()).then(d => {
        const opts = type === 'CALL' ? (d.calls || []) : (d.puts || []);
        const sorted = opts.sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price));
        setChain(sorted.slice(0, 12));
        const best = sorted.find(o => type === 'CALL' ? o.strike >= price : o.strike <= price) || sorted[0];
        if (best) { setSelected(best); setLimitPrice(((best.bid + best.ask) / 2).toFixed(2)); }
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [expiry, sym, type, price]);

  const dte = expiry ? Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const cost = selected ? ((parseFloat(limitPrice) || 0) * qty * 100).toFixed(2) : '0.00';
  const webullStr = selected ? `Symbol: ${selected.symbol || sym}\nAction: BUY TO OPEN\nQty: ${qty} contract${qty > 1 ? 's' : ''}\nType: ${orderType === 'limit' ? 'LIMIT' : 'MARKET'}${orderType === 'limit' ? '\nLimit: $' + limitPrice : ''}\nExpiry: ${expiry} (${dte} DTE)\nStrike: $${selected.strike} ${type}\nEst. Cost: $${cost}${setup ? '\nEntry: $' + setup.entry + '\nTarget 1: $' + setup.t1 + '\nStop Loss: $' + setup.stop : ''}` : '';

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.88)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn .2s ease' }}>
      <div style={{ background: C.panel, border: `1px solid ${col}44`, borderRadius: 12, width: 620, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: col }}>{type} ORDER - {sym} @ ${price?.toFixed(2)}</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Options Chain + Webull Order Fields</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer' }}>x</button>
        </div>
        <div style={{ padding: 20 }}>
          {setup && (
            <div style={{ background: C.dim, border: `1px solid ${col}33`, borderRadius: 8, padding: 14, marginBottom: 16 }}>
              <div className="mono" style={{ fontSize: 9, color: col, letterSpacing: 2, marginBottom: 10 }}>APEX TRADE SETUP</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8 }}>
                {[['ENTRY', `$${setup.entry}`, col], ['STOP', `$${setup.stop}`, C.red], ['TARGET 1', `$${setup.t1}`, C.green], ['TARGET 2', `$${setup.t2}`, C.green]].map(([l, v, c]) => (
                  <div key={l} style={{ background: C.panel, borderRadius: 4, padding: '8px 10px', textAlign: 'center' }}>
                    <div style={{ fontSize: 8, color: C.muted, letterSpacing: 1, marginBottom: 3 }}>{l}</div>
                    <div className="mono" style={{ fontSize: 12, fontWeight: 700, color: c }}>{v}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 10, lineHeight: 1.7 }}>{setup.note}</div>
            </div>
          )}
          <div style={{ marginBottom: 16 }}>
            <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>EXPIRATION</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {expirations.slice(0, 6).map(d => {
                const days = Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
                const tag = days === 0 ? '0DTE' : days <= 3 ? `${days}DTE` : days <= 7 ? 'WEEKLY' : `${days}D`;
                return (
                  <button key={d} onClick={() => setExpiry(d)} style={{ padding: '6px 12px', borderRadius: 4, border: `1px solid ${expiry === d ? col : C.border}`, background: expiry === d ? `${col}18` : 'transparent', color: expiry === d ? col : C.muted, fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono', textAlign: 'center' }}>
                    {d}<br/><span style={{ fontSize: 9, opacity: 0.7 }}>{tag}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 24, color: C.muted }}>
              <div style={{ animation: 'spin 1s linear infinite', display: 'inline-block', marginBottom: 8, fontSize: 20 }}>R</div>
              <div>Loading options chain...</div>
            </div>
          ) : chain.length > 0 ? (
            <div style={{ marginBottom: 16 }}>
              <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>OPTIONS CHAIN - {type}S</div>
              <div style={{ background: C.dim, borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '6px 12px', borderBottom: `1px solid ${C.border}` }}>
                  {['STRIKE', 'BID', 'ASK', 'MID', 'VOL', 'OI', 'IV'].map(h => <div key={h} className="mono" style={{ fontSize: 8, color: C.muted }}>{h}</div>)}
                </div>
                {chain.map((opt, i) => {
                  const mid = ((opt.bid || 0) + (opt.ask || 0)) / 2;
                  const isSel = selected?.strike === opt.strike;
                  const isATM = Math.abs(opt.strike - price) < 0.5;
                  return (
                    <div key={i} onClick={() => { setSelected(opt); setLimitPrice(mid.toFixed(2)); }} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr 1fr', padding: '7px 12px', cursor: 'pointer', background: isSel ? `${col}15` : isATM ? 'rgba(255,255,255,.02)' : 'transparent', borderBottom: `1px solid ${C.border}22`, borderLeft: isSel ? `3px solid ${col}` : '3px solid transparent' }}>
                      <div className="mono" style={{ fontSize: 11, fontWeight: 700, color: isATM ? C.yellow : C.text }}>${opt.strike}{isATM ? ' *' : ''}</div>
                      <div className="mono" style={{ fontSize: 11, color: C.red }}>${opt.bid?.toFixed(2) || '--'}</div>
                      <div className="mono" style={{ fontSize: 11, color: C.green }}>${opt.ask?.toFixed(2) || '--'}</div>
                      <div className="mono" style={{ fontSize: 11, color: col }}>${mid.toFixed(2)}</div>
                      <div className="mono" style={{ fontSize: 11, color: C.text }}>{opt.volume?.toLocaleString() || '--'}</div>
                      <div className="mono" style={{ fontSize: 11, color: C.muted }}>{opt.oi?.toLocaleString() || '--'}</div>
                      <div className="mono" style={{ fontSize: 11, color: C.purple }}>{opt.iv ? (opt.iv * 100).toFixed(0) + '%' : '--'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ padding: 16, textAlign: 'center', color: C.muted, fontSize: 12, marginBottom: 16 }}>No chain data - enter order manually in Webull</div>
          )}
          <div style={{ background: C.dim, border: `1px solid ${col}33`, borderRadius: 8, padding: 16, marginBottom: 16 }}>
            <div className="mono" style={{ fontSize: 9, color: col, letterSpacing: 2, marginBottom: 14 }}>WEBULL ORDER FIELDS</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>CONTRACTS</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[1, 2, 3, 5, 10].map(n => (
                    <button key={n} onClick={() => setQty(n)} style={{ flex: 1, padding: '8px 0', borderRadius: 4, border: `1px solid ${qty === n ? col : C.border}`, background: qty === n ? `${col}18` : 'transparent', color: qty === n ? col : C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>{n}</button>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>ORDER TYPE</div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[['LIMIT', 'limit'], ['MARKET', 'market']].map(([l, v]) => (
                    <button key={v} onClick={() => setOrderType(v)} style={{ flex: 1, padding: '8px 0', borderRadius: 4, border: `1px solid ${orderType === v ? col : C.border}`, background: orderType === v ? `${col}18` : 'transparent', color: orderType === v ? col : C.muted, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>{l}</button>
                  ))}
                </div>
              </div>
            </div>
            {orderType === 'limit' && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>LIMIT PRICE</div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input value={limitPrice} onChange={e => setLimitPrice(e.target.value)} style={{ flex: 1, padding: '10px 14px', borderRadius: 4, background: C.panel, border: `1px solid ${col}44`, color: col, fontSize: 16, fontFamily: 'JetBrains Mono', fontWeight: 700 }} placeholder="0.00" />
                  {selected && <div style={{ fontSize: 10, color: C.muted, textAlign: 'center' }}><div>Bid: ${selected.bid?.toFixed(2)}</div><div>Ask: ${selected.ask?.toFixed(2)}</div></div>}
                </div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
              {[['EST. COST', `$${cost}`, C.yellow], ['MAX LOSS', `$${cost}`, C.red], ['CONTRACTS', `${qty}x100`, C.accent]].map(([l, v, c]) => (
                <div key={l} style={{ background: C.panel, borderRadius: 4, padding: '8px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 8, color: C.muted, letterSpacing: 1, marginBottom: 3 }}>{l}</div>
                  <div className="mono" style={{ fontSize: 13, fontWeight: 700, color: c }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 6, padding: 12, position: 'relative', marginBottom: 14 }}>
              <pre className="mono" style={{ fontSize: 11, color: C.text, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{webullStr}</pre>
              <button onClick={() => navigator.clipboard.writeText(webullStr)} style={{ position: 'absolute', top: 8, right: 8, padding: '4px 10px', borderRadius: 4, border: `1px solid ${C.accent}44`, background: `${C.accent}11`, color: C.accent, fontSize: 10, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>COPY</button>
            </div>
            <div style={{ background: C.panel, borderRadius: 6, padding: 12 }}>
              <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>HOW TO ENTER IN WEBULL</div>
              {[`1. Open Webull -> Search ${sym}`, `2. Tap Options -> Select ${expiry || 'expiry'}`, `3. Choose ${type} -> Strike $${selected?.strike || '---'}`, `4. Tap BUY -> ${orderType === 'limit' ? 'Set Limit $' + limitPrice : 'Market Order'}`, `5. Qty: ${qty} contract${qty > 1 ? 's' : ''} -> Review -> Submit`, `6. Set stop alert at $${setup?.stop || '---'}`, `7. Take 50% at $${setup?.t1 || '---'}`].map((s, i) => (
                <div key={i} style={{ fontSize: 11, color: i === 0 ? C.text : C.muted, padding: '3px 0' }}>{s}</div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={onClose} style={{ flex: 1, padding: '12px 0', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>CLOSE</button>
            <button onClick={() => { navigator.clipboard.writeText(webullStr); alert('Copied! Open Webull and enter the order.'); }} style={{ flex: 2, padding: '12px 0', borderRadius: 6, border: 'none', background: col, color: C.dark, fontSize: 13, fontWeight: 900, cursor: 'pointer', letterSpacing: 1, fontFamily: 'JetBrains Mono' }}>COPY ORDER -> WEBULL</button>
          </div>
        </div>
      </div>
    </div>
  );
}

//  Scanner Card 
//  Volatility Speedometer (BlackBoxStocks-style) 
function VolatilityDial({ ind }) {
  if (!ind) return null;
  const rsi = ind.rsi || 50;
  const macd = ind.macd || 0;
  const vwapDist = ind.vwapDist || 0;

  // Calculate volatility score 0-100
  const rsiScore = Math.abs(rsi - 50) * 2; // 0-100
  const macdScore = Math.min(100, Math.abs(macd) * 10);
  const vwapScore = Math.min(100, Math.abs(vwapDist) * 5);
  const vol = Math.round((rsiScore * 0.4 + macdScore * 0.3 + vwapScore * 0.3));
  const clamped = Math.min(100, Math.max(0, vol));

  const label = clamped > 75 ? 'EXTREME' : clamped > 55 ? 'HIGH' : clamped > 35 ? 'MODERATE' : 'LOW';
  const col = clamped > 75 ? C.red : clamped > 55 ? C.yellow : clamped > 35 ? C.accent : C.green;

  // SVG arc for speedometer
  const angle = -140 + (clamped / 100) * 280; // -140 to +140 degrees
  const rad = angle * Math.PI / 180;
  const cx = 60, cy = 60, r = 44;
  const nx = cx + r * Math.cos(rad), ny = cy + r * Math.sin(rad);

  // Arc path
  const startAngle = -140 * Math.PI / 180;
  const endAngle = (clamped / 100 * 280 - 140) * Math.PI / 180;
  const largeArc = clamped > 50 ? 1 : 0;
  const sx = cx + r * Math.cos(startAngle), sy = cy + r * Math.sin(startAngle);
  const ex = cx + r * Math.cos(endAngle), ey = cy + r * Math.sin(endAngle);

  return (
    <div style={{display:'flex', alignItems:'center', gap:12, background:C.dim, borderRadius:8, padding:'8px 12px', marginBottom:8}}>
      <svg width={120} height={80} viewBox="0 0 120 80">
        {/* Background arc */}
        <path d={`M ${cx + r * Math.cos(-140*Math.PI/180)} ${cy + r * Math.sin(-140*Math.PI/180)} A ${r} ${r} 0 1 1 ${cx + r * Math.cos(140*Math.PI/180)} ${cy + r * Math.sin(140*Math.PI/180)}`} fill="none" stroke={C.border} strokeWidth={6} strokeLinecap="round"/>
        {/* Colored arc */}
        {clamped > 0 && <path d={`M ${sx} ${sy} A ${r} ${r} 0 ${largeArc} 1 ${ex} ${ey}`} fill="none" stroke={col} strokeWidth={6} strokeLinecap="round" opacity={0.9}/>}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={cx + 36 * Math.cos(rad)} y2={cy + 36 * Math.sin(rad)} stroke={col} strokeWidth={2.5} strokeLinecap="round"/>
        <circle cx={cx} cy={cy} r={4} fill={col}/>
        {/* Labels */}
        <text x={22} y={74} fill={C.muted} fontSize={7} fontFamily="JetBrains Mono">LOW</text>
        <text x={84} y={74} fill={C.muted} fontSize={7} fontFamily="JetBrains Mono">HIGH</text>
        <text x={cx} y={cy + 16} fill={col} fontSize={10} fontWeight="bold" fontFamily="JetBrains Mono" textAnchor="middle">{clamped}</text>
      </svg>
      <div>
        <div className="mono" style={{fontSize:9, color:C.muted, letterSpacing:1, marginBottom:2}}>VOLATILITY</div>
        <div style={{fontSize:16, fontWeight:700, color:col, fontFamily:'JetBrains Mono'}}>{label}</div>
        <div className="mono" style={{fontSize:9, color:C.muted, marginTop:2}}>RSI {rsi?.toFixed(0)} . MACD {macd?.toFixed(2)}</div>
      </div>
    </div>
  );
}

//  Interactive Candle Chart 
function ChartModal({ sym, candles, ind, onClose }) {
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({x:0, y:0});
  const [drawings, setDrawings] = useState([]);
  const [drawMode, setDrawMode] = useState(null); // null | 'hline' | 'trendline'
  const [drawStart, setDrawStart] = useState(null);
  const [zoom, setZoom] = useState(120);
  const [timeframe, setTimeframe] = useState(1);
  const [chartSource, setChartSource] = useState('apex');
  const [showEMA, setShowEMA] = useState(true);
  const [showVWAP, setShowVWAP] = useState(true);
  const [showBB, setShowBB] = useState(false);
  const [showVolume, setShowVolume] = useState(true);

  if (!candles?.length) return null;

  // Aggregate candles by timeframe
  const aggregated = timeframe > 1 ? (() => {
    const agg = [];
    for (let i = 0; i < candles.length; i += timeframe) {
      const chunk = candles.slice(i, i + timeframe);
      if (chunk.length === 0) continue;
      agg.push({
        t: chunk[0].t,
        o: chunk[0].o,
        h: Math.max(...chunk.map(c => c.h)),
        l: Math.min(...chunk.map(c => c.l)),
        c: chunk[chunk.length - 1].c,
        v: chunk.reduce((a, c) => a + (c.v || 0), 0)
      });
    }
    return agg;
  })() : candles;
  const rc = aggregated.slice(-zoom);
  const prices = rc.flatMap(c => [c.h, c.l]).filter(p => p > 0);
  if (!prices.length) return null;

  const maxP = Math.max(...prices), minP = Math.min(...prices);
  const range = (maxP - minP) || maxP * 0.01;
  const pad = 10, w = 880, h = 300, volH = 40;
  const cw = (w - pad * 2) / rc.length;
  const toY = p => pad + ((maxP - p) / range) * (h - pad * 2);
  const toP = y => maxP - ((y - pad) / (h - pad * 2)) * range;

  const v = vwap(rc);
  const closes = rc.map(c => c.c);
  const e9 = ema(closes, 9);
  const e21 = ema(closes, 21);
  const bData = boll(closes);
  const maxVol = Math.max(...rc.map(c => c.v || 0));

  // Entry/exit annotation from setup
  const setup = ind?.setup;
  const sigType = ind?.signal?.type;
  const sigCol = sigType === 'CALL' ? C.green : sigType === 'PUT' ? C.red : null;

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * w;
    const y = ((e.clientY - rect.top) / rect.height) * h;
    const idx = Math.min(rc.length-1, Math.max(0, Math.floor((x-pad)/cw)));
    setHovered(idx);
    setMousePos({x, y, price: toP(y)});
  };

    const handleWheel = (e) => {
    e.preventDefault();
    setZoom(z => {
      const newZ = e.deltaY > 0 ? Math.min(300, z + 15) : Math.max(20, z - 15);
      return newZ;
    });
  };

  const handleClick = (e) => {
    if (!drawMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * w;
    const y = ((e.clientY - rect.top) / rect.height) * h;
    const price = toP(y);
    const idx = Math.min(rc.length-1, Math.max(0, Math.floor((x-pad)/cw)));
    if (drawMode === 'hline') {
      setDrawings(d => [...d, {type:'hline', price, color:C.yellow}]);
    } else if (drawMode === 'trendline') {
      if (!drawStart) {
        setDrawStart({x, y, idx, price});
      } else {
        setDrawings(d => [...d, {type:'trendline', x1:drawStart.x, y1:drawStart.y, x2:x, y2:y, color:C.accent}]);
        setDrawStart(null);
        setDrawMode(null);
      }
    }
  };

  const hc = hovered !== null ? rc[hovered] : rc[rc.length-1];
  const hTime = hc ? new Date(hc.t*1000).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}) : '';
  const pctChg = hc && rc[0] ? (((hc.c - rc[0].c) / rc[0].c) * 100).toFixed(2) : 0;

  return (
    <div onClick={e => e.target===e.currentTarget && onClose()} style={{position:'fixed',inset:0,background:'rgba(0,0,0,.93)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{background:C.panel,border:`2px solid ${C.accent}44`,borderRadius:14,width:'96vw',maxWidth:1040,padding:18}}>

        {/* Header */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
          <div>
            <div style={{fontFamily:'Bebas Neue',fontSize:28,color:C.accent,letterSpacing:3,lineHeight:1}}>{sym} LIVE CHART</div>
            <div className="mono" style={{fontSize:9,color:C.muted}}>{timeframe===1?'1-MIN':timeframe===5?'5-MIN':timeframe===15?'15-MIN':'1-HOUR'} . {rc.length} CANDLES . INTERACTIVE</div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
            {/* OHLCV display */}
            <div style={{background:C.dim,borderRadius:6,padding:'6px 12px',textAlign:'right'}}>
              <div className="mono" style={{fontSize:9,color:C.muted}}>{hTime}</div>
              <div className="mono" style={{fontSize:20,fontWeight:700,color:hc?.c>=hc?.o?C.green:C.red}}>${hc?.c?.toFixed(2)}</div>
              <div className="mono" style={{fontSize:9,color:C.muted}}>O:{hc?.o?.toFixed(2)} H:{hc?.h?.toFixed(2)} L:{hc?.l?.toFixed(2)}</div>
              <div className="mono" style={{fontSize:9,color:parseFloat(pctChg)>=0?C.green:C.red}}>{parseFloat(pctChg)>=0?'+':''}{pctChg}%</div>
            </div>
            <button onClick={onClose} style={{background:'none',border:'none',color:C.muted,fontSize:24,cursor:'pointer',padding:4}}>X</button>
          </div>
        </div>

        {/* Toolbar */}
        <div style={{display:'flex',gap:8,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>

          {/* Chart source toggle */}
          <div style={{display:'flex',gap:4}}>
            <button onClick={()=>setChartSource('apex')} style={{padding:'3px 8px',borderRadius:3,border:'1px solid '+(chartSource==='apex'?C.accent:C.border),background:chartSource==='apex'?C.accent+'18':'transparent',color:chartSource==='apex'?C.accent:C.muted,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono'}}>APEX</button>
            <button onClick={()=>setChartSource('tv')} style={{padding:'3px 8px',borderRadius:3,border:'1px solid '+(chartSource==='tv'?C.yellow:C.border),background:chartSource==='tv'?C.yellow+'18':'transparent',color:chartSource==='tv'?C.yellow:C.muted,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono'}}>TRADINGVIEW</button>
          </div>
          <div style={{width:1,height:16,background:C.border}}/>
          {/* Timeframe */}
          <div style={{display:'flex',gap:4}}>
            <span className="mono" style={{fontSize:9,color:C.muted,alignSelf:'center'}}>TF:</span>
            {[['1m',1],['5m',5],['15m',15],['1h',60]].map(([label,mins]) => (
              <button key={label} onClick={()=>setTimeframe(mins)} style={{padding:'3px 8px',borderRadius:3,border:`1px solid ${timeframe===mins?C.yellow:C.border}`,background:timeframe===mins?`${C.yellow}18`:'transparent',color:timeframe===mins?C.yellow:C.muted,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{label}</button>
            ))}
          </div>
          <div style={{width:1,height:16,background:C.border}}/>
          {/* Zoom */}
          <div style={{display:'flex',gap:4}}>
            <span className="mono" style={{fontSize:9,color:C.muted,alignSelf:'center'}}>ZOOM:</span>
            {[30,60,90,120,180].map(z => (
              <button key={z} onClick={()=>setZoom(z)} style={{padding:'3px 8px',borderRadius:3,border:`1px solid ${zoom===z?C.accent:C.border}`,background:zoom===z?`${C.accent}18`:'transparent',color:zoom===z?C.accent:C.muted,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{z}</button>
            ))}
          </div>
          <div style={{width:1,height:16,background:C.border}}/>
          {/* Indicators */}
          {[['EMA',showEMA,setShowEMA,C.accent],['VWAP',showVWAP,setShowVWAP,C.yellow],['BOLL',showBB,setShowBB,C.purple],['VOL',showVolume,setShowVolume,C.muted]].map(([l,on,set,col])=>(
            <button key={l} onClick={()=>set(s=>!s)} style={{padding:'3px 8px',borderRadius:3,border:`1px solid ${on?col:C.border}`,background:on?`${col}18`:'transparent',color:on?col:C.muted,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{l}</button>
          ))}
          <div style={{width:1,height:16,background:C.border}}/>
          {/* Draw tools */}
          {[['H-LINE','hline'],['TREND','trendline']].map(([l,mode])=>(
            <button key={mode} onClick={()=>setDrawMode(drawMode===mode?null:mode)} style={{padding:'3px 8px',borderRadius:3,border:`1px solid ${drawMode===mode?C.yellow:C.border}`,background:drawMode===mode?`${C.yellow}18`:'transparent',color:drawMode===mode?C.yellow:C.muted,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{l}</button>
          ))}
          {drawings.length>0 && <button onClick={()=>{setDrawings([]);setDrawStart(null);}} style={{padding:'3px 8px',borderRadius:3,border:`1px solid ${C.red}`,background:`${C.red}10`,color:C.red,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono'}}>CLEAR</button>}
          <span className="mono" style={{fontSize:8,color:C.muted,marginLeft:'auto'}}>{drawMode?`CLICK TO DRAW ${drawMode.toUpperCase()} . ESC TO CANCEL`:'Click chart to draw lines'}</span>
        </div>

        {/* Signal annotations bar */}
        {setup && sigCol && (
          <div style={{background:`${sigCol}10`,border:`1px solid ${sigCol}33`,borderRadius:6,padding:'6px 12px',marginBottom:10,display:'flex',gap:20,alignItems:'center',flexWrap:'wrap'}}>
            <div className="mono" style={{fontSize:9,color:sigCol,letterSpacing:2}}>{sigType} SETUP ACTIVE</div>
            {/* Risk/Reward Calculator */}
              {setup.entry && setup.stop && setup.t1 && (()=>{
                const e=parseFloat(setup.entry),s=parseFloat(setup.stop),t=parseFloat(setup.t1);
                const risk=Math.abs(e-s)*100,reward=Math.abs(t-e)*100;
                const rr=risk>0?(reward/risk).toFixed(1):'--';
                return(
                  <div style={{display:'flex',gap:8,marginBottom:8,flexWrap:'wrap'}}>
                    <div style={{background:C.red+'12',border:'1px solid '+C.red+'33',borderRadius:6,padding:'4px 10px',flex:1,textAlign:'center'}}>
                      <div className="mono" style={{fontSize:7,color:C.muted}}>MAX RISK</div>
                      <div className="mono" style={{fontSize:13,color:C.red,fontWeight:700}}>-${Math.round(risk)}</div>
                    </div>
                    <div style={{background:C.green+'12',border:'1px solid '+C.green+'33',borderRadius:6,padding:'4px 10px',flex:1,textAlign:'center'}}>
                      <div className="mono" style={{fontSize:7,color:C.muted}}>POTENTIAL PROFIT</div>
                      <div className="mono" style={{fontSize:13,color:C.green,fontWeight:700}}>+${Math.round(reward)}</div>
                    </div>
                    <div style={{background:C.accent+'12',border:'1px solid '+C.accent+'33',borderRadius:6,padding:'4px 10px',flex:1,textAlign:'center'}}>
                      <div className="mono" style={{fontSize:7,color:C.muted}}>RISK:REWARD</div>
                      <div className="mono" style={{fontSize:13,color:C.accent,fontWeight:700}}>{rr}:1</div>
                    </div>
                  </div>
                );
              })()}
              {[['ENTRY',setup.entry,sigCol],['STOP',setup.stop,C.red],['TARGET 1',setup.t1,C.green],['TARGET 2',setup.t2,C.green]].map(([l,v2,c])=>(
              <div key={l} style={{display:'flex',gap:6,alignItems:'center'}}>
                <div style={{width:16,height:2,background:c,opacity:0.8}}/>
                <span style={{fontSize:10,color:C.muted}}>{l}:</span>
                <span className="mono" style={{fontSize:11,fontWeight:700,color:c}}>${v2}</span>
              </div>
            ))}
          </div>
        )}

        {/* MAIN CHART */}
        {chartSource==='tv' ? (
          <div style={{width:'100%',height:500,borderRadius:8,overflow:'hidden',border:'1px solid '+C.border}}>
            <iframe src={'https://s.tradingview.com/widgetembed/?symbol='+sym+'&interval='+(timeframe===1?'1':timeframe===5?'5':timeframe===15?'15':'60')+'&theme='+(C.bg.startsWith('#0')||C.bg.startsWith('#1')?'dark':'light')+'&style=1&locale=en&toolbar_bg='+encodeURIComponent(C.panel)+'&enable_publishing=false&hide_side_toolbar=false&allow_symbol_change=true&save_image=false'} style={{width:'100%',height:'100%',border:'none'}} title='TradingView'/>
          </div>
        ) : <svg width="100%" height={h + (showVolume ? volH+4 : 0)} viewBox={`0 0 ${w} ${h + (showVolume ? volH+4 : 0)}`}
          style={{display:'block',cursor:drawMode?'crosshair':'crosshair',background:C.dim,borderRadius:8}}
          onMouseMove={handleMouseMove}
          onWheel={handleWheel}
          
          onMouseLeave={()=>setHovered(null)}
          onClick={handleClick}
        >
          {/* Grid */}
          {[0.2,0.4,0.6,0.8].map(f=>{
            const gy=pad+f*(h-pad*2);
            const gp=maxP-f*range;
            return <g key={f}><line x1={pad} y1={gy} x2={w-pad} y2={gy} stroke={C.border} strokeWidth={0.5} opacity={0.4}/><text x={w-pad-2} y={gy-2} fill={C.muted} fontSize={8} textAnchor="end" fontFamily="JetBrains Mono">${gp.toFixed(2)}</text></g>;
          })}
          {/* Time labels */}
          {rc.filter((_,i)=>i%Math.floor(rc.length/6)===0).map((c,i)=>{
            const x=pad+(rc.indexOf(c))*cw+cw/2;
            const t=new Date(c.t*1000).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'});
            return <text key={i} x={x} y={h-2} fill={C.muted} fontSize={7} textAnchor="middle" fontFamily="JetBrains Mono">{t}</text>;
          })}

          {/* Bollinger Bands */}
          {showBB && bData && (() => {
            const uY=toY(bData.upper), lY=toY(bData.lower), mY=toY(bData.mid);
            return <>
              <line x1={pad} y1={uY} x2={w-pad} y2={uY} stroke={C.purple} strokeWidth={1} strokeDasharray="4,3" opacity={0.6}/>
              <line x1={pad} y1={lY} x2={w-pad} y2={lY} stroke={C.purple} strokeWidth={1} strokeDasharray="4,3" opacity={0.6}/>
              <line x1={pad} y1={mY} x2={w-pad} y2={mY} stroke={C.purple} strokeWidth={0.5} opacity={0.4}/>
            </>;
          })()}

          {/* VWAP */}
          {showVWAP && <><line x1={pad} y1={toY(v)} x2={w-pad} y2={toY(v)} stroke={C.yellow} strokeWidth={1.5} strokeDasharray="6,4" opacity={0.85}/><text x={pad+4} y={toY(v)-3} fill={C.yellow} fontSize={8} fontFamily="JetBrains Mono">VWAP ${v?.toFixed(2)}</text></>}

          {/* EMA lines */}
          {showEMA && rc.length > 9 && (() => {
            const pts9=rc.map((_,i)=>{const sl=rc.slice(Math.max(0,i-8),i+1);return `${pad+i*cw+cw/2},${toY(sl.reduce((a,c)=>a+c.c,0)/sl.length)}`;}).join(' ');
            const pts21=rc.map((_,i)=>{const sl=rc.slice(Math.max(0,i-20),i+1);return `${pad+i*cw+cw/2},${toY(sl.reduce((a,c)=>a+c.c,0)/sl.length)}`;}).join(' ');
            return <><polyline points={pts9} fill="none" stroke={C.accent} strokeWidth={1.5} opacity={0.8}/><polyline points={pts21} fill="none" stroke={C.purple} strokeWidth={1} opacity={0.6}/></>;
          })()}

          {/* Setup level lines on chart */}
          {setup && sigCol && [
            [setup.entry, sigCol, 'ENTRY', '8,4'],
            [setup.stop, C.red, 'STOP', '4,4'],
            [setup.t1, C.green, 'T1', '6,3'],
            [setup.t2, C.green, 'T2', '6,3'],
          ].map(([price, col, label, dash]) => {
            if (!price) return null;
            const py = toY(parseFloat(price));
            if (py < pad || py > h-pad) return null;
            return <g key={label}>
              <line x1={pad} y1={py} x2={w-pad} y2={py} stroke={col} strokeWidth={label==='STOP'?1.5:2} strokeDasharray={dash} opacity={0.9}/>
              <rect x={pad+4} y={py-9} width={label.length*7+8} height={14} fill={col} opacity={0.9} rx={2}/>
              <text x={pad+8} y={py+2} fill="#000" fontSize={9} fontFamily="JetBrains Mono" fontWeight="bold">{label} ${price}</text>
            </g>;
          })}

                    {/* EMA crossover markers */}
          {showEMA && rc.length > 21 && rc.map((c, i) => {
            if (i < 21 || i === 0) return null;
            const sl1 = rc.slice(Math.max(0,i-8),i+1);
            const sl2 = rc.slice(Math.max(0,i-20),i+1);
            const e9Now = sl1.reduce((a,c)=>a+c.c,0)/sl1.length;
            const e21Now = sl2.reduce((a,c)=>a+c.c,0)/sl2.length;
            const sl1p = rc.slice(Math.max(0,i-9),i);
            const sl2p = rc.slice(Math.max(0,i-21),i);
            const e9Prev = sl1p.length ? sl1p.reduce((a,c)=>a+c.c,0)/sl1p.length : e9Now;
            const e21Prev = sl2p.length ? sl2p.reduce((a,c)=>a+c.c,0)/sl2p.length : e21Now;
            const crossUp = e9Prev <= e21Prev && e9Now > e21Now;
            const crossDown = e9Prev >= e21Prev && e9Now < e21Now;
            if (!crossUp && !crossDown) return null;
            const cx = pad + i * cw + cw/2;
            const cy = toY(c.c);
            return <g key={'cross'+i}>
              <polygon points={crossUp ? `${cx},${cy-12} ${cx-5},${cy-4} ${cx+5},${cy-4}` : `${cx},${cy+12} ${cx-5},${cy+4} ${cx+5},${cy+4}`} fill={crossUp ? C.green : C.red} opacity={0.9}/>
              <text x={cx} y={crossUp ? cy-14 : cy+18} fill={crossUp ? C.green : C.red} fontSize={7} textAnchor="middle" fontFamily="JetBrains Mono">{crossUp ? 'BULL X' : 'BEAR X'}</text>
            </g>;
          })}
          {/* User drawings */}
          {drawings.map((d,i) => d.type==='hline' ? (
            <g key={i}>
              <line x1={pad} y1={toY(d.price)} x2={w-pad} y2={toY(d.price)} stroke={d.color} strokeWidth={1.5} strokeDasharray="6,3" opacity={0.8}/>
              <text x={w-pad-4} y={toY(d.price)-3} fill={d.color} fontSize={8} textAnchor="end" fontFamily="JetBrains Mono">${d.price?.toFixed(2)}</text>
            </g>
          ) : (
            <line key={i} x1={d.x1} y1={d.y1} x2={d.x2} y2={d.y2} stroke={d.color} strokeWidth={1.5} opacity={0.8}/>
          ))}

          {/* Candles */}
          {rc.map((c,i)=>{
            const x=pad+i*cw;
            const bull=c.c>=c.o;
            const col=bull?C.green:C.red;
            const bT=toY(Math.max(c.o,c.c));
            const bB=toY(Math.min(c.o,c.c));
            const isHov=hovered===i;
            return (
              <g key={i} opacity={hovered!==null&&!isHov?0.3:1}>
                {isHov&&<rect x={x} y={pad} width={cw} height={h-pad*2} fill={col} opacity={0.07}/>}
                <line x1={x+cw/2} y1={toY(c.h)} x2={x+cw/2} y2={toY(c.l)} stroke={col} strokeWidth={isHov?2:0.9}/>
                <rect x={x+0.5} y={bT} width={Math.max(1.5,cw-1)} height={Math.max(1,bB-bT)} fill={col} opacity={isHov?1:0.85} rx={0.5}/>
              </g>
            );
          })}

                    {/* Current price line */}
          {rc.length > 0 && (() => {
            const lastPrice = rc[rc.length-1].c;
            const py = toY(lastPrice);
            const isUp = rc[rc.length-1].c >= rc[rc.length-1].o;
            return <g>
              <line x1={pad} y1={py} x2={w-pad} y2={py} stroke={isUp?C.green:C.red} strokeWidth={1} strokeDasharray="2,2" opacity={0.6}/>
              <rect x={w-pad-58} y={py-8} width={56} height={16} fill={isUp?C.green:C.red} rx={3}/>
              <text x={w-pad-5} y={py+4} fill="#000" fontSize={9} textAnchor="end" fontFamily="JetBrains Mono" fontWeight="bold">${lastPrice.toFixed(2)}</text>
            </g>;
          })()}

          {/* Crosshair */}
          {hovered!==null && <>
            <line x1={pad+hovered*cw+cw/2} y1={pad} x2={pad+hovered*cw+cw/2} y2={h-pad} stroke={C.muted} strokeWidth={0.8} strokeDasharray="3,2" opacity={0.5}/>
            <line x1={pad} y1={mousePos.y} x2={w-pad} y2={mousePos.y} stroke={C.muted} strokeWidth={0.8} strokeDasharray="3,2" opacity={0.5}/>
            <rect x={w-pad-52} y={mousePos.y-8} width={50} height={14} fill={C.panel} rx={2}/>
            <text x={w-pad-2} y={mousePos.y+3} fill={C.text} fontSize={8} textAnchor="end" fontFamily="JetBrains Mono">${mousePos.price?.toFixed(2)}</text>
          </>}

          {/* In-progress trendline */}
          {drawStart && hovered!==null && (
            <line x1={drawStart.x} y1={drawStart.y} x2={pad+hovered*cw+cw/2} y2={mousePos.y} stroke={C.accent} strokeWidth={1.5} strokeDasharray="4,2" opacity={0.7}/>
          )}

          {/* Volume bars */}
          {showVolume && maxVol > 0 && rc.map((c,i)=>{
            const x=pad+i*cw;
            const bull=c.c>=c.o;
            const barH=((c.v||0)/maxVol)*volH;
            return <rect key={i} x={x+0.5} y={h+4+(volH-barH)} width={Math.max(1,cw-1)} height={barH} fill={bull?C.green:C.red} opacity={0.5}/>;
          })}
        </svg>}

        {/* Footer legend */}
        <div style={{display:'flex',gap:16,marginTop:8,alignItems:'center',flexWrap:'wrap'}}>
          {[['EMA9',C.accent],['EMA21',C.purple],['VWAP',C.yellow],['BB',C.purple]].map(([l,c])=>(
            <div key={l} style={{display:'flex',gap:4,alignItems:'center'}}>
              <div style={{width:20,height:2,background:c}}/>
              <span style={{fontSize:9,color:C.muted,fontFamily:'JetBrains Mono'}}>{l}</span>
            </div>
          ))}
          <span style={{fontSize:9,color:C.muted,fontFamily:'JetBrains Mono',marginLeft:'auto'}}>Keys: E=EMA V=VWAP B=BB H=H-Line T=Trend C=Clear +/-=Zoom ESC=Close . Scroll=Zoom</span>
        </div>
      </div>
    </div>
  );
}

function InteractiveChart({ candles, vwapVal }) {
  const [hovered, setHovered] = useState(null);
  const [mouseX, setMouseX] = useState(0);

  if (!candles?.length) return <div style={{ height: 120, background: C.dim, borderRadius: 4, marginBottom: 8 }}/>;

  const rc = candles.slice(-50);
  const prices = rc.flatMap(c => [c.h, c.l]).filter(p => p > 0);
  if (!prices.length) return <div style={{ height: 120, background: C.dim, borderRadius: 4, marginBottom: 8 }}/>;

  const maxP = Math.max(...prices), minP = Math.min(...prices);
  const range = (maxP - minP) || maxP * 0.01 || 1;
  const pad = 4, w = 300, h = 120;
  const cw = (w - pad * 2) / rc.length;
  const toY = p => pad + ((maxP - p) / range) * (h - pad * 2 - 12);
  const vy = toY(vwapVal || vwap(rc));

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * w;
    const idx = Math.min(rc.length - 1, Math.max(0, Math.floor((x - pad) / cw)));
    setHovered(idx);
    setMouseX(x);
  };

  const hc = hovered !== null ? rc[hovered] : null;
  const time = hc ? new Date(hc.t * 1000).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : null;

  return (
    <div style={{ position: 'relative', borderRadius: 6, overflow: 'hidden', background: C.dim, marginBottom: 8 }}>
      {/* Hover tooltip */}
      {hc && (
        <div style={{ position: 'absolute', top: 2, left: mouseX > 180 ? 'auto' : Math.max(0, mouseX - 10), right: mouseX > 180 ? 2 : 'auto', background: 'rgba(0,0,0,.9)', border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 8px', zIndex: 10, pointerEvents: 'none', whiteSpace: 'nowrap' }}>
          <div className="mono" style={{ fontSize: 9, color: C.muted }}>{time}</div>
          <div className="mono" style={{ fontSize: 10, color: C.green }}>H: ${hc.h?.toFixed(2)}</div>
          <div className="mono" style={{ fontSize: 10, color: C.red }}>L: ${hc.l?.toFixed(2)}</div>
          <div className="mono" style={{ fontSize: 10, color: hc.c >= hc.o ? C.green : C.red, fontWeight: 700 }}>C: ${hc.c?.toFixed(2)}</div>
        </div>
      )}
      <svg
        width="100%" height={h}
        viewBox={`0 0 ${w} ${h}`}
        style={{ display: 'block', cursor: 'crosshair' }}
        onMouseMove={handleMouseMove}
          
        onMouseLeave={() => setHovered(null)}
      >
        {/* VWAP line */}
        <line x1={pad} y1={vy} x2={w - pad} y2={vy} stroke={C.yellow} strokeWidth={1.2} strokeDasharray="4,3" opacity={0.8}/>

        {/* EMA line approximation */}
        {rc.length > 9 && (() => {
          const emaPoints = rc.map((_, i) => {
            const slice = rc.slice(Math.max(0, i-8), i+1);
            const e = slice.reduce((acc, c) => acc + c.c, 0) / slice.length;
            return `${pad + i * cw + cw/2},${toY(e)}`;
          }).join(' ');
          return <polyline points={emaPoints} fill="none" stroke={C.accent} strokeWidth={1} opacity={0.6}/>;
        })()}

        {/* Candles */}
        {rc.map((c, i) => {
          const x = pad + i * cw;
          const bull = c.c >= c.o;
          const col2 = bull ? C.green : C.red;
          const bT = toY(Math.max(c.o, c.c));
          const bB = toY(Math.min(c.o, c.c));
          const isHov = hovered === i;
          return (
            <g key={i} opacity={hovered !== null && !isHov ? 0.4 : 1}>
              {/* Wick */}
              <line x1={x + cw/2} y1={toY(c.h)} x2={x + cw/2} y2={toY(c.l)} stroke={col2} strokeWidth={isHov ? 1.5 : 0.8} />
              {/* Body */}
              <rect
                x={x + 1} y={bT}
                width={Math.max(1.5, cw - 2)}
                height={Math.max(1, bB - bT)}
                fill={col2}
                opacity={isHov ? 1 : 0.85}
                rx={0.5}
              />
              {/* Hover highlight */}
              {isHov && <rect x={x} y={pad} width={cw} height={h - pad*2} fill={col2} opacity={0.08} rx={1}/>}
            </g>
          );
        })}

        {/* Crosshair vertical line on hover */}
        {hovered !== null && (
          <line
            x1={pad + hovered * cw + cw/2} y1={pad}
            x2={pad + hovered * cw + cw/2} y2={h - pad}
            stroke={C.muted} strokeWidth={0.8} strokeDasharray="3,2" opacity={0.6}
          />
        )}

        {/* Price labels on right */}
        <text x={w - 2} y={toY(maxP) + 3} fill={C.muted} fontSize={7} textAnchor="end" fontFamily="JetBrains Mono">${maxP.toFixed(1)}</text>
        <text x={w - 2} y={toY(minP) + 3} fill={C.muted} fontSize={7} textAnchor="end" fontFamily="JetBrains Mono">${minP.toFixed(1)}</text>
        <text x={w - 2} y={vy - 2} fill={C.yellow} fontSize={7} textAnchor="end" fontFamily="JetBrains Mono">V</text>
      </svg>
    </div>
  );
}


//  DAILY PERFORMANCE DASHBOARD 
function DailyDashboard({ pnl, wins, losses, trades }) {
  const total = wins + losses;
  const winRate = total > 0 ? Math.round(wins/total*100) : 0;
  const streak = trades.length > 0 ? (() => {
    let s = 0, type = trades[0]?.result;
    for (const t of trades) { if (t.result === type) s++; else break; }
    return { count: s, type };
  })() : { count: 0, type: 'NONE' };

  return (
    <div style={{background:'linear-gradient(90deg,'+C.panel+','+(pnl>=0?C.green:C.red)+'08)',border:'1px solid '+(pnl>=0?C.green:C.red)+'22',borderRadius:10,padding:'10px 18px',marginBottom:14,display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
      <div style={{display:'flex',alignItems:'center',gap:6}}>
        <span className="mono" style={{fontSize:9,color:C.muted,letterSpacing:1}}>TODAY P&L</span>
        <span className="mono" style={{fontSize:18,fontWeight:700,color:pnl>=0?C.green:C.red}}>{pnl>=0?'+':''}${pnl.toFixed(0)}</span>
      </div>
      <div style={{width:1,height:20,background:C.border}}/>
      <div style={{display:'flex',gap:12}}>
        <div><span className="mono" style={{fontSize:9,color:C.muted}}>TRADES </span><span className="mono" style={{fontSize:12,color:C.text,fontWeight:700}}>{total}</span></div>
        <div><span className="mono" style={{fontSize:9,color:C.green}}>W </span><span className="mono" style={{fontSize:12,color:C.green,fontWeight:700}}>{wins}</span></div>
        <div><span className="mono" style={{fontSize:9,color:C.red}}>L </span><span className="mono" style={{fontSize:12,color:C.red,fontWeight:700}}>{losses}</span></div>
        <div><span className="mono" style={{fontSize:9,color:C.muted}}>RATE </span><span className="mono" style={{fontSize:12,color:winRate>=55?C.green:winRate>=45?C.yellow:C.red,fontWeight:700}}>{winRate}%</span></div>
      </div>
      {streak.count >= 2 && <>
        <div style={{width:1,height:20,background:C.border}}/>
        <span className="mono" style={{fontSize:10,color:streak.type==='WIN'?C.green:C.red,fontWeight:700}}>{streak.count}x {streak.type} STREAK</span>
      </>}
      {losses >= 2 && <>
        <div style={{width:1,height:20,background:C.border}}/>
        <span className="mono" style={{fontSize:10,color:C.red,fontWeight:700,padding:'2px 8px',borderRadius:4,background:C.red+'18'}}>STOP TRADING - 2 LOSSES</span>
      </>}
    </div>
  );
}

//  PRE-TRADE CHECKLIST 
function PreTradeChecklist({ sym, ind, onConfirm }) {
  const [checked, setChecked] = useState({});
  const items = [
    { id: 'light', text: 'Traffic light is GREEN', check: ind?.signal?.type !== 'NEUTRAL' },
    { id: 'conf', text: 'AI confidence 80%+', check: ind?.ml?.score >= 80 },
    { id: 'time', text: 'Inside prime window (9:45am-12:30pm)', check: (() => { const h=new Date().getHours(),m=new Date().getMinutes(); return h*60+m>=585&&h*60+m<=750; })() },
    { id: 'spy', text: 'SPY/QQQ direction matches signal', check: true },
    { id: 'size', text: 'Position size = 1-2 contracts max', check: true },
    { id: 'stop', text: 'I know my stop loss price', check: !!ind?.setup?.stop },
  ];
  const allChecked = items.every(i => checked[i.id] || i.check);

  return (
    <div style={{background:C.dim,border:'1px solid '+C.border,borderRadius:8,padding:14,marginTop:8}}>
      <div className="mono" style={{fontSize:9,color:C.yellow,letterSpacing:2,marginBottom:10}}>PRE-TRADE CHECKLIST</div>
      {items.map(item => (
        <div key={item.id} onClick={()=>setChecked(c=>({...c,[item.id]:!c[item.id]}))} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',cursor:'pointer'}}>
          <div style={{width:16,height:16,borderRadius:3,border:'1px solid '+(checked[item.id]||item.check?C.green:C.border),background:(checked[item.id]||item.check)?C.green+'22':'transparent',display:'flex',alignItems:'center',justifyContent:'center'}}>
            {(checked[item.id]||item.check) && <span style={{color:C.green,fontSize:11,fontWeight:700}}>v</span>}
          </div>
          <span style={{fontSize:11,color:(checked[item.id]||item.check)?C.text:C.muted}}>{item.text}</span>
        </div>
      ))}
      {allChecked && onConfirm && (
        <button onClick={onConfirm} style={{width:'100%',marginTop:10,padding:'10px',borderRadius:6,border:'none',background:C.green,color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>ALL CHECKS PASSED - EXECUTE TRADE</button>
      )}
    </div>
  );
}

//  OPEN COUNTDOWN (skip first 60 seconds rule) 
function OpenCountdown() {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      const now = new Date();
      const h = now.getHours(), m = now.getMinutes(), s = now.getSeconds();
      const mktOpen = h === 9 && m === 30;
      if (h === 9 && m === 30 && s < 60) {
        setSecs(60 - s);
      } else if (h === 9 && m < 30) {
        setSecs(-1); // before open
      } else {
        setSecs(0); // past the first minute
      }
    }, 1000);
    return () => clearInterval(t);
  }, []);

  if (secs === 0 || secs === -1) return null;
  return (
    <div style={{background:C.red+'18',border:'1px solid '+C.red+'44',borderRadius:8,padding:'10px 16px',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'center',gap:10}}>
      <span className="mono" style={{fontSize:14,color:C.red,fontWeight:700}}>DO NOT TRADE</span>
      <span className="mono" style={{fontSize:20,color:C.red,fontWeight:700}}>{secs}s</span>
      <span style={{fontSize:11,color:C.muted}}>Skip first 60 seconds at open - wait for direction</span>
    </div>
  );
}


//  LEGAL DISCLAIMER 
function DisclaimerModal({ onAccept }) {
  return (
    <div style={{position:'fixed',inset:0,zIndex:10000,background:'rgba(0,0,0,.95)',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:12,padding:'32px 28px',maxWidth:600,maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{fontFamily:'Bebas Neue',fontSize:24,color:C.red,letterSpacing:3,marginBottom:16}}>IMPORTANT DISCLAIMER</div>
        
        <div style={{fontSize:12,color:C.text,lineHeight:1.9,marginBottom:20}}>
          <p style={{marginBottom:12}}>By using APEX Scanner Pro, you acknowledge and agree to the following:</p>
          
          <p style={{marginBottom:12}}><b style={{color:C.red}}>RISK WARNING:</b> Options trading involves substantial risk of loss and is not suitable for all investors. You could lose more than your initial investment. Past performance does not guarantee future results. The signals, analysis, and recommendations provided by APEX Scanner Pro are for informational and educational purposes only.</p>
          
          <p style={{marginBottom:12}}><b style={{color:C.yellow}}>NOT FINANCIAL ADVICE:</b> APEX Scanner Pro does not provide personalized investment advice. The AI-generated signals are algorithmic analyses based on technical indicators and should not be considered as recommendations to buy or sell any securities. Always do your own research and consult with a qualified financial advisor before making investment decisions.</p>
          
          <p style={{marginBottom:12}}><b style={{color:C.accent}}>NO GUARANTEE:</b> No trading system, including APEX Scanner Pro, can guarantee profits or prevent losses. The AI confidence scores represent algorithmic probability assessments, not guaranteed outcomes. Market conditions can change rapidly and unpredictably.</p>
          
          <p style={{marginBottom:12}}><b style={{color:C.accent}}>AUTO-TRADING RISK:</b> If you enable auto-trading features, you are solely responsible for all trades executed on your behalf. APEX Scanner Pro and its creators are not liable for any losses resulting from automated trade execution. Always start with sandbox/paper trading.</p>
          
          <p style={{marginBottom:12}}><b style={{color:C.muted}}>SIMULATED DATA:</b> Some features may display simulated or delayed data. Always verify trade information with your broker before executing trades.</p>
          
          <p style={{marginBottom:12}}><b style={{color:C.muted}}>LIABILITY:</b> APEX Scanner Pro, its creators, affiliates, and partners shall not be held liable for any direct, indirect, incidental, or consequential damages arising from the use of this software. Use at your own risk.</p>
        </div>
        
        <div style={{background:C.dim,border:'1px solid '+C.border,borderRadius:8,padding:'12px 16px',marginBottom:20}}>
          <div style={{fontSize:11,color:C.muted,lineHeight:1.7}}>
            By clicking "I Understand and Accept" below, you confirm that you:<br/>
            - Are at least 18 years of age<br/>
            - Understand the risks involved in options trading<br/>
            - Accept full responsibility for your trading decisions<br/>
            - Have read and agree to these terms
          </div>
        </div>
        
        <button onClick={onAccept} style={{width:'100%',padding:'14px',borderRadius:8,border:'none',background:C.accent,color:'#000',fontSize:14,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1}}>I UNDERSTAND AND ACCEPT THE RISKS</button>
        
        <div className="mono" style={{fontSize:8,color:C.muted,textAlign:'center',marginTop:12,letterSpacing:1}}>APEX SCANNER PRO - FOR EDUCATIONAL AND INFORMATIONAL PURPOSES ONLY</div>
      </div>
    </div>
  );
}


//  ACTIVE POSITIONS MANAGER 
function PositionsManager() {
  const [positions, setPositions] = useState([]);
  const [liveBalance, setLiveBalance] = useState(null);
  React.useEffect(() => {
    const fetchBalance = () => {
      // Load real journal stats
    fetch('/api/markov/journal').then(r=>r.json()).then(d=>{
      const trades = d.trades || d.data || [];
      const today = new Date().toISOString().slice(0,10);
      const todayT = trades.filter(t=>t.date===today||t.created_at?.startsWith(today));
      const wins = todayT.filter(t=>t.result==='WIN').length;
      const losses = todayT.filter(t=>t.result==='LOSS').length;
      const pnl = todayT.reduce((a,t)=>a+(t.pnl||0),0);
      const wr = (wins+losses)>0?Math.round(wins/(wins+losses)*100):0;
      setJournalStats({trades:todayT.length,wins,losses,pnl:Math.round(pnl),winRate:wr});
      setTodayWins(wins);
      setTodayLosses(losses);
      setTodayTrades(todayT);
    }).catch(()=>{});

    fetch('/api/balance').then(r=>r.json()).then(d=>{
        if(d.ok) setLiveBalance(d);
      }).catch(()=>{});
    };
    fetchBalance();
    const t = setInterval(fetchBalance, 30000);
    return () => clearInterval(t);
  }, []);
  const [loading, setLoading] = useState(false);
  const [autoManage, setAutoManage] = useState(false);
  const [botPositions, setBotPositions] = useState([]);
  const [botStats, setBotStats] = useState({trades:0,wins:0,losses:0,pnl:0});
  const [journalStats, setJournalStats] = useState({trades:0,wins:0,losses:0,pnl:0,winRate:0});
  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch(API+'/api/account/positions');
      const d = await r.json();
      const pos = d?.positions?.position;
      setPositions(pos ? (Array.isArray(pos) ? pos : [pos]) : []);
    } catch(e) {}
    try {
      const br = await fetch(API+'/api/bot-positions');
      const bd = await br.json();
      setBotPositions(bd?.positions || []);
      setBotStats(bd?.stats || {trades:0,wins:0,losses:0,pnl:0});
    } catch(e) {}
    setLoading(false);
  };
  useEffect(() => { refresh(); const t = setInterval(refresh, 15000); return () => clearInterval(t); }, []);

  const exitPosition = async (symbol, qty, sandbox) => {
    if (!window.confirm('Close position in '+symbol+'? This will place a MARKET SELL order.')) return;
    try {
      const r = await fetch(API+'/api/order/place'+(sandbox?'':'?sandbox=false'), {
        method: 'POST',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({
          class: 'option', symbol: symbol.split(' ')[0],
          option_symbol: symbol, side: 'sell_to_close',
          quantity: String(Math.abs(qty)), type: 'market', duration: 'day'
        })
      });
      const d = await r.json();
      if (d.order) { alert('Exit order placed: #'+d.order.id); refresh(); }
      else { alert('Error: '+(d.error||'Unknown')); }
    } catch(e) { alert('Failed: '+e.message); }
  };

  const totalPnl = positions.reduce((a, p) => a + ((p.last_price - p.cost_basis) * p.quantity), 0);
  const totalValue = positions.reduce((a, p) => a + (p.last_price * Math.abs(p.quantity) * 100), 0);

  return (
    <div style={{padding:'16px 20px',maxWidth:1000,margin:'0 auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div>
          <div style={{fontFamily:'Bebas Neue',fontSize:24,color:C.accent,letterSpacing:3}}>ACTIVE POSITIONS</div>
          <div className="mono" style={{fontSize:9,color:C.muted}}>MANAGE YOUR OPEN TRADES . AUTO-REFRESHES EVERY 15s</div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button onClick={refresh} className="mono" style={{padding:'6px 14px',borderRadius:4,border:'1px solid '+C.border,background:'transparent',color:C.muted,fontSize:9,cursor:'pointer'}}>{loading?'...':'REFRESH'}</button>
        </div>
      </div>

      {/* Summary bar */}
      <div style={{background:C.panel,border:'1px solid '+(totalPnl>=0?C.green:C.red)+'33',borderRadius:8,padding:'12px 16px',marginBottom:16,display:'flex',gap:20,flexWrap:'wrap'}}>
        <div><span className="mono" style={{fontSize:9,color:C.muted}}>POSITIONS </span><span className="mono" style={{fontSize:16,color:C.text,fontWeight:700}}>{positions.length}</span></div>
        {liveBalance && <div style={{display:'flex',gap:16,marginTop:4}}>
          <div><span className="mono" style={{fontSize:9,color:C.muted}}>EQUITY </span><span className="mono" style={{fontSize:14,color:'#22c55e',fontWeight:700}}>${liveBalance.total_equity?.toFixed(2)}</span></div>
          <div><span className="mono" style={{fontSize:9,color:C.muted}}>AVAIL </span><span className="mono" style={{fontSize:14,color:C.text,fontWeight:700}}>${liveBalance.cash_available?.toFixed(2)}</span></div>
          <div><span className="mono" style={{fontSize:9,color:C.muted}}>P&L </span><span className="mono" style={{fontSize:14,color:liveBalance.close_pl>=0?'#22c55e':'#ef4444',fontWeight:700}}>{liveBalance.close_pl>=0?'+':''}{liveBalance.close_pl?.toFixed(2)}</span></div>
        </div>}
        <div><span className="mono" style={{fontSize:9,color:C.muted}}>TOTAL P&L </span><span className="mono" style={{fontSize:16,color:totalPnl>=0?C.green:C.red,fontWeight:700}}>{totalPnl>=0?'+':''}${totalPnl.toFixed(0)}</span></div>
        <div><span className="mono" style={{fontSize:9,color:C.muted}}>VALUE </span><span className="mono" style={{fontSize:16,color:C.text,fontWeight:700}}>${totalValue.toFixed(0)}</span></div>
      </div>

      {botPositions.length > 0 && (
        <div style={{marginBottom:16}}>
          <div className='mono' style={{fontSize:9,color:C.accent,letterSpacing:2,marginBottom:8}}>APEX BOT LIVE POSITIONS ({botPositions.length}) | {botStats.wins}W/{botStats.losses}L | P&L: {botStats.pnl>=0?'+':''}$\{botStats.pnl?.toFixed(0)}</div>
          {botPositions.map((pos,i) => {
              const closePos = async (p) => {
                if(!window.confirm('Close '+p.ticker+' position?')) return;
                try {
                  const r = await fetch('/api/order/place?sandbox=false', {
                    method:'POST', headers:{'Content-Type':'application/json'},
                    body: JSON.stringify({symbol:p.ticker, option_symbol:p.symbol, side:'sell_to_close', quantity:p.contracts||1, type:'market'})
                  });
                  const d = await r.json();
                  if(d.order) alert('Position closed! Order: '+d.order.id);
                  else alert('Error: '+JSON.stringify(d));
                } catch(e) { alert('Error: '+e.message); }
              };
            const gainPct = pos.peakPrice && pos.entryPremium ? ((pos.peakPrice - pos.entryPremium) / pos.entryPremium * 100).toFixed(1) : '0.0';
            const isUp = parseFloat(gainPct) >= 0;
            const col = isUp ? C.green : C.red;
            const t1 = pos.entryPremium * 1.40;
            const t2 = pos.entryPremium * 1.75;
            const stop = pos.stopPrice || pos.entryPremium * 0.65;
            const range = (pos.entryPremium * 2.5) - stop || 1;
            const curPct = Math.min(100,Math.max(0,((pos.peakPrice - stop)/range)*100));
            const entryPct = Math.max(0,((pos.entryPremium - stop)/range)*100);
            const t1Pct = ((t1-stop)/range)*100;
            const t2Pct = ((t2-stop)/range)*100;
            return (
              <div key={i} style={{background:C.panel,border:'1px solid '+col+'44',borderLeft:'3px solid '+col,borderRadius:8,padding:'12px 16px',marginBottom:8}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span className='mono' style={{fontSize:14,color:C.accent,fontWeight:700}}>{pos.ticker}</span>
                    <span className='mono' style={{fontSize:9,padding:'2px 6px',borderRadius:3,background:(pos.optionType==='CALL'?C.green:C.red)+'22',color:pos.optionType==='CALL'?C.green:C.red}}>{pos.optionType}</span>
                    {pos.t1Hit&&<span className='mono' style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:'#ffab0022',color:'#ffab00'}}>T1✓</span>}
                    {pos.t2Hit&&<span className='mono' style={{fontSize:8,padding:'1px 5px',borderRadius:3,background:C.green+'22',color:C.green}}>T2✓</span>}
                    <span className='mono' style={{fontSize:9,color:C.muted}}>{pos.contracts}x</span>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div className='mono' style={{fontSize:16,fontWeight:700,color:col}}>{isUp?'+':''}{gainPct}%</div>
                    <div className='mono' style={{fontSize:9,color:C.muted}}>Score: {pos.score}</div>
                  </div>
                </div>
                <div style={{position:'relative',height:16,marginBottom:6}}>
                  <div style={{position:'absolute',top:'50%',left:0,right:0,height:4,background:C.border,borderRadius:2,transform:'translateY(-50%)'}}/>
                  <div style={{position:'absolute',top:'50%',left:entryPct+'%',width:Math.abs(curPct-entryPct)+'%',height:4,background:col,borderRadius:2,transform:'translateY(-50%)',transition:'width 0.5s'}}/>
                  <div style={{position:'absolute',left:'0%',top:0,bottom:0,width:2,background:C.red,opacity:0.6}}/>
                  <div style={{position:'absolute',left:t1Pct+'%',top:0,bottom:0,width:2,background:'#ffab00'}}/>
                  <div style={{position:'absolute',left:t2Pct+'%',top:0,bottom:0,width:2,background:C.green}}/>
                  <div style={{position:'absolute',top:'50%',left:curPct+'%',width:12,height:12,borderRadius:'50%',background:col,border:'2px solid '+C.panel,transform:'translate(-50%,-50%)',zIndex:2,transition:'left 0.5s'}}/>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:8,fontFamily:'JetBrains Mono',color:C.muted}}>
                  <span style={{color:C.red}}>STOP ${stop.toFixed(2)}</span>
                  <span>ENTRY ${pos.entryPremium?.toFixed(2)}</span>
                  <span style={{color:'#ffab00'}}>T1 ${t1.toFixed(2)}</span>
                  <span style={{color:C.green}}>T2 ${t2.toFixed(2)}</span>
                  <span style={{color:C.green}}>PEAK ${pos.peakPrice?.toFixed(2)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {botStats.trades > 0 && (
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'10px 16px',marginBottom:16,display:'flex',gap:16,flexWrap:'wrap'}}>
          <div><span className='mono' style={{fontSize:8,color:C.muted}}>CLOSED </span><span className='mono' style={{fontSize:14,color:C.text,fontWeight:700}}>{botStats.trades}</span></div>
          <div><span className='mono' style={{fontSize:8,color:C.green}}>W </span><span className='mono' style={{fontSize:14,color:C.green,fontWeight:700}}>{botStats.wins}</span></div>
          <div><span className='mono' style={{fontSize:8,color:C.red}}>L </span><span className='mono' style={{fontSize:14,color:C.red,fontWeight:700}}>{botStats.losses}</span></div>
          <div><span className='mono' style={{fontSize:8,color:C.muted}}>P&amp;L </span><span className='mono' style={{fontSize:14,color:botStats.pnl>=0?C.green:C.red,fontWeight:700}}>{botStats.pnl>=0?'+':''}$\{botStats.pnl?.toFixed(0)}</span></div>
          <div><span className='mono' style={{fontSize:8,color:C.muted}}>WR </span><span className='mono' style={{fontSize:14,color:C.accent,fontWeight:700}}>{botStats.trades>0?Math.round(botStats.wins/botStats.trades*100):0}%</span></div>
        </div>
      )}
      {positions.length === 0 ? (
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:40,textAlign:'center'}}>
          <div style={{fontSize:14,color:C.muted,marginBottom:8}}>No open positions</div>
          <div style={{fontSize:11,color:C.muted}}>Positions will appear here when you enter trades through Tradier (sandbox or live)</div>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {positions.map((p, i) => {
            const pnl = (p.last_price - p.cost_basis) * p.quantity;
            const pnlPct = p.cost_basis > 0 ? ((p.last_price - p.cost_basis) / p.cost_basis * 100) : 0;
            const isProfit = pnl >= 0;
            return (
              <div key={i} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'14px 16px',borderLeft:'3px solid '+(isProfit?C.green:C.red)}}>
                <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                  <span className="mono" style={{fontSize:14,color:C.accent,fontWeight:700,minWidth:50}}>{p.symbol?.split(' ')[0]}</span>
                  <span className="mono" style={{fontSize:11,color:C.muted}}>{Math.abs(p.quantity)}x</span>
                  <span className="mono" style={{fontSize:11,color:C.muted}}>@ ${p.cost_basis?.toFixed(2)}</span>
                  <span className="mono" style={{fontSize:11,color:C.text}}>now ${p.last_price?.toFixed(2)}</span>
                  <span style={{flex:1}}/>
                  <span className="mono" style={{fontSize:16,fontWeight:700,color:isProfit?C.green:C.red}}>{isProfit?'+':''}${pnl.toFixed(0)} ({pnlPct>=0?'+':''}{pnlPct.toFixed(1)}%)</span>
                  <button onClick={()=>exitPosition(p.symbol,p.quantity,true)} style={{padding:'6px 16px',borderRadius:4,border:'1px solid '+C.red+'66',background:C.red+'18',color:C.red,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>EXIT</button>
                </div>
                {/* P&L progress bar */}
                <div style={{marginTop:8,height:4,background:C.dim,borderRadius:2,overflow:'hidden'}}>
                  <div style={{height:'100%',width:Math.min(100,Math.abs(pnlPct)*2)+'%',background:isProfit?C.green:C.red,borderRadius:2}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Trading mode selector */}
      <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:16,marginTop:16}}>
        <div className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2,marginBottom:12}}>TRADING MODE</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
          <div style={{background:C.dim,borderRadius:8,padding:14,border:'1px solid '+C.green+'33'}}>
            <div className="mono" style={{fontSize:11,color:C.green,fontWeight:700,marginBottom:4}}>MANUAL</div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>APEX shows signals. You enter trades yourself in Webull or your broker. Full control.</div>
          </div>
          <div style={{background:C.dim,borderRadius:8,padding:14,border:'1px solid '+C.yellow+'33'}}>
            <div className="mono" style={{fontSize:11,color:C.yellow,fontWeight:700,marginBottom:4}}>SEMI-AUTO</div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>APEX shows signal + one-click SEND ORDER to Tradier. You confirm before execution.</div>
          </div>
          <div style={{background:C.dim,borderRadius:8,padding:14,border:'1px solid '+C.red+'33'}}>
            <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}><span className="mono" style={{fontSize:11,color:C.red,fontWeight:700}}>APEX AUTOPILOT</span><span className="mono" style={{fontSize:8,color:C.purple,padding:'1px 5px',borderRadius:3,border:'1px solid '+C.purple+'44'}}>PRO</span></div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>AI trades for you automatically. Set your rules, click START, APEX handles everything. Telegram alerts for every trade. Pro and Annual plans only.</div>
          </div>
        </div>

        <div style={{marginTop:14}}>
          <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:8}}>SUPPORTED BROKERS</div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            <div style={{background:C.dim,borderRadius:6,padding:'8px 14px',border:'1px solid '+C.green+'33'}}>
              <span className="mono" style={{fontSize:10,color:C.green,fontWeight:700}}>TRADIER</span>
              <span style={{fontSize:10,color:C.muted,marginLeft:8}}>Connected - semi-auto + full-auto</span>
            </div>
            <div style={{background:C.dim,borderRadius:6,padding:'8px 14px',border:'1px solid '+C.border}}>
              <span className="mono" style={{fontSize:10,color:C.muted,fontWeight:700}}>WEBULL</span>
              <span style={{fontSize:10,color:C.muted,marginLeft:8}}>Manual only (no API)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


//  CLAUDE AI TRADE REASONING 
function AIReasoning({ sym, ind, candles }) {
  const [reasoning, setReasoning] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const prevSignal = useRef(null);

  useEffect(() => {
    if (!ind?.signal || ind.signal.type === 'NEUTRAL') { setReasoning(null); prevSignal.current = null; return; }
    // Only generate once per signal change
    const sigKey = sym + ind.signal.type + Math.round(ind.price);
    if (sigKey === prevSignal.current) return;
    prevSignal.current = sigKey;

    setLoading(true);
    const prompt = `You are an expert options day trader analyzing a real-time trade setup. Be direct and specific. Write 2-3 sentences max explaining WHY this trade setup exists right now, what confirms it, and one risk to watch. No disclaimers.

Stock: ${sym}
Signal: ${ind.signal.type} (${ind.signal.type === 'CALL' ? 'Bullish' : 'Bearish'})
Price: $${ind.price?.toFixed(2)}
VWAP: $${ind.vwap?.toFixed(2)} (price is ${ind.price > ind.vwap ? 'ABOVE' : 'BELOW'} VWAP)
EMA9: $${ind.ema9?.toFixed(2)}
EMA21: $${(ind.ema21||ind.ema9)?.toFixed(2)}
EMA Trend: ${ind.ema9 > (ind.ema21||ind.ema9) ? 'EMA9 above EMA21 (bullish)' : 'EMA9 below EMA21 (bearish)'}
RSI: ${ind.rsi?.toFixed(1)}
MACD: ${ind.macd?.toFixed(3)}
AI Confidence: ${ind.ml?.score}%
Change Today: ${ind.changePct?.toFixed(2)}%
Time: ${new Date().toLocaleTimeString()}
${ind.setup ? 'Entry: $' + ind.setup.entry + ' | Stop: $' + ind.setup.stop + ' | T1: $' + ind.setup.t1 + ' | T2: $' + ind.setup.t2 : ''}

Explain this setup in 2-3 sentences. Start with the key reason this trade exists.`;

    fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-sonnet-4-20250514',
        max_tokens: 150,
        messages: [{ role: 'user', content: prompt }]
      })
    })
    .then(r => r.json())
    .then(d => {
      const text = d.content?.[0]?.text || '';
      setReasoning(text);
      setLoading(false);
    })
    .catch(() => {
      // Fallback: generate reasoning locally from indicators
      const reasons = [];
      if (ind.price > ind.vwap) reasons.push(sym + ' is trading above VWAP ($' + ind.vwap?.toFixed(2) + '), showing institutional buying support.');
      else reasons.push(sym + ' broke below VWAP ($' + ind.vwap?.toFixed(2) + '), indicating selling pressure from institutions.');
      
      if (ind.ema9 > (ind.ema21||ind.ema9)) reasons.push('EMA9 crossed above EMA21 confirming short-term momentum shift to bullish.');
      else reasons.push('EMA9 is below EMA21 confirming bearish momentum.');
      
      if (ind.rsi < 35) reasons.push('RSI at ' + ind.rsi?.toFixed(0) + ' shows oversold conditions - bounce likely.');
      else if (ind.rsi > 65) reasons.push('RSI at ' + ind.rsi?.toFixed(0) + ' shows strong momentum but watch for overbought reversal.');
      else reasons.push('RSI at ' + ind.rsi?.toFixed(0) + ' is in the momentum zone.');

      const h = new Date().getHours(), m = new Date().getMinutes();
      if (h === 10 || (h === 11 && m < 30)) reasons.push('Setup occurring during prime window (9:45am-12:30pm) when win rates are highest.');

      setReasoning(reasons.join(' '));
      setLoading(false);
    });
  }, [sym, ind?.signal?.type, ind?.price]);

  if (!ind?.signal || ind.signal.type === 'NEUTRAL') return null;
  if (!reasoning && !loading) return null;

  const col = ind.signal.type === 'CALL' ? C.green : C.red;

  return (
    <div style={{background:col+'08',border:'1px solid '+col+'33',borderRadius:8,padding:'10px 14px',marginTop:8}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',cursor:'pointer'}} onClick={()=>setExpanded(!expanded)}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <div style={{width:20,height:20,borderRadius:'50%',background:col+'22',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <span className="mono" style={{fontSize:9,color:col,fontWeight:700}}>AI</span>
          </div>
          <span className="mono" style={{fontSize:9,color:col,letterSpacing:2,fontWeight:700}}>WHY THIS TRADE</span>
        </div>
        <span className="mono" style={{fontSize:8,color:C.muted}}>{loading?'ANALYZING...':expanded?'HIDE':'READ'}</span>
      </div>
      
      {(expanded || true) && (
        <div style={{marginTop:8}}>
          {loading ? (
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <div style={{width:12,height:12,border:'2px solid '+C.border,borderTop:'2px solid '+col,borderRadius:'50%',animation:'spin .6s linear infinite'}}/>
              <span style={{fontSize:11,color:C.muted}}>Claude is analyzing this setup...</span>
            </div>
          ) : (
            <div style={{fontSize:12,color:C.text,lineHeight:1.8}}>{reasoning}</div>
          )}
        </div>
      )}
    </div>
  );
}



//  BETA FEEDBACK FORM 
function FeedbackForm() {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [category, setCategory] = useState('general');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!feedback.trim()) return;
    setLoading(true);
    try {
      await fetch(API+'/api/feedback', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          rating, category, feedback: feedback.trim(),
          user: localStorage.getItem('apex_license')||'unknown',
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent
        })
      });
      setSubmitted(true);
    } catch(e) {}
    setLoading(false);
  };

  if (submitted) return (
    <div style={{background:C.green+'10',border:'1px solid '+C.green+'33',borderRadius:8,padding:24,textAlign:'center'}}>
      <div style={{fontSize:24,marginBottom:8}}>Thank you!</div>
      <div style={{fontSize:13,color:C.text}}>Your feedback helps us build a better trading experience.</div>
      <button onClick={()=>{setSubmitted(false);setFeedback('');setRating(0);}} className="mono" style={{marginTop:12,padding:'6px 16px',borderRadius:4,border:'1px solid '+C.border,background:'transparent',color:C.muted,fontSize:9,cursor:'pointer'}}>SUBMIT MORE FEEDBACK</button>
    </div>
  );

  return (
    <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:16}}>
      <div className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2,marginBottom:4}}>BETA FEEDBACK</div>
      <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Help us improve APEX. Your feedback shapes the product.</div>

      {/* Rating */}
      <div style={{marginBottom:14}}>
        <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1,marginBottom:6}}>HOW WOULD YOU RATE APEX?</div>
        <div style={{display:'flex',gap:6}}>
          {[1,2,3,4,5].map(n => (
            <button key={n} onClick={()=>setRating(n)} style={{width:36,height:36,borderRadius:6,border:'1px solid '+(rating>=n?C.yellow:C.border),background:rating>=n?C.yellow+'22':'transparent',color:rating>=n?C.yellow:C.muted,fontSize:14,cursor:'pointer',fontWeight:700}}>{n}</button>
          ))}
          <span style={{fontSize:10,color:C.muted,alignSelf:'center',marginLeft:8}}>{rating===0?'':rating<=2?'Needs work':rating===3?'Decent':rating===4?'Great':'Amazing!'}</span>
        </div>
      </div>

      {/* Category */}
      <div style={{marginBottom:14}}>
        <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1,marginBottom:6}}>CATEGORY</div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {[['general','General'],['signals','Signals/Accuracy'],['ui','Design/UI'],['feature','Feature Request'],['bug','Bug Report'],['broker','Broker/Trading']].map(([id,label]) => (
            <button key={id} onClick={()=>setCategory(id)} className="mono" style={{padding:'5px 12px',borderRadius:4,border:'1px solid '+(category===id?C.accent:C.border),background:category===id?C.accent+'18':'transparent',color:category===id?C.accent:C.muted,fontSize:9,cursor:'pointer'}}>{label}</button>
          ))}
        </div>
      </div>

      {/* Feedback text */}
      <div style={{marginBottom:14}}>
        <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1,marginBottom:6}}>YOUR FEEDBACK</div>
        <textarea value={feedback} onChange={e=>setFeedback(e.target.value)} placeholder="What do you like? What is confusing? What would you add? Found a bug? Tell us everything..." rows={4} style={{width:'100%',padding:'10px 12px',borderRadius:6,border:'1px solid '+C.border,background:C.dim,color:C.text,fontSize:12,fontFamily:'inherit',outline:'none',resize:'vertical',lineHeight:1.7}}/>
      </div>

      {/* Quick feedback buttons */}
      <div style={{marginBottom:14}}>
        <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1,marginBottom:6}}>QUICK FEEDBACK (click to add)</div>
        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
          {['Signals are accurate','Need more indicators','Charts need work','Love the AI reasoning','Too complex for beginners','Need mobile app','Auto-trading works great','Need more brokers','Would pay $49/mo','Would recommend to friends'].map(q => (
            <button key={q} onClick={()=>setFeedback(f=>f+(f?' | ':'')+q)} style={{padding:'4px 10px',borderRadius:4,border:'1px solid '+C.border,background:'transparent',color:C.muted,fontSize:9,cursor:'pointer',fontFamily:'inherit'}}>{q}</button>
          ))}
        </div>
      </div>

      <button onClick={submit} disabled={!feedback.trim()||loading} style={{width:'100%',padding:'12px',borderRadius:6,border:'none',background:feedback.trim()?C.accent:C.border,color:feedback.trim()?'#000':C.muted,fontSize:12,fontWeight:700,cursor:feedback.trim()?'pointer':'default',fontFamily:'JetBrains Mono',letterSpacing:1}}>{loading?'SENDING...':'SUBMIT FEEDBACK'}</button>
    </div>
  );
}



//  AUTOPILOT TRADE TRACKER 
// Shows active auto-trades with AI decision support


//  SMART MONEY TAB 
function SmartMoneyTab() {
  const [section, setSection] = useState('congress');
  const [congressData, setCongressData] = useState([]);
  const [whaleData, setWhaleData] = useState([]);
  const [insiderData, setInsiderData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(API+'/api/smart-money/congress').then(r=>r.json()).catch(()=>({trades:[]})),
      fetch(API+'/api/smart-money/whales').then(r=>r.json()).catch(()=>({trades:[]})),
      fetch(API+'/api/smart-money/insiders').then(r=>r.json()).catch(()=>({trades:[]})),
    ]).then(([c,w,ins])=>{
      setCongressData(c.trades||[]);
      setWhaleData(w.trades||[]);
      setInsiderData(ins.trades||[]);
      setLoading(false);
    });
  }, []);

  const sections = [
    {id:'congress',label:'CONGRESS',count:congressData.length},
    {id:'whales',label:'WHALE FLOW',count:whaleData.length},
    {id:'insiders',label:'INSIDERS',count:insiderData.length},
  ];

  const formatMoney = (n) => {
    if (n >= 1000000) return '$' + (n/1000000).toFixed(1) + 'M';
    if (n >= 1000) return '$' + (n/1000).toFixed(0) + 'K';
    return '$' + n;
  };

  return (
    <div style={{padding:'16px 20px',maxWidth:1100,margin:'0 auto'}}>
      <div style={{fontFamily:'Bebas Neue',fontSize:24,color:C.accent,letterSpacing:3,marginBottom:4}}>SMART MONEY TRACKER</div>
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:16}}>FOLLOW THE MONEY - SEE WHAT THE BIG PLAYERS ARE BUYING</div>

      {/* Section tabs */}
      <div style={{display:'flex',gap:0,marginBottom:16,border:'1px solid '+C.border,borderRadius:8,overflow:'hidden'}}>
        {sections.map(s=>(
          <button key={s.id} onClick={()=>setSection(s.id)} style={{flex:1,padding:'12px 16px',background:section===s.id?C.accent+'15':'transparent',border:'none',borderRight:'1px solid '+C.border,color:section===s.id?C.accent:C.muted,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1,display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
            {s.label}
            <span style={{fontSize:9,padding:'1px 6px',borderRadius:10,background:section===s.id?C.accent+'22':C.dim,color:section===s.id?C.accent:C.muted}}>{s.count}</span>
          </button>
        ))}
      </div>

      {loading && <div style={{textAlign:'center',padding:40,color:C.muted}}>Loading smart money data...</div>}

      {/* CONGRESS TRADES */}
      {!loading && section==='congress' && (
        <div>
          <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'12px 16px',marginBottom:12}}>
            <div style={{fontSize:12,color:C.text,lineHeight:1.7}}>
              Members of Congress are required to disclose stock trades within 45 days. These politicians often have access to non-public information through committee hearings and briefings. Tracking their trades can reveal where institutional money is flowing.
            </div>
          </div>

          {/* Buy/Sell filter */}
          <div style={{display:'flex',gap:4,marginBottom:12}}>
            {[['all','ALL'],['buy','BUYS'],['sell','SELLS']].map(([id,label])=>(
              <button key={id} onClick={()=>setFilter(id)} className="mono" style={{padding:'5px 14px',borderRadius:20,border:'1px solid '+(filter===id?C.accent:C.border),background:filter===id?C.accent+'15':'transparent',color:filter===id?C.accent:C.muted,fontSize:9,cursor:'pointer'}}>{label}</button>
            ))}
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {(filter==='all'?congressData:congressData.filter(t=>filter==='buy'?t.type==='Purchase':t.type==='Sale')).map((t,i)=>(
              <div key={i} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'12px 16px',borderLeft:'3px solid '+(t.type==='Purchase'?C.green:C.red)}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <span className="mono" style={{fontSize:14,color:C.accent,fontWeight:700}}>{t.ticker}</span>
                  <span className="mono" style={{fontSize:10,color:t.type==='Purchase'?C.green:C.red,fontWeight:700,padding:'2px 8px',borderRadius:4,background:(t.type==='Purchase'?C.green:C.red)+'15'}}>{t.type==='Purchase'?'BUY':'SELL'}</span>
                  <span style={{fontSize:11,color:C.text}}>{t.representative}</span>
                  <span className="mono" style={{fontSize:9,color:C.muted,padding:'2px 6px',borderRadius:3,background:C.dim}}>{t.party}</span>
                  <span style={{flex:1}}/>
                  <span className="mono" style={{fontSize:12,color:C.yellow,fontWeight:700}}>{t.range||formatMoney(t.amount||0)}</span>
                </div>
                <div style={{display:'flex',gap:12,marginTop:6}}>
                  <span className="mono" style={{fontSize:9,color:C.muted}}>{t.date||t.transactionDate}</span>
                  <span className="mono" style={{fontSize:9,color:C.muted}}>{t.house||'Congress'}</span>
                  {t.excessReturn && <span className="mono" style={{fontSize:9,color:parseFloat(t.excessReturn)>0?C.green:C.red}}>Return: {t.excessReturn}</span>}
                </div>
              </div>
            ))}
            {congressData.length===0 && <div style={{textAlign:'center',padding:30,color:C.muted}}>
              <div style={{fontSize:13,marginBottom:8}}>No congress trade data available</div>
              <div style={{fontSize:11}}>Add your Quiver Quantitative API key in Settings to get real congressional trade data. Free at quiverquant.com</div>
            </div>}
          </div>
        </div>
      )}

      {/* WHALE FLOW */}
      {!loading && section==='whales' && (
        <div>
          <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'12px 16px',marginBottom:12}}>
            <div style={{fontSize:12,color:C.text,lineHeight:1.7}}>
              Large options orders ($100K+) from institutional traders. SWEEPS are urgently filled across exchanges (bullish signal). BLOCKS are single large orders. GOLDEN SWEEPS are $1M+ bets.
            </div>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {whaleData.map((t,i)=>{
              const col = t.sentiment==='BULLISH'?C.green:t.sentiment==='BEARISH'?C.red:C.yellow;
              return (
                <div key={i} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'12px 16px',borderLeft:'3px solid '+col}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                    <span className="mono" style={{fontSize:14,color:C.accent,fontWeight:700}}>{t.ticker}</span>
                    <span className="mono" style={{fontSize:10,color:t.type==='CALL'?C.green:C.red,fontWeight:700}}>{t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</span>
                    <span style={{fontSize:11,color:C.muted}}>Strike ${t.strike}</span>
                    <span className="mono" style={{fontSize:9,color:col,padding:'2px 8px',borderRadius:4,background:col+'15',fontWeight:700}}>{t.flowType}</span>
                    <span style={{flex:1}}/>
                    <span className="mono" style={{fontSize:13,color:C.yellow,fontWeight:700}}>{formatMoney(t.premium)}</span>
                    {t.score && <span className="mono" style={{fontSize:9,color:C.accent}}>{t.score}</span>}
                  </div>
                  <div style={{display:'flex',gap:12,marginTop:6}}>
                    <span className="mono" style={{fontSize:9,color:C.muted}}>{t.time}</span>
                    <span className="mono" style={{fontSize:9,color:col}}>{t.sentiment}</span>
                    {t.expiry && <span className="mono" style={{fontSize:9,color:C.muted}}>Exp: {t.expiry}</span>}
                  </div>
                </div>
              );
            })}
            {whaleData.length===0 && <div style={{textAlign:'center',padding:30,color:C.muted}}>
              <div style={{fontSize:13,marginBottom:8}}>No whale flow data available</div>
              <div style={{fontSize:11}}>Whale flow requires Unusual Whales API (~$30/mo). Add your API key in Settings to see real institutional options flow.</div>
            </div>}
          </div>
        </div>
      )}

      {/* INSIDERS */}
      {!loading && section==='insiders' && (
        <div>
          <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'12px 16px',marginBottom:12}}>
            <div style={{fontSize:12,color:C.text,lineHeight:1.7}}>
              When company executives (CEOs, CFOs, directors) buy or sell their own stock, they must report it. Insider BUYING is one of the strongest bullish signals - they know their company best.
            </div>
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {insiderData.map((t,i)=>(
              <div key={i} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'12px 16px',borderLeft:'3px solid '+(t.type==='Buy'?C.green:C.red)}}>
                <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <span className="mono" style={{fontSize:14,color:C.accent,fontWeight:700}}>{t.ticker}</span>
                  <span className="mono" style={{fontSize:10,color:t.type==='Buy'?C.green:C.red,fontWeight:700,padding:'2px 8px',borderRadius:4,background:(t.type==='Buy'?C.green:C.red)+'15'}}>{t.type==='Buy'?'BUY':'SELL'}</span>
                  <span style={{fontSize:11,color:C.text}}>{t.name}</span>
                  <span className="mono" style={{fontSize:9,color:C.muted}}>{t.title}</span>
                  <span style={{flex:1}}/>
                  <span className="mono" style={{fontSize:12,color:C.yellow,fontWeight:700}}>{formatMoney(t.value||0)}</span>
                </div>
                <div style={{display:'flex',gap:12,marginTop:6}}>
                  <span className="mono" style={{fontSize:9,color:C.muted}}>{t.date}</span>
                  <span className="mono" style={{fontSize:9,color:C.muted}}>{t.contracts||1} contracts @ ${t.price}</span>
                </div>
              </div>
            ))}
            {insiderData.length===0 && <div style={{textAlign:'center',padding:30,color:C.muted}}>
              <div style={{fontSize:13,marginBottom:8}}>No insider trade data available</div>
              <div style={{fontSize:11}}>Insider trading data requires Finnhub Premium or Quiver API. Add your API key in Settings.</div>
            </div>}
          </div>
        </div>
      )}

      {/* Data source info */}
      <div style={{background:C.dim,border:'1px solid '+C.border,borderRadius:8,padding:'10px 14px',marginTop:16}}>
        <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1,marginBottom:6}}>DATA SOURCES</div>
        <div style={{fontSize:10,color:C.muted,lineHeight:1.7}}>
          Congress: Quiver Quantitative API (free tier available at quiverquant.com) | Whale Flow: Unusual Whales API ($30/mo at unusualwhales.com) | Insiders: Finnhub or Quiver API | Add your API keys in Settings to activate live data.
        </div>
      </div>
    </div>
  );
}



//  PAPER TRADE RESULTS DISPLAY 
function PaperTradeResults() {
  const [data, setData] = useState({trades:[],summary:{}});
  const [loading, setLoading] = useState(true);

  useEffect(()=>{
    const load = () => {
      fetch(API+'/api/markov/journal').then(r=>r.json()).then(d=>{setData(d);setLoading(false);}).catch(()=>setLoading(false));
    };
    load();
    const t = setInterval(load, 30000);
    return ()=>clearInterval(t);
  },[]);

  const s = data.stats||data.summary||{};
  const trades = data.trades||[];

  return (
    <div style={{marginTop:20}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
        <div>
          <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.accent,letterSpacing:3}}>PAPER TRADING RESULTS</div>
          <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1}}>LIVE DATA FROM PAPER TEST BOT</div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        {[
          {label:'TRADES',val:s.totalTrades||0,col:C.text},
          {label:'WINS',val:s.wins||0,col:C.green},
          {label:'LOSSES',val:s.losses||0,col:C.red},
          {label:'WIN RATE',val:(s.winRate||0)+'%',col:(s.winRate||0)>=50?C.green:C.red},
          {label:'P&L',val:(s.totalPnl>=0?'+':'')+'$'+(s.totalPnl||0),col:(s.totalPnl||0)>=0?C.green:C.red},
        ].map((item,i)=>(
          <div key={i} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'8px 14px',flex:1,minWidth:70,textAlign:'center'}}>
            <div className="mono" style={{fontSize:8,color:C.muted}}>{item.label}</div>
            <div className="mono" style={{fontSize:18,color:item.col,fontWeight:700}}>{item.val}</div>
          </div>
        ))}
      </div>

      {/* Trade list */}
      {(()=>{
        const [pf, setPf] = React.useState('all');
        const tod = new Date().toDateString();
        const ft = pf==='all' ? trades
          : pf==='today' ? trades.filter(t=>new Date(t.date||t.entryTime).toDateString()===tod)
          : pf==='wins' ? trades.filter(t=>t.result==='WIN')
          : trades.filter(t=>t.result==='LOSS');
        return (<>
          <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
            {[['all','ALL',trades.length],['today','TODAY',trades.filter(t=>new Date(t.date||t.entryTime).toDateString()===tod).length],['wins','WINS',trades.filter(t=>t.result==='WIN').length],['losses','LOSSES',trades.filter(t=>t.result==='LOSS').length]].map(([id,lb,cnt])=>(
              <button key={id} onClick={()=>setPf(id)} className="mono" style={{padding:'4px 12px',borderRadius:20,border:'1px solid '+(pf===id?C.accent:C.border),background:pf===id?C.accent+'15':'transparent',color:pf===id?C.accent:C.muted,fontSize:9,cursor:'pointer'}}>{lb} {cnt}</button>
            ))}
          </div>
          {loading ? <div style={{textAlign:'center',padding:20,color:C.muted}}>Loading...</div> :
          <div style={{display:'flex',flexDirection:'column',gap:4}}>
          {ft.slice().reverse().map((t,i) => {
          const isWin = t.result === 'WIN';
          const col = isWin ? C.green : C.red;
          return (
            <div key={i} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'10px 14px',borderLeft:'3px solid '+col}}>
              <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                <span className="mono" style={{fontSize:13,color:C.accent,fontWeight:700}}>{t.symbol}</span>
                <span className="mono" style={{fontSize:10,color:t.type==='CALL'?C.green:C.red,fontWeight:700,padding:'2px 8px',borderRadius:4,background:(t.type==='CALL'?C.green:C.red)+'15'}}>{t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</span>
                <span style={{fontSize:10,color:C.muted}}>@ ${typeof t.entryPremium==='number'?t.entryPremium.toFixed(2):t.entry?.toFixed(2)||'--'}</span>
                <span style={{flex:1}}/>
                <span className="mono" style={{fontSize:12,color:col,fontWeight:700,padding:'2px 8px',borderRadius:4,background:col+'15'}}>{t.result||'OPEN'}</span>
                <span className="mono" style={{fontSize:13,color:col,fontWeight:700}}>{(t.pnl>=0?'+':'')+'$'+(t.pnl||0).toFixed(2)}</span>
              </div>
              <div style={{display:'flex',gap:12,marginTop:4}}>
                <span className="mono" style={{fontSize:9,color:C.muted}}>{t.entryTime ? new Date(t.entryTime).toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit',hour12:true}) : ''}</span>
                {t.holdTime&&<span className="mono" style={{fontSize:9,color:C.muted}}>{t.holdTime}min hold</span>}
                {t.exitReason&&<span className="mono" style={{fontSize:9,color:C.muted}}>{t.exitReason}</span>}{t.score&&<span className="mono" style={{fontSize:9,color:C.accent}}> Score:{t.score}</span>}{t.contracts&&<span className="mono" style={{fontSize:9,color:C.muted}}> {t.contracts}x contracts</span>}{t.regimeLabel&&<span className="mono" style={{fontSize:9,color:t.regimeLabel==="TRENDING"?C.green:t.regimeLabel==="VOLATILE"?C.red:"#ffab00"}}> {t.regimeLabel}</span>}
                {t.exitPremium&&<span className="mono" style={{fontSize:9,color:C.muted}}>Exit:${t.exitPremium?.toFixed(2)}</span>}
              </div>
            </div>
          );
        })}
      </div>}
          </>);
      })()}
    </div>
  );
}


//  TRADE PERFORMANCE STATS 
function TradePerformanceStats() {
  const [data, setData] = useState({trades:[],summary:{}});
  useEffect(()=>{
    fetch(API+'/api/markov/journal').then(r=>r.json()).then(d=>setData(d)).catch(()=>{});
    const t = setInterval(()=>{fetch(API+'/api/markov/journal').then(r=>r.json()).then(d=>setData(d)).catch(()=>{});}, 60000);
    return ()=>clearInterval(t);
  },[]);

  const s = data.stats||data.summary||{};
  const trades = data.trades||[];
  const wins = trades.filter(t=>t.result==='WIN');
  const losses = trades.filter(t=>t.result==='LOSS');
  const bySymbol = {};
  trades.forEach(t=>{
    if(!bySymbol[t.symbol]) bySymbol[t.symbol]={w:0,l:0,pnl:0};
    if(t.result==='WIN') bySymbol[t.symbol].w++;
    else if(t.result==='LOSS') bySymbol[t.symbol].l++;
    bySymbol[t.symbol].pnl += (t.pnl||0);
  });

  if (trades.length === 0) return null;

  return (
    <div style={{marginTop:20}}>
      <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.accent,letterSpacing:3,marginBottom:12}}>TRADE PERFORMANCE</div>

      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'10px 14px',flex:1,textAlign:'center'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>PROFIT FACTOR</div>
          <div className="mono" style={{fontSize:20,color:C.accent,fontWeight:700}}>{s.profitFactor||'N/A'}</div>
        </div>
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'10px 14px',flex:1,textAlign:'center'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>AVG WIN</div>
          <div className="mono" style={{fontSize:20,color:C.green,fontWeight:700}}>${s.avgWin||0}</div>
        </div>
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'10px 14px',flex:1,textAlign:'center'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>AVG LOSS</div>
          <div className="mono" style={{fontSize:20,color:C.red,fontWeight:700}}>${s.avgLoss||0}</div>
        </div>
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'10px 14px',flex:1,textAlign:'center'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>AVG HOLD</div>
          <div className="mono" style={{fontSize:20,color:C.text,fontWeight:700}}>{s.avgHoldTime||0}m</div>
        </div>
      </div>

      {/* Per-symbol breakdown */}
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:1,marginBottom:8}}>PERFORMANCE BY SYMBOL</div>
      <div style={{display:'flex',flexDirection:'column',gap:4}}>
        {/* Risk Analysis */}
      {trades.length > 0 && (
        <div style={{marginTop:16}}>
          <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:1,marginBottom:8}}>RISK ANALYSIS</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:12}}>
            {(()=>{
              const totalRisk=trades.reduce((a,t)=>{
                const e=parseFloat(t.entry)||0,s=parseFloat(t.stop)||0;
                return a+Math.abs(e-s)*100;
              },0);
              const totalPnl=trades.reduce((a,t)=>a+(t.pnl||0),0);
              const rMultiples=trades.map(t=>{
                const e=parseFloat(t.entry)||0,s=parseFloat(t.stop)||0;
                const risk=Math.abs(e-s)*100;
                return risk>0?(t.pnl||0)/risk:0;
              });
              const avgR=rMultiples.length>0?(rMultiples.reduce((a,r)=>a+r,0)/rMultiples.length):0;
              const expectancy=trades.length>0?Math.round(totalPnl/trades.length):0;
              return [
                {label:'TOTAL RISKED',val:'$'+Math.round(totalRisk),col:C.muted},
                {label:'RETURN ON RISK',val:totalRisk>0?Math.round(totalPnl/totalRisk*100)+'%':'--',col:totalPnl>=0?C.green:C.red},
                {label:'AVG R-MULTIPLE',val:avgR.toFixed(1)+'R',col:avgR>=0?C.green:C.red},
                {label:'EXPECTANCY',val:(expectancy>=0?'+':'')+' $'+expectancy+'/trade',col:expectancy>=0?C.green:C.red},
              ].map((item,i)=>(
                <div key={i} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'8px 14px',flex:1,minWidth:100,textAlign:'center'}}>
                  <div className="mono" style={{fontSize:7,color:C.muted}}>{item.label}</div>
                  <div className="mono" style={{fontSize:14,color:item.col,fontWeight:700}}>{item.val}</div>
                </div>
              ));
            })()}
          </div>

          <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:1,marginBottom:8}}>TRADE R-MULTIPLES</div>
          <div style={{display:'flex',flexDirection:'column',gap:3,marginBottom:16}}>
            {trades.slice().reverse().slice(0,10).map((t,i)=>{
              const e=parseFloat(t.entry)||0,s=parseFloat(t.stop)||0;
              const risk=Math.abs(e-s)*100;
              const rMult=risk>0?((t.pnl||0)/risk):0;
              return(
                <div key={i} style={{display:'flex',alignItems:'center',gap:8,fontSize:10}}>
                  <span className="mono" style={{color:C.accent,fontWeight:700,minWidth:40}}>{t.symbol}</span>
                  <span className="mono" style={{color:t.type==='CALL'?C.green:C.red,minWidth:30}}>{t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</span>
                  <span style={{color:C.muted}}>Risked ${Math.round(risk)}</span>
                  <span style={{flex:1}}/>
                  <span className="mono" style={{color:t.result==='WIN'?C.green:C.red,fontWeight:700}}>{rMult>=0?'+':''}{rMult.toFixed(1)}R</span>
                  <span className="mono" style={{color:t.result==='WIN'?C.green:C.red}}>{(t.pnl||0)>=0?'+':''}${t.pnl||0}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {Object.entries(bySymbol).map(([sym,d])=>(
          <div key={sym} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:6,padding:'8px 14px',display:'flex',alignItems:'center',gap:16}}>
            <span className="mono" style={{fontSize:12,color:C.accent,fontWeight:700,minWidth:50}}>{sym}</span>
            <span className="mono" style={{fontSize:10,color:C.green}}>W:{d.w}</span>
            <span className="mono" style={{fontSize:10,color:C.red}}>L:{d.l}</span>
            <span className="mono" style={{fontSize:10,color:d.w+d.l>0?((d.w/(d.w+d.l)*100)>=50?C.green:C.red):C.muted}}>{d.w+d.l>0?Math.round(d.w/(d.w+d.l)*100):0}%</span>
            <span style={{flex:1}}/>
            <span className="mono" style={{fontSize:12,color:d.pnl>=0?C.green:C.red,fontWeight:700}}>{d.pnl>=0?'+':''}${Math.round(d.pnl)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}



//  AUTO JOURNAL FROM PAPER TRADES 
function AutoJournal() {
  const [data, setData] = useState({trades:[]});
  useEffect(()=>{
    fetch(API+'/api/markov/journal').then(r=>r.json()).then(d=>setData(d)).catch(()=>{});
  },[]);

  const trades = data.trades||[];
  if (trades.length === 0) return null;

  // Group by date
  const byDate = {};
  trades.forEach(t=>{
    const date = t.date || (t.entry_time ? t.entry_time.split('T')[0] : t.entryTime ? t.entryTime.split('T')[0] : new Date().toISOString().slice(0,10));
    if (!byDate[date]) byDate[date] = [];
    byDate[date].push(t);
  });

  return (
    <div style={{marginTop:20}}>
      <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.accent,letterSpacing:3,marginBottom:4}}>APEX LIVE TRADE JOURNAL</div>
      <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1,marginBottom:12}}>AUTOMATICALLY LOGGED FROM LIVE TRADING</div>

      {Object.entries(byDate).reverse().map(([date,dayTrades])=>{
        const w = dayTrades.filter(t=>t.result==='WIN').length;
        const l = dayTrades.filter(t=>t.result==='LOSS').length;
        const pnl = dayTrades.reduce((a,t)=>a+(t.pnl||0),0);
        return (
          <div key={date} style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:14,marginBottom:10}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
              <span style={{fontSize:13,color:C.text,fontWeight:700}}>{date}</span>
              <div style={{display:'flex',gap:10}}>
                <span className="mono" style={{fontSize:10,color:C.muted}}>{dayTrades.length} trades</span>
                <span className="mono" style={{fontSize:10,color:C.green}}>W:{w}</span>
                <span className="mono" style={{fontSize:10,color:C.red}}>L:{l}</span>
                <span className="mono" style={{fontSize:10,color:pnl>=0?C.green:C.red,fontWeight:700}}>{pnl>=0?'+':''}${Math.round(pnl)}</span>
              </div>
            </div>
            {dayTrades.map((t,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'4px 0',borderTop:i>0?'1px solid '+C.border+'20':'none',fontSize:10}}>
                <span className="mono" style={{color:C.accent,fontWeight:700,minWidth:40}}>{t.ticker||t.symbol||'?'}</span>
                <span className="mono" style={{color:(t.option_type||t.optionType||t.direction)==='CALL'||(t.option_type||t.optionType)==='call'?C.green:C.red,minWidth:35}}>{t.option_type?.toUpperCase()||t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</span>
                <span style={{color:C.muted}}>@ ${typeof(t.entry_premium||t.entryPremium)==='number'?(t.entry_premium||t.entryPremium).toFixed(2):'--'}</span>
                <span style={{color:C.muted}}>{t.exit_reason||t.exitReason||''}</span>
                <span style={{flex:1}}/>
                <span className="mono" style={{color:t.result==='WIN'?C.green:C.red,fontWeight:700}}>{t.result} {(t.pnl>=0?'+':'')+'$'+(t.pnl||0).toFixed(2)}</span>
                <span style={{color:C.muted,fontSize:9}}>{t.holdTime||0}min</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}


const Card = memo(({ sym, candles, ind, onOrderClick, onTradierClick }) => {
  const [chartOpen, setChartOpen] = useState(false);
  if (!ind) return null;
  const { price, vwap: v, ema9, macd: m, rsi: r, signal: sig, changePct, ml, setup } = ind;
  const has = sig?.type !== 'NEUTRAL';
  const isDark = true; // cards always use C theme colors
  return (
    <div className="card" style={{ borderColor: has ? sig.color + '44' : C.border, animation: 'fadeIn .4s ease', transition: 'border-color .3s' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div><div className="mono" style={{ fontSize: 15, fontWeight: 700, color: C.accent, letterSpacing: 1 }}>{sym}</div><div style={{ fontSize: 9, color: C.muted, letterSpacing: 2 }}>LIVE . 1-MIN</div></div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {sig && <span className="mono" style={{ fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 3, background: `${sig.color}18`, border: `1px solid ${sig.color}44`, color: sig.color }}>{sig.type === 'CALL' ? '+' : sig.type === 'PUT' ? '-' : '~'} {sig.label}</span>}
          {ml && has && <span className="mono" style={{ fontSize: 9, padding: '2px 8px', borderRadius: 3, background: `${ml.color}12`, border: `1px solid ${ml.color}33`, color: ml.color }}>{ml.score}% {ml.grade}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span className="mono" style={{ fontSize: 20, fontWeight: 700 }}>${price?.toFixed(2)}</span>
        <span className="mono" style={{ fontSize: 11, color: changePct >= 0 ? C.green : C.red }}>{changePct >= 0 ? '+' : ''}{changePct?.toFixed(2)}%</span>
      </div>
      <div onClick={() => setChartOpen(true)} style={{cursor:'pointer',position:'relative'}} title="Click to expand chart">
        <InteractiveChart candles={candles} vwapVal={v} />
        <div style={{position:'absolute',top:4,right:4,background:`${C.accent}22`,border:`1px solid ${C.accent}44`,borderRadius:3,padding:'1px 5px',fontSize:8,color:C.accent,fontFamily:'JetBrains Mono',pointerEvents:'none'}}>EXPAND</div>
      </div>
                  {/* Quick trade button */}
            {has && ind?.ml?.score >= 70 && (
              <div style={{marginTop:8}}>
                <button onClick={() => onOrderClick && onOrderClick({sym, type: sig.type, setup, ml})} style={{width:'100%',padding:'10px',borderRadius:6,border:'none',background:sig.type==='CALL'?C.green:C.red,color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                  <span>{sig.type === 'CALL' ? '+ TRADE CALL' : '- TRADE PUT'} - {sym}</span>
                  <span style={{fontSize:10,opacity:.7}}>{ind.ml.score}% AI</span>
                </button>
              </div>
            )}
          {chartOpen && <ChartModal sym={sym} candles={candles} ind={ind} onClose={() => setChartOpen(false)}/>}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
          <span style={{ fontSize: 9, color: C.muted, letterSpacing: 1 }}>RSI</span>
          <span className="mono" style={{ fontSize: 10, color: r > 70 ? C.red : r < 30 ? C.green : C.yellow }}>{r?.toFixed(1)}</span>
        </div>
        <div style={{ height: 2, background: C.dim, borderRadius: 1 }}><div style={{ height: '100%', width: `${Math.min(100, r || 0)}%`, background: r > 70 ? C.red : r < 30 ? C.green : C.yellow, borderRadius: 1, transition: 'width .8s' }} /></div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6, marginBottom: has ? 10 : 0 }}>
        {[['VWAP', v, C.yellow], ['EMA9', ema9, C.accent], ['MACD', m, m >= 0 ? C.green : C.red]].map(([l, val, col]) => (
          <div key={l} style={{ fontSize: 9, color: C.muted }}>{l}<br /><span className="mono" style={{ color: col, fontSize: 10 }}>${val?.toFixed(2)}</span></div>
        ))}
      </div>
      {has && (
        <>
          {ml && <div style={{ background: C.dim, borderRadius: 4, padding: '8px 10px', marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span className="mono" style={{ fontSize: 9, color: ml?.color||C.muted, letterSpacing: 2 }}>AI SCORE</span>
              <span className="mono" style={{ fontSize: 9, color: ml.color, fontWeight: 700 }}>{ml.label}</span>
            </div>
            <div style={{ height: 4, background: C.border, borderRadius: 2, marginBottom: 6 }}><div style={{ height: '100%', width: `${ml.score}%`, background: ml.color, borderRadius: 2, transition: 'width 1s' }} /></div>
            <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.6 }}>{ml.signals?.[0]}</div>
          </div>}
          <TrafficLight ind={ind} marketOpen={true} hour={new Date().getHours()} />
          <AIReasoning sym={sym} ind={ind} candles={candles}/>
          <PreTradeChecklist sym={sym} ind={ind}/>
          <OneTapSignal sym={sym} ind={ind} onOrder={onOrderClick} />
          <div style={{display:'flex',gap:6,marginTop:8}}>
            <button onClick={() => onTradierClick({ sym, type: sig.type, price, setup, ind })} style={{flex:2,padding:'10px 0',borderRadius:4,border:'none',cursor:'pointer',background:sig.color,color:'#020408',fontSize:11,fontWeight:900,letterSpacing:1,fontFamily:'JetBrains Mono'}}>
              ! {sig.type==='CALL'?'+':'-'}{sig.type} - SEND ORDER
            </button>
            <button onClick={() => onOrderClick({ sym, type: sig.type, price, setup, ind })} style={{flex:1,padding:'10px 0',borderRadius:4,border:`1px solid ${sig.color}44`,cursor:'pointer',background:'transparent',color:sig.color,fontSize:10,fontWeight:700,letterSpacing:1,fontFamily:'JetBrains Mono'}}>
              WEBULL
            </button>
          </div>
        </>
      )}
    </div>
  );
});

function Logo({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      <defs><linearGradient id="lg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor={C.accent} /><stop offset="100%" stopColor={C.purple} /></linearGradient></defs>
      <rect width="100" height="100" rx="16" fill={C.panel} />
      <rect x="2" y="2" width="96" height="96" rx="14" fill="none" stroke="url(#lg)" strokeWidth="1.5" />
      <circle cx="50" cy="42" r="20" fill="none" stroke="url(#lg)" strokeWidth="2" />
      <polyline points="33,42 38,35 44,48 50,28 57,54 63,36 67,42" fill="none" stroke={C.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      <text x="50" y="80" textAnchor="middle" fontFamily="Bebas Neue,sans-serif" fontSize="22" fill="url(#lg)" letterSpacing="4">APEX</text>
    </svg>
  );
}


//  Traffic Light System 
function TrafficLight({ ind, marketOpen, hour }) {
  if (!ind) return null;
  const { signal, ml, rsi, vwap: v, price, ema9, ema21 } = ind;
  const isPrime = hour >= 10 && hour < 12;
  const isChop = hour >= 12 && hour < 13;

  let light = 'RED', reason = '*', action = 'STAY OUT';
  if (signal?.type !== 'NEUTRAL' && ml?.score >= 80 && isPrime && !isChop) {
    light = 'GREEN';
    reason = signal.type === 'CALL' ? 'Strong bullish setup - VWAP reclaimed' : 'Strong bearish setup - VWAP rejected';
    action = signal.type === 'CALL' ? 'LOOK TO BUY CALLS' : 'LOOK TO BUY PUTS';
  } else if (signal?.type !== 'NEUTRAL' && ml?.score >= 65) {
    light = 'YELLOW';
    reason = isChop ? 'Valid signal but lunch chop - reduced size' : 'Moderate setup - wait for confirmation';
    action = 'WAIT FOR BETTER ENTRY';
  } else {
    reason = !marketOpen ? 'Market closed' : isChop ? 'Lunch chop - no trades' : signal?.type === 'NEUTRAL' ? 'No clear signal' : 'Low confidence - skip';
    action = 'NO TRADE';
  }

  const colors = { GREEN: C.green, YELLOW: C.yellow, RED: C.red };
  const col = colors[light];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: `${col}10`, border: `1px solid ${col}33`, borderRadius: 6, marginBottom: 8 }}>
      <div style={{ width: 12, height: 12, borderRadius: '50%', background: col, flexShrink: 0, boxShadow: `0 0 8px ${col}` }} />
      <div>
        <div className="mono" style={{ fontSize: 10, fontWeight: 700, color: col, letterSpacing: 1 }}>{light} - {action}</div>
        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{reason}</div>
      </div>
    </div>
  );
}

//  Simple Signal Decision Card 
function OneTapSignal({ sym, ind, onOrder }) {
  const [chain, setChain] = useState(null);
  const [expiries, setExpiries] = useState([]);
  const [expiry, setExpiry] = useState('');
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const { signal, price, setup, ml } = ind || {};
  if (!signal || signal.type === 'NEUTRAL') return null;
  const col = signal.type === 'CALL' ? C.green : C.red;
  const isCall = signal.type === 'CALL';

  // Load chain when expanded
  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    fetch(`${API}/api/options/expirations?symbol=${sym}&sandbox=false`)
      .then(r => r.json())
      .then(d => {
        const dates = d.expirations || [];
        setExpiries(dates);
        const first = dates[0];
        if (first) { setExpiry(first); return first; }
      })
      .then(exp => {
        if (!exp) { setLoading(false); return; }
        return fetch(`${API}/api/options/chain?symbol=${sym}&expiration=${exp}&sandbox=false`);
      })
      .then(r => r?.json())
      .then(d => {
        if (!d) { setLoading(false); return; }
        const opts = isCall ? (d.calls||[]) : (d.puts||[]);
        const near = opts.filter(o => o.ask > 0 && Math.abs(o.strike - price) / price < 0.04)
                        .sort((a,b) => Math.abs(a.strike-price) - Math.abs(b.strike-price));
        const best = near.find(o => isCall ? o.strike >= price : o.strike <= price) || near[0];
        setChain(best);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [expanded, sym, signal.type, price]);

  const ask = chain?.ask || 0;
  const limitPrice = ask > 0 ? (ask * 1.05).toFixed(2) : null;
  const totalCost = limitPrice ? Math.round(parseFloat(limitPrice) * qty * 100) : null;
  const dte = expiry ? Math.ceil((new Date(expiry) - new Date()) / 86400000) : 0;

  // Profit estimates
  const profitT1 = totalCost && setup ? Math.round(totalCost * ((Math.abs(parseFloat(setup.t1) - parseFloat(setup.entry)) / parseFloat(setup.entry)) * 100) * 0.08) : null;
  const profitT2 = profitT1 ? Math.round(profitT1 * 2) : null;

  if (!expanded) {
    // COLLAPSED: Action banner with key info visible immediately
    return (
      <div onClick={() => setExpanded(true)} style={{ background: `${col}12`, border: `2px solid ${col}`, borderRadius: 8, padding: 14, cursor: 'pointer', marginTop: 10, animation: 'fadeIn .3s ease' }}>
        {/* Signal type */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ fontFamily: 'Bebas Neue', fontSize: 26, color: col, letterSpacing: 2, lineHeight: 1 }}>
            {isCall ? '+ BUY CALL' : '- BUY PUT'} - {sym}
          </div>
          {ml && <div className="mono" style={{ fontSize: 12, fontWeight: 700, color: ml.color, background: `${ml.color}18`, padding: '3px 10px', borderRadius: 4 }}>{ml.score}% {ml.grade}</div>}
        </div>
        {/* Entry instruction */}
        {setup && (
          <div style={{ background: C.dim, borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
            <div className="mono" style={{ fontSize: 9, color: col, letterSpacing: 2, marginBottom: 6 }}>! WHAT TO DO RIGHT NOW</div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.8 }}>
              <span style={{ color: col, fontWeight: 700 }}>1. ENTER</span> when {sym} stock price is at or above <span style={{ color: col, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>${setup.entry}</span><br/>
              <span style={{ color: C.green, fontWeight: 700 }}>2. TAKE 50% PROFIT</span> when stock hits <span style={{ color: C.green, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>${setup.t1}</span><br/>
              <span style={{ color: C.green, fontWeight: 700 }}>3. TAKE REST</span> when stock hits <span style={{ color: C.green, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>${setup.t2}</span><br/>
              <span style={{ color: C.red, fontWeight: 700 }}>4. EXIT ALL</span> if stock drops to <span style={{ color: C.red, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>${setup.stop}</span> - no exceptions
            </div>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 11, color: C.muted }}>Tap for strike price, cost & send order</div>
          <div style={{ fontSize: 18, color: col }}></div>
        </div>
      </div>
    );
  }

  // EXPANDED: Clean simple order card
  return (
    <div style={{ background: C.panel, border: `2px solid ${col}`, borderRadius: 10, marginTop: 10, overflow: 'hidden', animation: 'fadeIn .2s ease' }}>
      {/* Header */}
      <div style={{ background: `${col}18`, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontFamily: 'Bebas Neue', fontSize: 24, color: col, letterSpacing: 2 }}>
          {isCall ? '+ BUY CALL' : '- BUY PUT'} - {sym}
        </div>
        <button onClick={() => setExpanded(false)} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer' }}>x</button>
      </div>

      <div style={{ padding: 16 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 24, color: C.muted }}>
            <div style={{ fontSize: 24, animation: 'spin 1s linear infinite', display: 'inline-block' }}>R</div>
            <div style={{ fontSize: 12, marginTop: 8 }}>Loading best strike...</div>
          </div>
        ) : (
          <>
            {/* THE TRADE - Big and clear */}
            <div style={{ background: C.dim, borderRadius: 8, padding: 16, marginBottom: 14, textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: C.muted, fontFamily: 'JetBrains Mono', letterSpacing: 2, marginBottom: 8 }}>THE TRADE</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: col, fontFamily: 'JetBrains Mono', marginBottom: 4 }}>
                {sym} ${chain?.strike} {signal.type}
              </div>
              <div style={{ fontSize: 13, color: C.muted, fontFamily: 'JetBrains Mono' }}>
                Expires: {expiry} {dte === 0 ? '(TODAY - 0DTE)' : dte === 1 ? '(TOMORROW)' : `(${dte} DAYS)`}
              </div>
            </div>

            {/* 3 KEY NUMBERS */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 14 }}>
              <div style={{ background: C.dim, borderRadius: 8, padding: '12px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono', marginBottom: 6 }}>YOU PAY</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.yellow, fontFamily: 'JetBrains Mono' }}>${limitPrice || '--'}</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>per share (limit)</div>
              </div>
              <div style={{ background: C.dim, borderRadius: 8, padding: '12px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono', marginBottom: 6 }}>TOTAL COST</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.yellow, fontFamily: 'JetBrains Mono' }}>${totalCost || '--'}</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>{qty} contract = 100 shares</div>
              </div>
              <div style={{ background: C.dim, borderRadius: 8, padding: '12px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono', marginBottom: 6 }}>MAX LOSS</div>
                <div style={{ fontSize: 22, fontWeight: 700, color: C.red, fontFamily: 'JetBrains Mono' }}>${totalCost || '--'}</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>if option expires $0</div>
              </div>
            </div>

            {/* PROFIT TARGETS */}
            {profitT1 && (
              <div style={{ background: `${C.green}08`, border: `1px solid ${C.green}33`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.green, fontFamily: 'JetBrains Mono', letterSpacing: 2, marginBottom: 10 }}> ESTIMATED PROFIT</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>TARGET 1 - Sell 50%</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: C.green, fontFamily: 'JetBrains Mono' }}>+${profitT1}</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Stock at ${setup?.t1}</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>TARGET 2 - Sell rest</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: C.green, fontFamily: 'JetBrains Mono' }}>+${profitT2}</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Stock at ${setup?.t2}</div>
                  </div>
                </div>
              </div>
            )}

            {/* ENTRY & EXIT LEVELS */}
            {setup && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 14 }}>
                <div style={{ background: `${col}12`, border: `1px solid ${col}44`, borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: C.muted, fontFamily: 'JetBrains Mono', marginBottom: 3 }}>ENTER WHEN</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: col, fontFamily: 'JetBrains Mono' }}>${setup.entry}</div>
                  <div style={{ fontSize: 9, color: C.muted }}>stock price</div>
                </div>
                <div style={{ background: `${C.red}12`, border: `1px solid ${C.red}44`, borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: C.muted, fontFamily: 'JetBrains Mono', marginBottom: 3 }}>EXIT IF WRONG</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.red, fontFamily: 'JetBrains Mono' }}>${setup.stop}</div>
                  <div style={{ fontSize: 9, color: C.muted }}>stop loss</div>
                </div>
                <div style={{ background: `${C.green}12`, border: `1px solid ${C.green}44`, borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, color: C.muted, fontFamily: 'JetBrains Mono', marginBottom: 3 }}>TAKE 50% PROFIT</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.green, fontFamily: 'JetBrains Mono' }}>${setup.t1}</div>
                  <div style={{ fontSize: 9, color: C.muted }}>target 1</div>
                </div>
              </div>
            )}

            {/* WHAT HAPPENS NEXT */}
            {setup && (
              <div style={{ background: C.dim, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
                <div className="mono" style={{ fontSize: 9, color: C.accent, letterSpacing: 2, marginBottom: 10 }}> STEP BY STEP - WHAT TO DO</div>
                {[
                  { num: '1', color: col,    text: `Watch ${sym} stock price on Webull`, sub: `Wait until it reaches $${setup.entry}` },
                  { num: '2', color: col,    text: 'Send your order below', sub: `Buy ${signal.type} at the strike price shown` },
                  { num: '3', color: C.yellow, text: 'Set a stop loss alert in Webull', sub: `Alert when ${sym} hits $${setup.stop} -> then SELL ALL` },
                  { num: '4', color: C.green,  text: `When ${sym} reaches $${setup.t1}`, sub: 'Sell 50% of your contracts immediately - lock in profit' },
                  { num: '5', color: C.green,  text: `When ${sym} reaches $${setup.t2}`, sub: 'Sell remaining contracts - trade complete' },
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 12, marginBottom: 10, alignItems: 'flex-start' }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: `${s.color}22`, border: `2px solid ${s.color}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span className="mono" style={{ fontSize: 11, color: s.color, fontWeight: 700 }}>{s.num}</span>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, color: C.text, fontWeight: 700 }}>{s.text}</div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{s.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CONTRACTS selector - Simple */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono', letterSpacing: 2, marginBottom: 8 }}>HOW MANY CONTRACTS?</div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[1, 2, 3, 5, 10].map(n => (
                  <button key={n} onClick={() => setQty(n)} style={{
                    flex: 1, padding: '12px 0', borderRadius: 6,
                    border: `2px solid ${qty === n ? col : C.border}`,
                    background: qty === n ? `${col}22` : C.dim,
                    color: qty === n ? col : C.muted,
                    fontSize: 16, fontWeight: 900, cursor: 'pointer',
                    fontFamily: 'JetBrains Mono', transition: 'all .15s'
                  }}>
                    {n}
                    <div style={{ fontSize: 9, fontWeight: 400, marginTop: 2, opacity: 0.7 }}>
                      ${totalCost ? Math.round((totalCost / qty) * n) : '--'}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* EXPIRY selector - Simple */}
            {expiries.length > 1 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono', letterSpacing: 2, marginBottom: 8 }}>WHEN DOES IT EXPIRE?</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {expiries.slice(0, 4).map(d => {
                    const days = Math.ceil((new Date(d) - new Date()) / 86400000);
                    const label = days === 0 ? 'TODAY 0DTE' : days === 1 ? 'TOMORROW' : days <= 7 ? ('THIS WEEK ' + days + 'd') : (days + ' DAYS');
                    const isSelected = expiry === d;
                    return (
                      <button key={d} onClick={() => setExpiry(d)} style={{
                        flex: 1, minWidth: 80, padding: '10px 8px', borderRadius: 6, textAlign: 'center',
                        border: `2px solid ${isSelected ? col : C.border}`,
                        background: isSelected ? `${col}18` : C.dim,
                        color: isSelected ? col : C.muted,
                        fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono',
                        whiteSpace: 'pre-line', lineHeight: 1.4
                      }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 6, fontFamily: 'JetBrains Mono' }}>
                   Beginners: pick THIS WEEK for more time. 0DTE = highest risk.
                </div>
              </div>
            )}

            {/* PRE-TRADE CHECKLIST */}
            <div style={{ background: C.dim, border: `1px solid ${C.yellow}33`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
              <div className="mono" style={{ fontSize: 9, color: C.yellow, letterSpacing: 2, marginBottom: 10 }}> PRE-TRADE CHECKLIST - CHECK BEFORE SENDING</div>
              {[
                ['Is the traffic light GREEN?', 'If yellow or red - do NOT trade'],
                ['Is it between 10am and 12pm?', 'Outside this window = lower win rate'],
                ['Is confidence 80% or above?', 'Below 80% = skip this trade'],
                ['Do you know your stop loss?', `Exit if ${sym} hits $${setup?.stop || '--'}`],
                ['Are you risking only what you can lose?', 'Start with 1 contract only'],
              ].map(([q, a], i) => (
                <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${C.yellow}`, flexShrink: 0, marginTop: 1 }}/>
                  <div>
                    <div style={{ fontSize: 12, color: C.text, fontWeight: 700 }}>{q}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{a}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* SEND BUTTON */}
            <button onClick={() => onOrder({ sym, type: signal.type, price, setup, ind })}
              style={{ width: '100%', padding: '16px 0', borderRadius: 8, border: 'none', background: col, color: '#000', fontSize: 15, fontWeight: 900, cursor: 'pointer', fontFamily: 'JetBrains Mono', letterSpacing: 2 }}>
              ! SEND ORDER -> TRADIER
            </button>
            <div style={{ textAlign: 'center', fontSize: 10, color: C.muted, marginTop: 8, fontFamily: 'JetBrains Mono' }}>
              Opens order panel for final review before sending
            </div>
          </>
        )}
      </div>
    </div>
  );
}

//  Pre-Market Briefing 
function PreMarketBriefing() {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gapData, setGapData] = useState({});
  const [WATCH, setWATCH] = React.useState(['SPY','QQQ','NVDA','AAPL','MSFT','GOOGL','AMZN','AMD']);
  React.useEffect(() => {
    fetch('/api/watchlist').then(r=>r.json()).then(d=>{
      if(d.tickers && d.tickers.length > 0) setWATCH(d.tickers);
    }).catch(()=>{});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/briefing`)
      .then(r => r.json())
      .then(d => { if (!d.error) setBriefing(d); setLoading(false); })
      .catch(() => setLoading(false));

    // Get pre-market quotes
    fetch(`${API}/api/quotes?symbols=${WATCH.join(',')}&sandbox=false`)
      .then(r => r.json())
      .then(d => {
        const gaps = {};
        (d.quotes || []).forEach(q => {
          const gapPct = q.prevClose ? ((q.last - q.prevClose) / q.prevClose) * 100 : 0;
          gaps[q.symbol] = { last: q.last, prevClose: q.prevClose, gapPct, gapType: gapPct > 0.5 ? 'GAP UP' : gapPct < -0.5 ? 'GAP DOWN' : 'FLAT' };
        });
        setGapData(gaps);
      }).catch(() => {});
  }, []);

  const now = new Date();
  const hour = now.getHours(), min = now.getMinutes();
  const isPreMarket = hour < 9 || (hour === 9 && min < 30);
  const isMarketHours = (hour > 9 || (hour === 9 && min >= 30)) && hour < 16;

  const timeLabel = isPreMarket ? 'PRE-MARKET BRIEFING' : isMarketHours ? 'MARKET IS OPEN' : 'AFTER HOURS';
  const timeColor = isPreMarket ? C.yellow : isMarketHours ? C.green : C.muted;

  return (
    <div style={{ animation: 'fadeIn .3s ease' }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: 14, border: `1px solid ${timeColor}33` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div className="mono" style={{ fontSize: 9, color: timeColor, letterSpacing: 2 }}>{timeLabel}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginTop: 4 }}>
              {now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="mono" style={{ fontSize: 24, color: timeColor }}>{now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>EST</div>
          </div>
        </div>

        {/* Daily plan */}
        <div style={{ background: C.dim, borderRadius: 8, padding: 12 }}>
          <div className="mono" style={{ fontSize: 9, color: C.accent, letterSpacing: 2, marginBottom: 10 }}>YOUR GAME PLAN TODAY</div>
          {[
            { time: '9:00-9:30', label: 'PRE-MARKET', action: 'Check gaps below. No trades yet. Identify bias.', color: C.muted },
            { time: '9:30-10:00', label: 'OPEN', action: 'Watch direction only. Let price discover VWAP. No entry.', color: C.yellow },
            { time: '9:45-12:30', label: 'PRIME WINDOW ', action: 'YOUR BEST TIME. Trade VWAP reclaims and retests only.', color: C.green },
            { time: '12:00-1:00', label: 'LUNCH CHOP', action: 'NO TRADES. Close open positions. Eat lunch.', color: C.red },
            { time: '12:30-3:30', label: 'WIND DOWN', action: 'NO NEW TRADES. Bot manages exits only.', color: C.muted },
          ].map(s => (
            <div key={s.time} style={{ display: 'flex', gap: 12, marginBottom: 8, alignItems: 'flex-start' }}>
              <div className="mono" style={{ fontSize: 10, color: C.muted, width: 80, flexShrink: 0, paddingTop: 1 }}>{s.time}</div>
              <div>
                <div className="mono" style={{ fontSize: 10, color: s.color, fontWeight: 700, letterSpacing: 1 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>{s.action}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gap Scanner */}
      <div className="card" style={{ marginBottom: 14 }}>
        <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 12 }}>GAP SCANNER - PRE-MARKET MOVERS</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {WATCH.map(sym => {
            const g = gapData[sym];
            const col = !g ? C.muted : g.gapPct > 0.5 ? C.green : g.gapPct < -0.5 ? C.red : C.muted;
            const bias = !g ? '--' : g.gapPct > 1 ? ' BULLISH' : g.gapPct > 0.3 ? ' SLIGHT BULL' : g.gapPct < -1 ? ' BEARISH' : g.gapPct < -0.3 ? ' SLIGHT BEAR' : ' NEUTRAL';
            return (
              <div key={sym} style={{ background: C.dim, border: `1px solid ${col}33`, borderRadius: 8, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span className="mono" style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>{sym}</span>
                  <span className="mono" style={{ fontSize: 11, color: col, fontWeight: 700 }}>{g ? `${g.gapPct >= 0 ? '+' : ''}${g.gapPct?.toFixed(2)}%` : '--'}</span>
                </div>
                <div className="mono" style={{ fontSize: 10, color: col, marginBottom: 4 }}>{g?.gapType || 'LOADING'}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{bias}</div>
                {g?.last && <div className="mono" style={{ fontSize: 11, color: C.text, marginTop: 4 }}>${g.last?.toFixed(2)} <span style={{ fontSize: 9, color: C.muted }}>vs ${g.prevClose?.toFixed(2)}</span></div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Economic Events */}
      {briefing && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 12 }}>TODAY'S EVENTS - RISK CALENDAR</div>
          {briefing.hasHighImpact && (
            <div style={{ background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 6, padding: 10, marginBottom: 12 }}>
              <div className="mono" style={{ fontSize: 10, color: C.red, fontWeight: 700, letterSpacing: 1 }}> HIGH IMPACT EVENT TODAY - REDUCE SIZE</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Consider waiting until after the event before trading. Volatility will spike.</div>
            </div>
          )}
          {briefing.cautions?.length > 0 ? (
            briefing.cautions.map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', background: c.includes('HIGH') ? C.red : C.yellow, flexShrink: 0 }} />
                <div style={{ fontSize: 12, color: C.text }}>{c}</div>
              </div>
            ))
          ) : (
            <div style={{ fontSize: 12, color: C.green }}>v No major economic events today - clean trading environment</div>
          )}
        </div>
      )}

      {/* Earnings */}
      {briefing?.earningsToday?.length > 0 && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="mono" style={{ fontSize: 9, color: C.yellow, letterSpacing: 2, marginBottom: 12 }}>EARNINGS TODAY - AVOID THESE TICKERS</div>
          {briefing.earningsToday.map((e, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${C.border}` }}>
              <span className="mono" style={{ fontSize: 13, fontWeight: 700, color: C.yellow }}>{e.symbol}</span>
              <span style={{ fontSize: 11, color: C.muted }}>{e.hour === 'bmo' ? 'Pre-market' : 'After close'} . EPS Est: {e.epsEstimate || '--'}</span>
            </div>
          ))}
        </div>
      )}

      {/* Rules reminder */}
      <div className="card" style={{ border: `1px solid ${C.accent}22` }}>
        <div className="mono" style={{ fontSize: 9, color: C.accent, letterSpacing: 2, marginBottom: 12 }}>APEX TRADING RULES - READ EVERY MORNING</div>
        {[
          'Only trade during your prime window: 9:45am - 12:30pm',
          'Only enter on VWAP reclaims or retests - not breakouts',
          'Wait for GREEN signal with 80%+ confidence before entering',
          'Max 1-2 contracts until your win rate exceeds 60%',
          'Take 50% off at Target 1 - always, no exceptions',
          'Stop loss is non-negotiable - exit at your level, no hoping',
          'No trades during lunch (12-1pm) - close positions first',
          'Max 2 losses per day - if you hit 2 losses, stop trading',
        ].map((rule, i) => (
          <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
            <span className="mono" style={{ fontSize: 10, color: C.accent, flexShrink: 0 }}>{i + 1}.</span>
            <span style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>{rule}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

//  Tradier One-Tap Execution 
function TradierOrderPanel({ sym, type, price, setup, ind, onClose }) {
  const [chain, setChain] = useState([]);
  const [expirations, setExpirations] = useState([]);
  const [expiry, setExpiry] = useState('');
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState(1);
  const [limitPct, setLimitPct] = useState(5); // % above ask for faster fill
  const [limitPrice, setLimitPrice] = useState('');
  const [orderType, setOrderType] = useState('limit');
  const [useSandbox, setUseSandbox] = useState(true);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [error, setError] = useState('');
  const [step, setStep] = useState('setup'); // setup | confirm | sent
  const col = type === 'CALL' ? C.green : C.red;

  // Load expirations
  useEffect(() => {
    fetch(`${API}/api/options/expirations?symbol=${sym}&sandbox=${useSandbox}`)
      .then(r => r.json())
      .then(d => {
        const dates = d.expirations || [];
        setExpirations(dates);
        if (dates[0]) setExpiry(dates[0]);
      }).catch(() => setLoading(false));
  }, [sym, useSandbox]);

  // Load chain when expiry changes
  useEffect(() => {
    if (!expiry) return;
    setLoading(true);
    fetch(`${API}/api/options/chain?symbol=${sym}&expiration=${expiry}&sandbox=${useSandbox}`)
      .then(r => r.json())
      .then(d => {
        const opts = type === 'CALL' ? (d.calls || []) : (d.puts || []);
        const sorted = opts.filter(o => o.ask > 0).sort((a, b) => Math.abs(a.strike - price) - Math.abs(b.strike - price));
        setChain(sorted.slice(0, 8));
        // Auto-select best strike
        // Filter to strikes within 3% of current price to avoid bad chain data
        const nearStrikes = sorted.filter(o => Math.abs(o.strike - price) / price < 0.03);
        const useChain = nearStrikes.length > 0 ? nearStrikes : sorted;
        const best = useChain.find(o => type === 'CALL' ? o.strike >= price : o.strike <= price) || useChain[0];
        if (best) {
          setSelected(best);
          const buffered = (best.ask * (1 + limitPct / 100)).toFixed(2);
          setLimitPrice(buffered);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [expiry, sym, type, price]);

  // Update limit when pct changes
  useEffect(() => {
    if (selected?.ask) {
      setLimitPrice((selected.ask * (1 + limitPct / 100)).toFixed(2));
    }
  }, [limitPct, selected]);

  const dte = expiry ? Math.ceil((new Date(expiry) - new Date()) / (1000 * 60 * 60 * 24)) : 0;
  const totalCost = selected ? (parseFloat(limitPrice) * qty * 100).toFixed(0) : 0;
  const optionSym = selected ? `${sym}${expiry?.replace(/-/g, '').slice(2)}${type === 'CALL' ? 'C' : 'P'}${String(selected.strike * 1000).padStart(8, '0')}` : '';

  // Send order to Tradier
  const sendOrder = async () => {
    if (!selected || !expiry) return;
    setSending(true);
    setError('');

    try {
      const orderPayload = {
        class: 'option',
        symbol: sym,
        option_symbol: optionSym,
        side: 'buy_to_open',
        quantity: qty,
        type: orderType,
        duration: 'day',
        price: orderType === 'limit' ? parseFloat(limitPrice) : undefined,
        sandbox: useSandbox,
        // Setup data for reference
        entry: setup?.entry,
        stop: setup?.stop,
        target1: setup?.t1,
        target2: setup?.t2,
      };

      const r = await fetch(`${API}/api/tradier/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderPayload)
      });
      const d = await r.json();

      if (d.order?.id || d.id) {
        setOrderResult({
          id: d.order?.id || d.id,
          status: d.order?.status || 'submitted',
          symbol: optionSym,
          qty,
          price: limitPrice,
          type: orderType,
          side: 'buy_to_open',
          cost: totalCost,
          sandbox: useSandbox,
        });
        setStep('sent');
      } else {
        setError(d.error || d.message || JSON.stringify(d));
      }
    } catch (e) {
      setError(`Connection error: ${e.message}`);
    }
    setSending(false);
  };

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.92)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'fadeIn .2s ease' }}>
      <div style={{ background: C.panel, border: `2px solid ${col}66`, borderRadius: 14, width: 560, maxHeight: '92vh', overflow: 'auto' }}>

        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="mono" style={{ fontSize: 15, fontWeight: 700, color: col }}>{type === 'CALL' ? '+ BUY CALL' : '- BUY PUT'} - {sym}</div>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 3, fontFamily: 'JetBrains Mono' }}>
              {useSandbox ? ' SANDBOX - Paper Trade (Safe)' : ' LIVE - Real Money'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={async () => { const ns = !useSandbox; if (!ns) { if (!window.confirm('Switch to LIVE TRADING with REAL MONEY? Real trades on account 6YB79458.')) return; } setUseSandbox(ns); try { await fetch(API+'/api/bot/mode', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({useSandbox:ns})}); } catch(e){} }} style={{ padding: '4px 10px', borderRadius: 4, border: `1px solid ${useSandbox ? C.accent : C.red}`, background: useSandbox ? `${C.accent}18` : `${C.red}18`, color: useSandbox ? C.accent : C.red, fontSize: 9, cursor: 'pointer', fontFamily: 'JetBrains Mono', fontWeight: 700 }}>
              {useSandbox ? 'SANDBOX' : 'LIVE'}
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 20, cursor: 'pointer' }}>x</button>
          </div>
        </div>

        <div style={{ padding: 20 }}>

          {/* SENT confirmation */}
          {step === 'sent' && orderResult && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}></div>
              <div style={{ fontFamily: 'Bebas Neue', fontSize: 28, color: C.green, letterSpacing: 2, marginBottom: 8 }}>ORDER SENT!</div>
              <div style={{ background: C.dim, border: `1px solid ${C.green}33`, borderRadius: 8, padding: 16, margin: '16px 0', textAlign: 'left' }}>
                <div className="mono" style={{ fontSize: 9, color: C.green, letterSpacing: 2, marginBottom: 10 }}>ORDER CONFIRMATION</div>
                {[
                  ['Order ID', `#${orderResult.id}`],
                  ['Status', orderResult.status.toUpperCase()],
                  ['Symbol', orderResult.symbol],
                  ['Side', 'BUY TO OPEN'],
                  ['Quantity', `${orderResult.qty} contract${orderResult.qty > 1 ? 's' : ''}`],
                  ['Price', `$${orderResult.price} limit`],
                  ['Est. Cost', `$${orderResult.cost}`],
                  ['Mode', orderResult.sandbox ? 'SANDBOX (paper)' : 'LIVE'],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `1px solid ${C.border}22` }}>
                    <span style={{ fontSize: 11, color: C.muted }}>{l}</span>
                    <span className="mono" style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>{v}</span>
                  </div>
                ))}
              </div>
              {setup && (
                <div style={{ background: C.dim, borderRadius: 8, padding: 14, marginBottom: 16, textAlign: 'left' }}>
                  <div className="mono" style={{ fontSize: 9, color: C.yellow, letterSpacing: 2, marginBottom: 8 }}>YOUR EXIT PLAN</div>
                  <div style={{ fontSize: 12, color: C.text, lineHeight: 1.8 }}>
                     Stop Loss: Stock hits <span style={{ color: C.red, fontWeight: 700 }}>${setup.stop}</span> -> exit immediately<br/>
                     Target 1: Stock hits <span style={{ color: C.green, fontWeight: 700 }}>${setup.t1}</span> -> sell 50%, move stop to breakeven<br/>
                     Target 2: Stock hits <span style={{ color: C.green, fontWeight: 700 }}>${setup.t2}</span> -> sell remaining 50%
                  </div>
                </div>
              )}
              <button onClick={onClose} style={{ width: '100%', padding: '12px 0', borderRadius: 6, border: 'none', background: C.green, color: C.dark, fontSize: 13, fontWeight: 900, cursor: 'pointer', fontFamily: 'JetBrains Mono', letterSpacing: 2 }}>
                DONE - MONITOR TRADE
              </button>
            </div>
          )}

          {/* Setup step */}
          {step === 'setup' && (
            <>
              {/* Price info */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8, marginBottom: 16 }}>
                {[
                  ['STOCK PRICE', `$${price?.toFixed(2)}`, C.text],
                  ['SIGNAL', type, col],
                  ['AI SCORE', ind?.ml ? `${ind.ml.score}% ${ind.ml.grade}` : '--', ind?.ml?.color || C.muted],
                ].map(([l, v, c]) => (
                  <div key={l} style={{ background: C.dim, borderRadius: 6, padding: '10px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: 8, color: C.muted, fontFamily: 'JetBrains Mono', letterSpacing: 1, marginBottom: 4 }}>{l}</div>
                    <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: c }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Expiration selector */}
              <div style={{ marginBottom: 14 }}>
                <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>EXPIRATION</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {expirations.slice(0, 5).map(d => {
                    const days = Math.ceil((new Date(d) - new Date()) / (1000 * 60 * 60 * 24));
                    const tag = days === 0 ? '0DTE' : days <= 3 ? `${days}DTE` : days <= 7 ? 'WEEKLY' : `${days}D`;
                    return (
                      <button key={d} onClick={() => setExpiry(d)} style={{ padding: '6px 10px', borderRadius: 4, border: `1px solid ${expiry === d ? col : C.border}`, background: expiry === d ? `${col}18` : 'transparent', color: expiry === d ? col : C.muted, fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono', textAlign: 'center' }}>
                        {d}<br/><span style={{ fontSize: 8, opacity: 0.7 }}>{tag}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Strike selector */}
              {loading ? (
                <div style={{ textAlign: 'center', padding: 24, color: C.muted, fontSize: 12 }}>Loading options chain...</div>
              ) : chain.length > 0 ? (
                <div style={{ marginBottom: 14 }}>
                  <div className="mono" style={{ fontSize: 9, color: C.muted, letterSpacing: 2, marginBottom: 8 }}>SELECT STRIKE - {type}S</div>
                  <div style={{ background: C.dim, borderRadius: 8, overflow: 'hidden' }}>
                    {chain.map((opt, i) => {
                      const mid = ((opt.bid || 0) + (opt.ask || 0)) / 2;
                      const isSel = selected?.strike === opt.strike;
                      const isATM = Math.abs(opt.strike - price) < 0.5;
                      const buffered = (opt.ask * (1 + limitPct / 100)).toFixed(2);
                      return (
                        <div key={i} onClick={() => { setSelected(opt); setLimitPrice(buffered); }} style={{ display: 'grid', gridTemplateColumns: '70px 60px 60px 70px auto', gap: 0, padding: '8px 12px', cursor: 'pointer', background: isSel ? `${col}18` : isATM ? 'rgba(255,255,255,.02)' : 'transparent', borderBottom: `1px solid ${C.border}22`, borderLeft: isSel ? `3px solid ${col}` : '3px solid transparent' }}>
                          <div className="mono" style={{ fontSize: 12, fontWeight: 700, color: isATM ? C.yellow : C.text }}>${opt.strike}{isATM ? ' *' : ''}</div>
                          <div className="mono" style={{ fontSize: 11, color: C.red }}>${opt.bid?.toFixed(2) || '--'}</div>
                          <div className="mono" style={{ fontSize: 11, color: C.green }}>${opt.ask?.toFixed(2) || '--'}</div>
                          <div className="mono" style={{ fontSize: 11, color: col, fontWeight: 700 }}>${buffered}</div>
                          <div style={{ fontSize: 9, color: C.muted, fontFamily: 'JetBrains Mono' }}>{opt.volume ? opt.volume.toLocaleString() + ' vol' : ''}</div>
                        </div>
                      );
                    })}
                    <div style={{ padding: '4px 12px', background: C.panel }}>
                      <div style={{ fontSize: 9, color: C.muted, fontFamily: 'JetBrains Mono' }}>* = At the money  |  Last column = your limit price (+{limitPct}% buffer)</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ padding: 16, textAlign: 'center', color: C.muted, fontSize: 12, marginBottom: 14 }}>No chain data - market may be closed</div>
              )}

              {/* Order controls */}
              <div style={{ background: C.dim, borderRadius: 8, padding: 16, marginBottom: 14 }}>
                <div className="mono" style={{ fontSize: 9, color: col, letterSpacing: 2, marginBottom: 14 }}>ORDER SETTINGS</div>

                {/* Contracts */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>CONTRACTS</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[1, 2, 3, 5, 10].map(n => (
                      <button key={n} onClick={() => setQty(n)} style={{ flex: 1, padding: '8px 0', borderRadius: 4, border: `1px solid ${qty === n ? col : C.border}`, background: qty === n ? `${col}18` : 'transparent', color: qty === n ? col : C.muted, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>{n}</button>
                    ))}
                  </div>
                </div>

                {/* Limit buffer */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 10, color: C.muted }}>LIMIT PRICE BUFFER (above ask)</span>
                    <span className="mono" style={{ fontSize: 10, color: col }}>+{limitPct}% for faster fill</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[0, 3, 5, 8, 10].map(pct => (
                      <button key={pct} onClick={() => setLimitPct(pct)} style={{ flex: 1, padding: '6px 0', borderRadius: 4, border: `1px solid ${limitPct === pct ? C.yellow : C.border}`, background: limitPct === pct ? `${C.yellow}18` : 'transparent', color: limitPct === pct ? C.yellow : C.muted, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>
                        {pct === 0 ? 'EXACT' : `+${pct}%`}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 6, fontFamily: 'JetBrains Mono' }}>
                    +5% recommended - gets you filled faster when price is moving
                  </div>
                </div>

                {/* Order type */}
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>ORDER TYPE</div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[['LIMIT', 'limit'], ['MARKET', 'market']].map(([l, v]) => (
                      <button key={v} onClick={() => setOrderType(v)} style={{ flex: 1, padding: '8px 0', borderRadius: 4, border: `1px solid ${orderType === v ? col : C.border}`, background: orderType === v ? `${col}18` : 'transparent', color: orderType === v ? col : C.muted, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>{l}</button>
                    ))}
                  </div>
                </div>

                {/* Cost summary */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
                  {/* Profit calculator */}
                {setup && parseFloat(totalCost) > 0 && (() => {
                  const stockMove = type==='CALL' ? parseFloat(setup.t1)-parseFloat(setup.entry) : parseFloat(setup.entry)-parseFloat(setup.t1);
                  const pctMove = (stockMove/parseFloat(setup.entry))*100;
                  const leverage = 8;
                  const profitT1 = Math.round(parseFloat(totalCost) * pctMove * leverage / 100);
                  const profitT2 = Math.round(parseFloat(totalCost) * pctMove * leverage * 2 / 100);
                  const rrActual = profitT1 > 0 ? (profitT1/parseFloat(totalCost)).toFixed(1) : 0;
                  return (
                    <div style={{background:`${C.green}08`,border:`1px solid ${C.green}22`,borderRadius:6,padding:'10px 14px',marginBottom:12}}>
                      <div className="mono" style={{fontSize:9,color:C.green,letterSpacing:2,marginBottom:8}}>PROFIT CALCULATOR</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:6}}>
                        {[
                          ['COST',`$${totalCost}`,C.yellow],
                          ['T1 PROFIT',`+$${profitT1}`,C.green],
                          ['T2 PROFIT',`+$${profitT2}`,C.green],
                          ['MAX LOSS',`-$${totalCost}`,C.red],
                        ].map(([l,v,c])=>(
                          <div key={l} style={{background:C.panel,borderRadius:4,padding:'6px 8px',textAlign:'center'}}>
                            <div style={{fontSize:7,color:C.muted,fontFamily:'JetBrains Mono',letterSpacing:1,marginBottom:2}}>{l}</div>
                            <div className="mono" style={{fontSize:13,fontWeight:700,color:c}}>{v}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{fontSize:10,color:C.muted,marginTop:6,fontFamily:'JetBrains Mono'}}>
                        Estimated R:R ratio: {rrActual}:1 . Based on {pctMove.toFixed(2)}% stock move to T1
                      </div>
                    </div>
                  );
                })()}
                {[
                    ['YOUR LIMIT', `$${limitPrice}`, col],
                    ['TOTAL COST', `$${totalCost}`, C.yellow],
                    ['CONTRACTS', `${qty}x100`, C.accent],
                  ].map(([l, v, c]) => (
                    <div key={l} style={{ background: C.panel, borderRadius: 4, padding: '8px 10px', textAlign: 'center' }}>
                      <div style={{ fontSize: 8, color: C.muted, letterSpacing: 1, marginBottom: 3 }}>{l}</div>
                      <div className="mono" style={{ fontSize: 14, fontWeight: 700, color: c }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Exit plan reminder */}
              {setup && (
                <div style={{ background: C.dim, border: `1px solid ${C.yellow}22`, borderRadius: 8, padding: 12, marginBottom: 14 }}>
                  <div className="mono" style={{ fontSize: 9, color: C.yellow, letterSpacing: 2, marginBottom: 8 }}>EXIT PLAN - SET ALERTS AFTER FILL</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6 }}>
                    {[['ENTRY', `$${setup.entry}`, col], ['STOP', `$${setup.stop}`, C.red], ['TARGET 1', `$${setup.t1}`, C.green], ['TARGET 2', `$${setup.t2}`, C.green]].map(([l, v, c]) => (
                      <div key={l} style={{ background: C.panel, borderRadius: 4, padding: '6px 8px', textAlign: 'center' }}>
                        <div style={{ fontSize: 8, color: C.muted, marginBottom: 2 }}>{l}</div>
                        <div className="mono" style={{ fontSize: 11, fontWeight: 700, color: c }}>{v}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Error */}
              {error && (
                <div style={{ background: `${C.red}12`, border: `1px solid ${C.red}33`, borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
                  <div className="mono" style={{ fontSize: 10, color: C.red, fontWeight: 700, marginBottom: 4 }}>ORDER FAILED</div>
                  <div style={{ fontSize: 11, color: C.text }}>{error}</div>
                  {useSandbox && <div style={{ fontSize: 10, color: C.muted, marginTop: 6 }}>Sandbox API may not support this order type. Try switching to a different expiry.</div>}
                </div>
              )}

              {/* Live warning */}
              {!useSandbox && (
                <div style={{ background: `${C.red}12`, border: `1px solid ${C.red}44`, borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
                  <div className="mono" style={{ fontSize: 10, color: C.red, fontWeight: 700, letterSpacing: 1 }}> LIVE MODE - REAL MONEY TRADE</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>This order will use real funds. Make sure you understand the risk before sending.</div>
                </div>
              )}

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={onClose} style={{ flex: 1, padding: '12px 0', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.muted, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'JetBrains Mono' }}>CANCEL</button>
                <button
                  onClick={sendOrder}
                  disabled={sending || !selected}
                  style={{ flex: 2, padding: '14px 0', borderRadius: 6, border: 'none', background: sending || !selected ? C.muted : col, color: C.dark, fontSize: 14, fontWeight: 900, cursor: sending || !selected ? 'not-allowed' : 'pointer', fontFamily: 'JetBrains Mono', letterSpacing: 2, transition: 'all .2s' }}
                >
                  {sending ? 'SENDING...' : `SEND ORDER - $${totalCost}`}
                </button>
              </div>

              <div style={{ textAlign: 'center', marginTop: 10, fontSize: 10, color: C.muted, fontFamily: 'JetBrains Mono' }}>
                {useSandbox ? 'Paper trade - no real money used' : `Live order - ${qty} contract${qty > 1 ? 's' : ''} at $${limitPrice} limit`}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


//  License Gate Screen 
function LicenseGate({ onUnlock }) {
  const [mode, setMode] = useState('login'); // login or signup
  const [email, setEmail] = useState('');
  const [key, setKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('apex_license');
    if (stored) { validateKey(stored, true); } else { setChecking(false); }
  }, []);

  const validateKey = async (k, silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/api/license/validate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: k, email })
      });
      const d = await r.json();
      if (d.valid) {
        localStorage.setItem('apex_license', k);
        localStorage.setItem('apex_plan', d.plan || 'starter');
        onUnlock({ key: k, plan: d.plan, email: d.email });
      } else {
        if (!silent) setError(d.reason || 'Invalid credentials');
        localStorage.removeItem('apex_license');
        setChecking(false);
      }
    } catch {
      if (k === 'APEX-DEV-MODE' || k === 'APEX-MASTER-2026') {
        localStorage.setItem('apex_license', k);
        localStorage.setItem('apex_plan', 'owner');
        onUnlock({ key: k, plan: 'owner', email: 'owner' });
      } else {
        if (!silent) setError('Could not connect to server');
        setChecking(false);
      }
    }
    if (!silent) setLoading(false);
  };

  if (checking) return (
    <div style={{minHeight:'100vh',background:'#04070d',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:16}}>
      <div style={{width:40,height:40,border:'3px solid #0f1f35',borderTop:'3px solid #00c9e8',borderRadius:'50%',animation:'spin .8s linear infinite'}}/>
      <style>{'@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#04070d',display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{width:'100%',maxWidth:420}}>
        {/* Logo */}
        <div style={{textAlign:'center',marginBottom:40}}>
          <svg width="56" height="56" viewBox="0 0 100 100"><defs><linearGradient id="alg" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#00c9e8"/><stop offset="100%" stopColor="#7c4dff"/></linearGradient></defs><rect width="100" height="100" rx="18" fill="#0d1117"/><rect x="3" y="3" width="94" height="94" rx="15" fill="none" stroke="url(#alg)" strokeWidth="1.5"/><polyline points="18,58 30,42 44,58 58,24 72,52 84,36" fill="none" stroke="#00c9e8" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/><text x="50" y="82" textAnchor="middle" fontFamily="Arial Black" fontSize="16" fontWeight="900" fill="url(#alg)" letterSpacing="3">APEX</text></svg>
          <div style={{fontFamily:'Bebas Neue, Arial Black, sans-serif',fontSize:32,color:'#ffffff',letterSpacing:8,marginTop:12}}>APEX SCANNER</div>
          <div style={{fontSize:12,color:'#3d5166',letterSpacing:3,fontFamily:'monospace',marginTop:4}}>AI-POWERED OPTIONS TRADING</div>
        </div>

        {/* Card */}
        <div style={{background:'#080e18',border:'1px solid #0f1f35',borderRadius:16,padding:'32px 28px'}}>
          {/* Tabs */}
          <div style={{display:'flex',marginBottom:24,borderBottom:'1px solid #0f1f35'}}>
            {[['login','Sign In'],['signup','Create Account']].map(([m,l])=>(
              <button key={m} onClick={()=>{setMode(m);setError('');}} style={{flex:1,padding:'12px 0',background:'none',border:'none',borderBottom:`2px solid ${mode===m?'#00c9e8':'transparent'}`,color:mode===m?'#ffffff':'#3d5166',fontSize:13,fontWeight:600,cursor:'pointer',letterSpacing:1,fontFamily:'inherit',transition:'all .2s'}}>{l}</button>
            ))}
          </div>

          {mode === 'signup' && (
            <div style={{marginBottom:16}}>
              <label style={{display:'block',fontSize:11,color:'#3d5166',letterSpacing:1,marginBottom:6,fontWeight:600}}>EMAIL</label>
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" type="email" style={{width:'100%',padding:'12px 16px',borderRadius:8,border:'1px solid #0f1f35',background:'#060b14',color:'#cdd9e5',fontSize:14,fontFamily:'inherit',outline:'none',transition:'border .2s'}} onFocus={e=>e.target.style.borderColor='#00c9e8'} onBlur={e=>e.target.style.borderColor='#0f1f35'}/>
            </div>
          )}

          <div style={{marginBottom:16}}>
            <label style={{display:'block',fontSize:11,color:'#3d5166',letterSpacing:1,marginBottom:6,fontWeight:600}}>LICENSE KEY</label>
            <input value={key} onChange={e=>setKey(e.target.value.toUpperCase())} onKeyDown={e=>e.key==='Enter'&&validateKey(key)} placeholder="APEX-PRO-XXXXXX" style={{width:'100%',padding:'12px 16px',borderRadius:8,border:'1px solid #0f1f35',background:'#060b14',color:'#cdd9e5',fontSize:14,fontFamily:'monospace',letterSpacing:2,outline:'none',transition:'border .2s'}} onFocus={e=>e.target.style.borderColor='#00c9e8'} onBlur={e=>e.target.style.borderColor='#0f1f35'}/>
          </div>

          {error && <div style={{background:'#ff174415',border:'1px solid #ff174433',borderRadius:8,padding:'8px 12px',marginBottom:16,fontSize:12,color:'#ff1744'}}>{error}</div>}

          <button onClick={()=>validateKey(key)} disabled={loading || !key} style={{width:'100%',padding:'14px',borderRadius:8,border:'none',background:loading?'#0f1f35':'linear-gradient(135deg,#00c9e8,#7c4dff)',color:'#000',fontSize:14,fontWeight:700,cursor:loading?'wait':'pointer',fontFamily:'inherit',letterSpacing:1,transition:'all .2s',opacity:!key?.5:1}}>{loading ? 'Validating...' : mode==='login' ? 'Sign In' : 'Activate Account'}</button>

          <div style={{textAlign:'center',marginTop:20}}>
            <div style={{fontSize:11,color:'#3d5166',lineHeight:1.8}}>
              {mode === 'login' ? "Don't have a key? " : 'Get your key at '}
              <span style={{color:'#00c9e8',cursor:'pointer'}}>apexscanner.io</span>
            </div>
          </div>
        </div>

        {/* Features preview */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8,marginTop:24}}>
          {[['*','Real-time'],['*','AI Signals'],['*','Live Charts'],['*','Options Flow'],['*','Alerts'],['*','Journal']].map(([ic,lb])=>(
            <div key={lb} style={{background:'#080e18',border:'1px solid #0f1f35',borderRadius:8,padding:'12px 8px',textAlign:'center'}}>
              <div style={{fontSize:18,marginBottom:4}}>{ic}</div>
              <div style={{fontSize:10,color:'#3d5166',fontWeight:600,letterSpacing:.5}}>{lb}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
//  DAILY TRADING GUIDE 

//  TRADING EDUCATION (LEARN TAB) 

//  LEARN TAB VISUAL COMPONENTS 
// Mini mockups of actual UI elements for the guide

function MockCard({ sym, price, change, signal, score, rsi }) {
  const col = signal === 'CALL' ? C.green : signal === 'PUT' ? C.red : C.muted;
  return (
    <div style={{background:C.dim,border:'1px solid '+C.border,borderRadius:8,padding:12,minWidth:160,maxWidth:200}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6}}>
        <span className="mono" style={{fontSize:12,color:C.accent,fontWeight:700}}>{sym}</span>
        {signal && <span className="mono" style={{fontSize:8,color:'#fff',padding:'2px 6px',borderRadius:3,background:col}}>{signal==='CALL'?'+ CALL':signal==='PUT'?'- PUT':'~ WAIT'}</span>}
      </div>
      <div className="mono" style={{fontSize:16,color:C.text,fontWeight:700}}>${price}</div>
      <div className="mono" style={{fontSize:10,color:change>=0?C.green:C.red}}>{change>=0?'+':''}{change}%</div>
      {score && <div style={{marginTop:6}}>
        <div className="mono" style={{fontSize:8,color:C.muted}}>AI SCORE</div>
        <div style={{background:C.border,borderRadius:3,height:6,marginTop:2}}>
          <div style={{background:score>=80?C.green:score>=60?C.yellow:C.red,borderRadius:3,height:6,width:score+'%'}}/>
        </div>
        <div className="mono" style={{fontSize:9,color:score>=80?C.green:C.muted,marginTop:2}}>{score}%</div>
      </div>}
      {rsi && <div className="mono" style={{fontSize:9,color:C.muted,marginTop:4}}>RSI {rsi}</div>}
    </div>
  );
}

function MockAlert({ sym, type, msg, score }) {
  const col = type === 'CALL' ? C.green : C.red;
  return (
    <div style={{background:C.dim,border:'1px solid '+col+'44',borderLeft:'3px solid '+col,borderRadius:6,padding:'5px 10px',display:'flex',alignItems:'center',gap:8,fontSize:10,maxWidth:350}}>
      <span className="mono" style={{color:col,fontWeight:700}}>{sym} {type}</span>
      <span style={{color:C.muted,flex:1}}>{msg}</span>
      <span className="mono" style={{color:C.accent,fontSize:9}}>{score}%</span>
      <span style={{color:C.muted,fontSize:12}}>x</span>
    </div>
  );
}

function MockTrafficLight({ color }) {
  return (
    <div style={{display:'inline-flex',gap:3,background:C.dim,borderRadius:10,padding:'3px 6px'}}>
      <div style={{width:8,height:8,borderRadius:'50%',background:color==='red'?C.red:C.border}}/>
      <div style={{width:8,height:8,borderRadius:'50%',background:color==='yellow'?C.yellow:C.border}}/>
      <div style={{width:8,height:8,borderRadius:'50%',background:color==='green'?C.green:C.border}}/>
    </div>
  );
}

function MockSetup({ type, entry, stop, t1 }) {
  const col = type === 'CALL' ? C.green : C.red;
  return (
    <div style={{background:col+'15',border:'1px solid '+col+'33',borderRadius:6,padding:'8px 12px',display:'flex',flexDirection:'column',gap:4}}>
      <div className="mono" style={{fontSize:11,color:col,fontWeight:700}}>{type === 'CALL' ? '+ TRADE CALL' : '- TRADE PUT'}</div>
      <div style={{display:'flex',gap:12}}>
        <span className="mono" style={{fontSize:9,color:C.muted}}>Entry <span style={{color:C.text}}>${entry}</span></span>
        <span className="mono" style={{fontSize:9,color:C.muted}}>T1 <span style={{color:C.green}}>${t1}</span></span>
        <span className="mono" style={{fontSize:9,color:C.muted}}>Stop <span style={{color:C.red}}>${stop}</span></span>
      </div>
    </div>
  );
}

function MockAutopilot({ trades, wins, losses, pnl }) {
  return (
    <div style={{background:C.dim,border:'1px solid '+C.red+'22',borderRadius:8,padding:12,borderLeft:'3px solid '+C.red}}>
      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
        <div style={{width:6,height:6,borderRadius:'50%',background:C.green}}/>
        <span className="mono" style={{fontSize:9,color:C.accent,fontWeight:700,letterSpacing:1}}>APEX AUTOPILOT</span>
        <span className="mono" style={{fontSize:7,color:C.purple,padding:'1px 4px',borderRadius:2,background:C.purple+'15'}}>PRO</span>
      </div>
      <div style={{display:'flex',gap:10}}>
        <span className="mono" style={{fontSize:9,color:C.muted}}>TODAY <span style={{color:C.text,fontWeight:700}}>{trades}</span> trades</span>
        <span className="mono" style={{fontSize:9,color:C.green}}>W <span style={{fontWeight:700}}>{wins}</span></span>
        <span className="mono" style={{fontSize:9,color:C.red}}>L <span style={{fontWeight:700}}>{losses}</span></span>
        <span className="mono" style={{fontSize:9,color:pnl>=0?C.green:C.red}}>P&L <span style={{fontWeight:700}}>{pnl>=0?'+':''}${pnl}</span></span>
      </div>
    </div>
  );
}

function MockModeBar() {
  return (
    <div style={{display:'flex',gap:6,background:C.dim,borderRadius:8,padding:6}}>
      <span className="mono" style={{fontSize:9,color:C.muted,padding:'4px 10px',borderRadius:16,border:'1px solid '+C.border}}>MANUAL</span>
      <span className="mono" style={{fontSize:9,color:C.muted,padding:'4px 10px',borderRadius:16,border:'1px solid '+C.border}}>SEMI-AUTO</span>
      <span className="mono" style={{fontSize:9,color:'#fff',padding:'4px 10px',borderRadius:16,border:'1px solid '+C.red,background:C.red+'22'}}>AUTOPILOT</span>
    </div>
  );
}

function MockTicker() {
  return (
    <div style={{background:'#0a0a0a',borderRadius:6,padding:'4px 12px',display:'flex',alignItems:'center',gap:8}}>
      <span className="mono" style={{fontSize:9,color:'#00e676',fontWeight:700}}>LIVE</span>
      <div style={{width:5,height:5,borderRadius:'50%',background:'#00e676'}}/>
      <span className="mono" style={{fontSize:9,color:'#00bcd4'}}>SPY</span>
      <span style={{fontSize:10,color:'#e0e0e0'}}>Fed Chair Signals Rate Decision...</span>
      <span className="mono" style={{fontSize:7,color:'#00bcd4'}}>READ</span>
    </div>
  );
}

function VisualRow({ children, label }) {
  return (
    <div style={{marginTop:12,marginBottom:12}}>
      {label && <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:1,marginBottom:6}}>{label}</div>}
      <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-start'}}>{children}</div>
    </div>
  );
}


function LearnTab() {
  const [section, setSection] = useState('basics');

  const sections = [
    { id: 'basics', label: 'OPTIONS 101' },
    { id: 'howto', label: 'HOW TO TRADE' },
    { id: 'strategy', label: 'APEX STRATEGY' },
    { id: 'risk', label: 'RISK MGMT' },
    { id: 'app', label: 'USING THE APP' },
    { id: 'glossary', label: 'GLOSSARY' },
  ];

  const Lesson = ({ title, children }) => (
    <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:16,marginBottom:12}}>
      <div style={{fontSize:15,fontWeight:700,color:C.accent,marginBottom:10}}>{title}</div>
      <div style={{fontSize:12,color:C.text,lineHeight:1.8}}>{children}</div>
    </div>
  );

  const Term = ({ word, def }) => (
    <div style={{display:'flex',gap:12,padding:'8px 0',borderBottom:'1px solid '+C.border+'20'}}>
      <span className="mono" style={{fontSize:11,color:C.accent,fontWeight:700,minWidth:120}}>{word}</span>
      <span style={{fontSize:11,color:C.muted,lineHeight:1.6}}>{def}</span>
    </div>
  );

  const Tip = ({ text }) => (
    <div style={{background:C.green+'10',border:'1px solid '+C.green+'33',borderRadius:6,padding:'8px 12px',marginTop:8,marginBottom:8}}>
      <span style={{fontSize:11,color:C.green}}>PRO TIP: </span>
      <span style={{fontSize:11,color:C.text}}>{text}</span>
    </div>
  );

  const Warn = ({ text }) => (
    <div style={{background:C.red+'10',border:'1px solid '+C.red+'33',borderRadius:6,padding:'8px 12px',marginTop:8,marginBottom:8}}>
      <span style={{fontSize:11,color:C.red}}>WARNING: </span>
      <span style={{fontSize:11,color:C.text}}>{text}</span>
    </div>
  );

  return (
    <div style={{padding:'16px 20px',maxWidth:900,margin:'0 auto'}}>
      <div style={{fontFamily:'Bebas Neue',fontSize:24,color:C.accent,letterSpacing:3,marginBottom:4}}>TRADING ACADEMY</div>
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:16}}>EVERYTHING YOU NEED TO BECOME A PROFITABLE TRADER</div>

      <div style={{display:'flex',gap:0,marginBottom:20,border:'1px solid '+C.border,borderRadius:6,overflow:'hidden',flexWrap:'wrap'}}>
        {sections.map(s => (
          <button key={s.id} onClick={()=>setSection(s.id)} style={{flex:1,minWidth:80,padding:'10px 8px',background:section===s.id?C.accent+'18':'transparent',border:'none',borderRight:'1px solid '+C.border,color:section===s.id?C.accent:C.muted,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1}}>{s.label}</button>
        ))}
      </div>

      {section === 'basics' && (<div>
        <Lesson title="What are stock options?">
          An option is a contract that gives you the right to buy or sell a stock at a specific price before a specific date. You pay a small amount (the premium) for this right.<br/><br/>
          There are only two types:<br/>
          <b style={{color:C.green}}>CALL</b> = You think the stock will go UP. You profit when price rises.<br/>
          <b style={{color:C.red}}>PUT</b> = You think the stock will go DOWN. You profit when price falls.<br/><br/>
          Each option contract controls 100 shares. So a $2.00 option costs $200 (2.00 x 100 shares).
          <Tip text="As a day trader, you buy and sell options within the same day. You never hold overnight. This limits your risk to what you paid for the option." />
        </Lesson>

        <Lesson title="Key terms you must know">
          <b style={{color:C.accent}}>Strike price</b> - The price at which the option can be exercised. For day trading, pick strikes close to the current stock price (ATM = At The Money).<br/><br/>
          <b style={{color:C.accent}}>Expiration</b> - When the option expires. Day traders use 0DTE (expires today) or 1-2 day expirations for maximum movement.<br/><br/>
          <b style={{color:C.accent}}>Premium</b> - What you pay for the option. This is your maximum possible loss.<br/><br/>
          <b style={{color:C.accent}}>ITM / OTM / ATM</b> - In The Money (profitable if exercised now), Out of The Money (not profitable yet), At The Money (strike = current price).
          <Tip text="For SPY and QQQ day trading, pick the ATM strike and 0DTE expiration. You get the most movement for your money." />
        </Lesson>

        <Lesson title="How options make money">
          You buy a SPY $590 Call for $2.00 ($200 total).<br/>
          SPY goes from $590 to $592. Your call is now worth $3.50.<br/>
          You sell for $3.50 ($350). Profit = $150 (75% return).<br/><br/>
          That same $2 move in the stock gave you 75% on your option.<br/>
          This is called <b style={{color:C.accent}}>leverage</b> - small stock moves = big option moves.<br/><br/>
          But it works both ways:<br/>
          If SPY drops $2, your $2.00 call might be worth $0.80. Loss = $120.
          <Warn text="Options can go to $0. Never risk more than you can afford to lose on a single trade. Start with 1 contract." />
        </Lesson>

        <Lesson title="Why day trade options?">
          1. <b style={{color:C.green}}>Limited risk</b> - You can only lose what you paid for the option<br/>
          2. <b style={{color:C.green}}>High leverage</b> - Small moves = big percentage gains<br/>
          3. <b style={{color:C.green}}>No overnight risk</b> - Close everything before 4pm<br/>
          4. <b style={{color:C.green}}>Both directions</b> - Make money whether market goes up OR down<br/>
          5. <b style={{color:C.green}}>Low capital needed</b> - Start with $500-2000
          <Warn text="You need a margin account and $25,000+ to avoid PDT (Pattern Day Trader) rule. Under $25K, you get only 3 day trades per 5 business days." />
        </Lesson>
      </div>)}

      {section === 'howto' && (<div>
        {/* Trading modes covered in Strategy section */}
        <Lesson title="Step 1: Before market opens (8:30-9:30 AM)">
          Open APEX and go to the BRIEFING tab. Check three things:<br/><br/>
          1. <b style={{color:C.accent}}>Gap scanner</b> - Are SPY/QQQ gapping up or down from yesterday? This tells you the market's overnight bias.<br/>
          2. <b style={{color:C.red}}>Economic events</b> - Red events (CPI, FOMC, Jobs Report) cause huge volatility. Trade smaller or sit out entirely.<br/>
          3. <b style={{color:C.yellow}}>Earnings</b> - If a stock you trade reports earnings today, avoid it. Too unpredictable.
          <Tip text="Write down your bias before the market opens: BULLISH, BEARISH, or NEUTRAL. Only take trades that match your bias." />
        </Lesson>

        <Lesson title="Step 2: Market opens (9:30-10:00 AM) - WATCH ONLY">
          Do NOT trade the first 30 minutes. Just watch.<br/><br/>
          Look for: Where does price settle relative to VWAP? The VWAP (yellow dashed line on the chart) is the most important level.<br/><br/>
          - Price above VWAP = institutions are buying = bullish bias<br/>
          - Price below VWAP = institutions are selling = bearish bias<br/>
          - Price chopping around VWAP = no direction = DO NOT TRADE
          <Warn text="The first 30 minutes is the most dangerous time. The biggest losses happen here. Professional traders wait." />
        </Lesson>

        <Lesson title="Step 3: Prime window (10:00 AM - 12:00 PM) - TRADE">
          This is YOUR window. APEX is tuned for this time period.<br/><br/>
          Look at each scanner card for these THREE conditions:<br/>
          1. Traffic light is GREEN<br/>
          2. AI Confidence is 80%+ (HIGH CONVICTION)<br/>
          3. Signal says CALL or PUT (not WAIT)<br/><br/>
          When all three align:<br/>
          - Read the trade setup (entry, stop, target prices)<br/>
          - Run through the Pre-Trade Checklist on the card<br/>
          - Enter the trade in Webull or click SEND ORDER for Tradier
          <Tip text="Maximum 2-3 trades per day. Quality over quantity. One good trade is better than five mediocre ones." />
        </Lesson>

        <Lesson title="Step 4: Managing the trade">
          Once you are in a trade, follow this exact plan:<br/><br/>
          1. Set your stop loss immediately - if price hits your stop, EXIT. No exceptions.<br/>
          2. When price hits Target 1 (T1): sell 50% of your contracts. This locks in profit.<br/>
          3. Move your stop loss to breakeven (your entry price).<br/>
          4. Let the remaining 50% ride toward Target 2 (T2).<br/>
          5. Trail your stop behind the EMA9 line on the chart.<br/><br/>
          This is called "scaling out" and it is the most important skill in day trading.
          <Tip text="After taking 50% at T1, your trade is now FREE. Even if the remaining contracts get stopped out at breakeven, you already locked in profit." />
        </Lesson>

        <Lesson title="Step 5: Lunch and afternoon (12:00 PM+)">
          12:00-1:00 PM: STOP TRADING. Close all positions. The lunch hour is choppy and unpredictable. APEX will show a red warning during this time.<br/><br/>
          NO TRADES after 12:30 PM: Only trade if you had ZERO losses in the morning. Use 1 contract max. The afternoon can trend well but is less predictable.<br/><br/>
          After 4:00 PM: Log your trades in the JOURNAL tab. Review what worked and what did not. This review is what separates profitable traders from losing ones.
        </Lesson>
      </div>)}

      {section === 'strategy' && (<div>
        <Lesson title="APEX Autopilot - hands-free AI trading">
          APEX Autopilot is the ultimate automation feature. Available on Pro and Annual plans, it lets the AI execute trades for you automatically.<br/><br/>
          <VisualRow label="AUTOPILOT PANEL"><MockAutopilot trades={3} wins={2} losses={1} pnl={185}/></VisualRow>
          <b style={{color:C.accent}}>How Autopilot works:</b><br/>
          1. Set your rules: minimum AI confidence (default 80%), max trades per day (default 5), contract size (default 1)<br/>
          2. Choose sandbox (paper) or live mode<br/>
          3. Click AUTOPILOT on the scanner mode bar<br/>
          4. APEX watches the market during prime hours (9:45am-12:30pm)<br/>
          5. When a signal meets ALL your criteria, it automatically finds the best option contract and places the order through Tradier or Alpaca<br/>
          6. You get a Telegram notification on your phone for every auto-trade<br/>
          7. Safety limits auto-stop after hitting your daily max or loss limit<br/><br/>
          <b style={{color:C.yellow}}>Safety features built in:</b><br/>
          - Sandbox mode first (paper trading, no real money)<br/>
          - Maximum daily trades limit<br/>
          - Daily loss limit<br/>
          - Only trades during prime window<br/>
          - 2-loss lockout (stops after 2 consecutive losses)<br/>
          - All trades logged with full transparency
          <Warn text="ALWAYS start Autopilot in SANDBOX mode. Run it for at least 2 weeks with a 55%+ win rate before switching to live. Never risk money you cannot afford to lose." />
        </Lesson>

        <Lesson title="The APEX strategy: VWAP reclaim + EMA confirmation">
          APEX uses a proven institutional strategy called VWAP Reclaim + EMA Confirmation. Here is how it works:<br/><br/>
          <b style={{color:C.yellow}}>VWAP Reclaim (for CALLS):</b><br/>
          1. Price drops below VWAP<br/>
          2. Price pushes back ABOVE VWAP<br/>
          3. Price holds above VWAP for 2-3 candles<br/>
          4. EMA9 crosses above EMA21 (confirmation)<br/>
          5. APEX fires a CALL signal with entry/stop/targets<br/><br/>
          <b style={{color:C.yellow}}>VWAP Rejection (for PUTS):</b><br/>
          1. Price rallies up to VWAP from below<br/>
          2. Price gets rejected and falls back below VWAP<br/>
          3. EMA9 crosses below EMA21 (confirmation)<br/>
          4. APEX fires a PUT signal
          <Tip text="The best setups happen when price reclaims VWAP on HIGH VOLUME (2x+ average). APEX shows volume ratio on each card." />
        </Lesson>

        <Lesson title="The 10-factor AI scoring system">
          APEX scores every potential trade using 10 factors:<br/><br/>
          1. <b style={{color:C.yellow}}>VWAP position</b> - Is price above or below the day's average?<br/>
          2. <b style={{color:C.accent}}>EMA alignment</b> - Are the trend lines bullish or bearish?<br/>
          3. <b style={{color:C.green}}>RSI</b> - Is momentum oversold (bounce) or overbought (drop)?<br/>
          4. <b style={{color:C.accent}}>MACD</b> - Is buying or selling pressure stronger?<br/>
          5. <b style={{color:C.purple}}>Bollinger Bands</b> - Is price at an extreme?<br/>
          6. <b style={{color:C.green}}>Volume</b> - Is there conviction behind the move?<br/>
          7. <b style={{color:C.accent}}>SPY/QQQ correlation</b> - Are the indexes aligned?<br/>
          8. <b style={{color:C.yellow}}>Multi-timeframe</b> - Does 1-min agree with 5-min trend?<br/>
          9. <b style={{color:C.accent}}>Time window</b> - Is it the prime trading window?<br/>
          10. <b style={{color:C.green}}>Momentum</b> - How strong is the move?<br/><br/>
          Each factor adds or subtracts points. 80%+ = HIGH CONVICTION (trade). Below 65% = skip.
        </Lesson>

        <Lesson title="What makes a good trade vs a bad trade">
          <b style={{color:C.green}}>GOOD trade characteristics:</b><br/>
          - AI confidence 80%+<br/>
          - SPY and QQQ both moving the same direction as your signal<br/>
          - Volume 1.5x+ above average<br/>
          - Inside the 9:45am-12:30pm window<br/>
          - Clear VWAP reclaim with 2-3 candle confirmation<br/>
          - Risk/reward at least 1.5:1<br/><br/>
          <b style={{color:C.red}}>BAD trade characteristics:</b><br/>
          - AI confidence below 70%<br/>
          - SPY going up but QQQ going down (divergence)<br/>
          - Low volume (below average)<br/>
          - During lunch (12-1pm) or first 60 seconds at open<br/>
          - Price extended far from VWAP<br/>
          - Taking a trade because you "feel" the direction
          <Warn text="If you find yourself saying 'I think it will go up' without checking the signals, STOP. Feelings are not a strategy. Let APEX tell you when conditions align." />
        </Lesson>
      </div>)}

      {section === 'risk' && (<div>
        <Lesson title="Rule 1: Never risk more than 2% of your account per trade">
          If your account is $5,000, your maximum loss per trade is $100.<br/>
          If your account is $10,000, max loss is $200.<br/>
          If your account is $25,000, max loss is $500.<br/><br/>
          This means: if a call option costs $3.00 ($300), and your max loss is $200, you can only buy 1 contract because the option COULD go to zero.<br/><br/>
          Start with 1 contract. Always. Until you prove consistent profitability.
        </Lesson>

        <Lesson title="Rule 2: Always use a stop loss">
          Before entering any trade, know your stop loss price. Write it down. Set an alert in your broker app.<br/><br/>
          When price hits your stop: EXIT IMMEDIATELY. No hoping, no wishing, no waiting "just a little more."<br/><br/>
          APEX shows your stop loss on every trade setup card and draws it on the expanded chart as a red dashed line.
          <Warn text="The #1 reason traders blow up accounts: they move their stop loss further away hoping the trade will come back. It rarely does. Your original stop was right." />
        </Lesson>

        <Lesson title="Rule 3: The 2-loss rule">
          After 2 losing trades in one day, STOP TRADING. Walk away. Come back tomorrow.<br/><br/>
          Why? After 2 losses, your emotions take over. You start revenge trading, taking bigger risks, and ignoring your rules. This is how accounts get destroyed in a single day.<br/><br/>
          APEX enforces this with the Daily Dashboard. When you hit 2 losses, it shows a red warning: STOP TRADING.
          <Tip text="The best traders in the world have losing days. The difference is they STOP when they are wrong and come back fresh the next day." />
        </Lesson>

        <Lesson title="Rule 4: Scale out - never go all in, never go all out">
          When your trade hits Target 1: sell 50%.<br/>
          Move stop to breakeven.<br/>
          Let the rest ride to Target 2.<br/><br/>
          This way: if T2 hits, you make big money. If it reverses after T1, you already locked in profit and the rest gets stopped at breakeven (no loss).<br/><br/>
          Never sell 100% at once. Never hold 100% hoping for more.
        </Lesson>

        <Lesson title="Rule 5: Track everything in the Journal">
          After every single trade, log it in the JOURNAL tab:<br/>
          - Symbol and type (CALL/PUT)<br/>
          - Entry and exit prices<br/>
          - Win or loss<br/>
          - What the setup was<br/>
          - What you learned<br/><br/>
          Review your journal every Friday. Look for patterns:<br/>
          - Which setups win most often?<br/>
          - What time do you trade best?<br/>
          - What causes your losses?<br/><br/>
          After 2 weeks, you will have data that tells you exactly how to improve.
          <Tip text="Professional traders treat trading like a business. The journal is your business ledger. No successful business runs without tracking results." />
        </Lesson>
      </div>)}

      {section === 'app' && (<div>
        <Lesson title="Scanner tab - Your command center">
          The Scanner is where you spend 90% of your time during market hours.<br/><br/>

          <VisualRow label="WHAT THE SCANNER CARDS LOOK LIKE">
            <MockCard sym="SPY" price="759.57" change={0.46} signal="CALL" score={92} rsi={55} />
            <MockCard sym="QQQ" price="746.16" change={-0.12} signal={null} score={45} rsi={42} />
            <MockCard sym="NVDA" price="222.82" change={-2.59} signal="PUT" score={88} rsi={30} />
          </VisualRow>

          <b style={{color:C.accent}}>Each card shows:</b><br/>
          - <b>Symbol + live price</b> - updates every second from Finnhub<br/>
          - <b>Signal badge</b> - <span style={{color:C.green}}>+ CALL</span> (buy calls), <span style={{color:C.red}}>- PUT</span> (buy puts), or <span style={{color:C.muted}}>~ WAIT</span> (no trade)<br/>
          - <b>AI Score bar</b> - the higher the better. Green = 80%+ (trade). Yellow = 60-79% (watch). Red = below 60% (skip).<br/>
          - <b>Mini chart</b> - click EXPAND for full interactive chart<br/>
          - <b>RSI, VWAP, EMA9, MACD</b> - key indicators at a glance<br/><br/>

          <b style={{color:C.accent}}>Traffic Light System:</b><br/>
          <div style={{display:'flex',gap:16,margin:'8px 0'}}>
            <div style={{display:'flex',alignItems:'center',gap:6}}><MockTrafficLight color="green"/><span style={{fontSize:11,color:C.green}}>GO - Take the trade</span></div>
            <div style={{display:'flex',alignItems:'center',gap:6}}><MockTrafficLight color="yellow"/><span style={{fontSize:11,color:C.yellow}}>CAUTION - Watch but wait</span></div>
            <div style={{display:'flex',alignItems:'center',gap:6}}><MockTrafficLight color="red"/><span style={{fontSize:11,color:C.red}}>STOP - Do NOT trade</span></div>
          </div><br/>

          <b style={{color:C.accent}}>When a signal fires, the card expands to show:</b><br/>
          <VisualRow>
            <MockSetup type="CALL" entry="759.57" stop="757.29" t1="762.99" />
          </VisualRow>
          Entry price, Target 1 (take 50% profit), and Stop loss (exit if wrong). Follow these prices exactly.
          <Tip text="Only trade when you see GREEN traffic light + 80%+ AI score + CALL or PUT signal. All three must align." />
        </Lesson>

        <Lesson title="Signal alerts - How they appear">
          When a signal fires, you see a compact notification in the bottom-right:<br/>
          <VisualRow label="ALERT EXAMPLES">
            <MockAlert sym="SPY" type="CALL" msg="VWAP reclaimed" score={92}/>
            <MockAlert sym="NVDA" type="PUT" msg="EMA death cross" score={87}/>
          </VisualRow>
          Click <b>x</b> to dismiss. Alerts auto-dismiss after 2 minutes. Max 2 visible at a time.
          <Tip text="The alert shows symbol, direction (CALL/PUT), reason, and AI confidence score. Higher score = stronger signal." />
        </Lesson>

        <Lesson title="News ticker - Stay informed">
          The live ticker at the top scrolls market news every 8 seconds:<br/>
          <VisualRow><MockTicker/></VisualRow>
          Click <b style={{color:'#00bcd4'}}>READ</b> to open the full article. The ticker shows the stock symbol in cyan and the headline in white. Always readable on every theme.
        </Lesson>

        <Lesson title="Expanded chart - How to read it">
          Click EXPAND on any card to open the full interactive chart.<br/><br/>
          <b style={{color:C.accent}}>The candles:</b> Green = price went up that minute. Red = price went down. The thick body shows open-to-close. The thin wicks show the high and low.<br/><br/>
          <b style={{color:C.yellow}}>VWAP</b> (yellow dashed line) - The most important line. Price above = bullish. Below = bearish.<br/><br/>
          <b style={{color:C.accent}}>EMA9</b> (cyan line) - Short-term trend. When it crosses above EMA21 = buy signal.<br/><br/>
          <b style={{color:C.purple}}>EMA21</b> (purple line) - Medium-term trend. Both rising = strong uptrend.<br/><br/>
          <b style={{color:C.green}}>Volume bars</b> (bottom) - Toggle with V key. High volume = strong move. Low volume = weak/unreliable.<br/><br/>
          <b style={{color:C.accent}}>Controls:</b> Scroll wheel = zoom. E = EMA. V = VWAP. B = Bollinger. H = draw horizontal line. T = draw trendline. C = clear drawings. ESC = close.
        </Lesson>

        <Lesson title="Briefing tab - Your morning preparation">
          Check this EVERY morning before the market opens:<br/><br/>
          - Gap scanner shows which stocks gapped up or down overnight<br/>
          - Economic calendar shows market-moving events (RED = high impact)<br/>
          - Earnings calendar shows which stocks report today<br/><br/>
          Use this to set your daily bias and know what to avoid.
        </Lesson>

        <Lesson title="Smart Money tab - Follow the big players">
          Three data sources that show you what institutions are doing:<br/><br/>
          <b style={{color:C.green}}>CONGRESS</b> - Politicians must disclose stock trades. When a senator buys $1M of NVDA, that is a signal.<br/>
          <b style={{color:C.accent}}>WHALE FLOW</b> - Large options orders ($100K+) from institutional traders. SWEEPS are the strongest signal.<br/>
          <b style={{color:C.yellow}}>INSIDERS</b> - CEOs buying their own stock is one of the most reliable bullish signals in finance.<br/><br/>
          <b>How to use:</b> Check Smart Money before trading. If Congress is buying NVDA AND your scanner shows a CALL signal on NVDA, that is extra confirmation.
          <Tip text="Insider BUYING is much more significant than insider selling. Executives sell for many reasons (taxes, diversification) but they only BUY when they believe the stock will go up." />
        </Lesson>

        <Lesson title="Journal tab - Your path to improvement">
          Click + LOG TRADE after each trade. Enter the symbol, type, entry/exit prices, and result.<br/><br/>
          The Journal tracks your: total trades, win rate, total P&L, wins, and losses.<br/><br/>
          Review weekly. Your win rate should be above 50% with proper risk management. If it is below 50%, study your losing trades and figure out why.
        </Lesson>

        <Lesson title="Settings tab - Customize your experience">
          <b style={{color:C.accent}}>Theme Selector</b> - 4 professional dark themes:<br/>
          <VisualRow>
            <span className="mono" style={{padding:'4px 12px',borderRadius:4,border:'1px solid '+C.border,background:'#111111',color:'#f5f5f5',fontSize:9}}>DARK</span>
            <span className="mono" style={{padding:'4px 12px',borderRadius:4,border:'1px solid #1a2e4e',background:'#0a1628',color:'#0078d7',fontSize:9}}>MIDNIGHT</span>
            <span className="mono" style={{padding:'4px 12px',borderRadius:4,border:'1px solid #3f3f46',background:'#18181b',color:'#22d3ee',fontSize:9}}>SLATE</span>
            <span className="mono" style={{padding:'4px 12px',borderRadius:4,border:'1px solid #3a3a3c',background:'#1c1c1e',color:'#0a84ff',fontSize:9}}>CHARCOAL</span>
          </VisualRow><br/>
          <b style={{color:C.accent}}>Watchlist</b> - Add/remove stock symbols. Search 65+ stocks or type any ticker manually.<br/><br/>
          <b style={{color:C.accent}}>Trading Modes:</b><br/>
          <VisualRow><MockModeBar/></VisualRow>
          MANUAL = you trade in your broker. SEMI-AUTO = one-click orders. AUTOPILOT = AI trades for you (Pro only).<br/><br/>
          <b style={{color:C.accent}}>Sound Alerts</b> - Distinct sounds: rising chime for CALL, descending pulse for PUT.<br/><br/>
          <b style={{color:C.accent}}>Auto-Trading</b> - Configure Autopilot: min confidence, max trades, sandbox/live mode.
        </Lesson>
      </div>)}

      {section === 'glossary' && (<div>
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:16}}>
          <Term word="ATM" def="At The Money - option strike price equal to current stock price" />
          <Term word="Ask" def="The price sellers want. You pay the ask when buying options." />
          <Term word="Bid" def="The price buyers offer. You get the bid when selling options." />
          <Term word="Bollinger Bands" def="Volatility bands above and below price. Price touching lower band = oversold. Upper band = overbought." />
          <Term word="Call option" def="Gives you the right to BUY stock. You buy calls when you think price will go UP." />
          <Term word="Candle" def="A chart element showing 1 minute of price action: open, high, low, close." />
          <Term word="Day trade" def="Buying and selling the same security in the same trading day." />
          <Term word="EMA" def="Exponential Moving Average. A line showing average price with more weight on recent prices." />
          <Term word="Entry" def="The price at which you open a trade." />
          <Term word="Expiration" def="The date an option contract expires and becomes worthless." />
          <Term word="MACD" def="Moving Average Convergence Divergence. Positive = buyers winning. Negative = sellers winning." />
          <Term word="0DTE" def="Zero Days To Expiration. Options that expire today. Maximum leverage, maximum risk." />
          <Term word="OTM" def="Out of The Money. A call is OTM when strike is above stock price." />
          <Term word="PDT Rule" def="Pattern Day Trader rule. Under $25K equity, limited to 3 day trades per 5 business days." />
          <Term word="Premium" def="The price you pay for an option contract. Your maximum possible loss." />
          <Term word="Put option" def="Gives you the right to SELL stock. You buy puts when you think price will go DOWN." />
          <Term word="R:R" def="Risk to Reward ratio. 2:1 means you risk $1 to make $2. Never take less than 1.5:1." />
          <Term word="RSI" def="Relative Strength Index (0-100). Below 30 = oversold. Above 70 = overbought." />
          <Term word="Scale out" def="Selling part of your position at Target 1 to lock in profits." />
          <Term word="Stop loss" def="A predetermined price where you exit to limit losses. Non-negotiable." />
          <Term word="Strike" def="The price at which an option can be exercised." />
          <Term word="Target 1 (T1)" def="First profit target. Sell 50% here and move stop to breakeven." />
          <Term word="Target 2 (T2)" def="Second profit target. Let remaining 50% ride here with trailing stop." />
          <Term word="VWAP" def="Volume Weighted Average Price. The most important line. Institutional traders use this as fair value." />
          <Term word="Volume" def="Number of shares/contracts traded. High volume = conviction. Low volume = unreliable." />
        </div>
      </div>)}

    </div>
  );
}


function GuideTab() {
  const [tradeMode, setTradeMode] = useState('manual');
  const [showModeHelp, setShowModeHelp] = useState(false);

  const modes = {
    manual: {
      label: 'Best for beginners. You see the signal and decide whether to trade.',
      color: C.green,
      steps: [
        { title: 'Watch the scanner cards', desc: 'Look for a card where the traffic light is GREEN and AI confidence says "HIGH CONVICTION" (80%+).', where: 'Scanner tab' },
        { title: 'Read the trade setup', desc: 'Scroll down on the card. You will see the colored banner: "BUY CALL - SPY" or "BUY PUT - QQQ" with exact entry price, stop loss, and targets.', where: 'Scanner tab " scroll down' },
        { title: 'Open Webull and enter the trade', desc: 'Follow the step-by-step instructions shown in the setup. Enter the symbol, strike, expiration, and order type in Webull.', where: 'Webull app' },
        { title: 'Manage: take 50% at T1, trail the rest', desc: 'When price hits Target 1, sell half your contracts. Move your stop to breakeven. Let the rest ride to Target 2.', where: 'Webull app' },
      ]
    },
    semi: {
      label: 'APEX shows the signal, you click ONE button to send the order to Tradier.',
      color: C.accent,
      steps: [
        { title: 'Signal fires on a card', desc: 'Same as manual " wait for green light + high conviction. The TRADE NOW button appears on the card.', where: 'Scanner tab' },
        { title: 'Click "TRADE NOW" or "Send to Tradier"', desc: 'The Tradier order panel opens with the correct strike, expiration, and limit price pre-filled.', where: 'Scanner tab - click the button' },
        { title: 'Review and confirm', desc: 'Check the order details. Click "Place order" to execute. Goes to Tradier sandbox or live.', where: 'Order panel' },
      ]
    },
    auto: {
      label: 'Trades execute automatically when conditions are met. Start in SANDBOX mode.',
      color: C.yellow,
      steps: [
        { title: 'Go to Settings > Auto-Trading Engine', desc: 'Set mode (sandbox/live), minimum confidence (default 80%), max daily trades (default 5), contract size.', where: 'Settings tab' },
        { title: 'Click START', desc: 'Auto-trading activates. A green border and warning shows your current mode.', where: 'Settings tab' },
        { title: 'APEX trades for you', desc: 'When a signal fires above your confidence threshold, APEX finds the ATM option and places a limit order through Tradier. You get a Telegram alert.', where: 'Automatic' },
        { title: 'Click STOP when done', desc: 'Or let the max daily trades limit stop it automatically. Review trades in the auto-trade history.', where: 'Settings tab' },
      ]
    },
    bot: {
      label: 'Standalone bot runs in a separate terminal. APEX must be running.',
      color: C.purple,
      steps: [
        { title: 'Make sure APEX is running', desc: 'Double-click the APEX Pro shortcut. The bot connects to APEX.', where: 'Desktop shortcut' },
        { title: 'Open a separate PowerShell', desc: 'Keep APEX open in the browser. Open a new terminal window.', where: 'Start menu > PowerShell' },
        { title: 'Run: node auto-bot.js', desc: 'Type "cd C:\\Users\\dwayn\\Desktop\\apex-final" then "node auto-bot.js". Bot checks signals every 5 seconds.', where: 'Terminal' },
        { title: 'Bot trades during 9:45am-12:30pm', desc: 'Executes through Tradier when conditions are met. Edit auto-bot.js to change symbols or settings. Ctrl+C to stop.', where: 'Terminal' },
      ]
    },
  };

  const tabs = [
    { name: 'SCANNER', icon: 'S', desc: 'Live signals, charts, trade setups. The signal banner at the top shows active trades. Scroll down on each card for the full setup with entry, stop, and targets.' },
    { name: 'BRIEFING', icon: 'B', desc: 'Check every morning before open. Shows gap scanner (up/down from yesterday), economic events (RED = high impact), and earnings. Your daily game plan.' },
    { name: 'C FLOW', icon: 'F', desc: 'See where big money moves. GOLDEN SWEEPS = largest institutional bets. ABOVE ASK = urgent buying. Dark pool prints show hidden block trades. Sector flow shows bullish vs bearish by industry.' },
    { name: 'BACKTEST', icon: 'T', desc: 'Test your strategy before risking money. Pick a symbol, set your risk/reward and stop loss, click Run. Look for 50%+ win rate and 1.5+ profit factor.' },
    { name: 'JOURNAL', icon: 'J', desc: 'Log every trade. Click + LOG TRADE after each trade. Track your win rate, total P&L, and patterns. Review weekly to learn from losses.' },
    { name: 'ALERTS', icon: 'A', desc: 'Every signal APEX has fired today. Click any alert to see its full setup. Alerts also go to Telegram if configured.' },
    { name: 'SETTINGS', icon: 'G', desc: 'Auto-trading engine, Telegram push alerts, watchlist presets, symbol search, themes, and TradingView webhook setup.' },
    { name: 'ACCURACY', icon: 'R', desc: 'Track AI signal performance over time. See today, this week, and this month win rates. Shows all active signals and history.' },
  ];

  const rules = [
    'Only trade 10:00 AM - 12:00 PM until consistently profitable',
    'Only enter on GREEN traffic light + 80%+ AI confidence',
    'Maximum 1-2 contracts until win rate exceeds 60%',
    'Always take 50% profit at Target 1 " no exceptions',
    'Stop loss is non-negotiable " exit at your level',
    'Maximum 2 losses per day " hit 2 losses, stop trading',
    'No trades during lunch (12-1 PM)',
    'Log every trade in the Journal tab',
    'Paper trade in Sandbox for 2+ weeks before real money',
    'Review your Journal weekly " learn from losses',
  ];

  const m = modes[tradeMode];

  return (
    <div style={{padding:'16px 20px',maxWidth:900,margin:'0 auto',animation:'fadeIn .3s ease'}}>
      
      {/* How APEX works */}
      <div style={{fontFamily:'Bebas Neue',fontSize:24,color:C.accent,letterSpacing:3,marginBottom:4}}>HOW APEX TRADING WORKS</div>
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:16}}>FROM SIGNAL TO PROFIT IN 4 STEPS</div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:24}}>
        {[
          ['1','Scanner watches','AI analyzes VWAP, EMA, RSI, MACD, volume every 500ms',C.accent],
          ['2','Signal fires','Traffic light turns GREEN when 10 factors align at 80%+',C.green],
          ['3','Setup shown','Entry, stop loss, Target 1, Target 2 " exact prices',C.yellow],
          ['4','You trade','Manual, semi-auto, or full-auto " your choice',C.purple],
        ].map(([num,title,desc,col]) => (
          <div key={num} className="card" style={{textAlign:'center',borderTop:`3px solid ${col}`}}>
            <div style={{width:36,height:36,borderRadius:'50%',background:`${col}18`,border:`2px solid ${col}`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 10px'}}>
              <span className="mono" style={{fontSize:14,color:col,fontWeight:700}}>{num}</span>
            </div>
            <div style={{fontSize:13,fontWeight:700,color:C.text,marginBottom:4}}>{title}</div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>{desc}</div>
          </div>
        ))}
      </div>

      {/* Trading mode selector */}
      <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.text,letterSpacing:3,marginBottom:12}}>CHOOSE YOUR TRADING MODE</div>
      
      <div style={{display:'flex',gap:0,marginBottom:16,border:`1px solid ${C.border}`,borderRadius:6,overflow:'hidden'}}>
        {[['manual','Manual'],['semi','Semi-auto'],['auto','Autopilot'],['bot','Bot']].map(([id,label]) => (
          <button key={id} onClick={()=>setTradeMode(id)} style={{flex:1,padding:'10px 12px',background:tradeMode===id?`${C.accent}18`:'transparent',border:'none',borderRight:`1px solid ${C.border}`,color:tradeMode===id?C.accent:C.muted,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1}}>{label}</button>
        ))}
      </div>

      <div style={{background:`${m.color}08`,border:`1px solid ${m.color}33`,borderRadius:6,padding:'8px 14px',marginBottom:14}}>
        <div style={{fontSize:12,color:m.color}}>{m.label}</div>
      </div>

      {m.steps.map((step,i) => (
        <div key={i} style={{display:'flex',gap:14,marginBottom:12,padding:'14px 16px',background:C.panel,border:`1px solid ${C.border}`,borderRadius:8}}>
          <div style={{width:30,height:30,borderRadius:'50%',background:C.dim,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <span className="mono" style={{fontSize:13,fontWeight:700,color:C.text}}>{i+1}</span>
          </div>
          <div style={{flex:1}}>
            <div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:3}}>{step.title}</div>
            <div style={{fontSize:12,color:C.muted,lineHeight:1.7}}>{step.desc}</div>
            <span className="mono" style={{display:'inline-block',fontSize:9,padding:'2px 8px',borderRadius:4,background:`${C.accent}15`,color:C.accent,marginTop:6}}>{step.where}</span>
          </div>
        </div>
      ))}

      {/* Where to find everything */}
      <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.text,letterSpacing:3,marginTop:24,marginBottom:12}}>WHERE TO FIND EVERYTHING</div>
      
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10,marginBottom:24}}>
        {tabs.map(t => (
          <div key={t.name} className="card" style={{cursor:'default'}}>
            <div style={{width:32,height:32,borderRadius:8,background:C.accent+'15',border:'1px solid '+C.accent+'44',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:6}}><span className='mono' style={{fontSize:13,fontWeight:700,color:C.accent}}>{t.icon}</span></div>
            <div style={{fontSize:13,fontWeight:700,color:C.accent,marginBottom:4}}>{t.name}</div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.6}}>{t.desc}</div>
          </div>
        ))}
      </div>

      {/* Golden rules */}
      <div className="card" style={{border:`1px solid ${C.red}33`}}>
        <div className="mono" style={{fontSize:9,color:C.red,letterSpacing:2,fontWeight:700,marginBottom:14}}>GOLDEN RULES " NEVER BREAK THESE</div>
        {rules.map((rule,i) => (
          <div key={i} style={{display:'flex',gap:10,marginBottom:10,alignItems:'flex-start'}}>
            <span className="mono" style={{fontSize:11,color:C.red,flexShrink:0,fontWeight:700}}>{i+1}.</span>
            <span style={{fontSize:12,color:C.text,lineHeight:1.6}}>{rule}</span>
          </div>
        ))}
      </div>

      {/* Daily schedule */}
      <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.text,letterSpacing:3,marginTop:24,marginBottom:12}}>DAILY SCHEDULE</div>
      <div style={{display:'flex',flexDirection:'column',gap:6}}>
        {[
          ['8:30-9:30','PRE-MARKET','Check BRIEFING tab. Note gaps and events. Set your bias.',C.accent],
          ['9:30-10:00','MARKET OPEN','Watch only. Do NOT trade. See which direction settles.',C.yellow],
          ['9:45-12:30','PRIME WINDOW','Your best window. Trade GREEN signals with 80%+ confidence.',C.green],
          ['12:00-1:00','LUNCH BREAK','STOP. Close positions. No new trades. Eat lunch.',C.red],
          ['12:30-3:30','WIND DOWN','No new entries. Let bot manage open positions to exit.',,C.muted],
          ['4:00+','AFTER CLOSE','Log trades in JOURNAL. Review stats. Learn.',C.accent],
        ].map(([time,label,action,col]) => (
          <div key={time} style={{display:'flex',gap:12,padding:'8px 14px',background:C.panel,border:`1px solid ${C.border}`,borderRadius:6,borderLeft:`3px solid ${col}`}}>
            <span className="mono" style={{fontSize:10,color:col,minWidth:70,fontWeight:700}}>{time}</span>
            <span className="mono" style={{fontSize:10,color:C.text,minWidth:85,fontWeight:700}}>{label}</span>
            <span style={{fontSize:11,color:C.muted}}>{action}</span>
          </div>
        ))}
      </div>

      {/* Chart & Indicator Glossary */}
      <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.text,letterSpacing:3,marginTop:24,marginBottom:12}}>UNDERSTANDING YOUR CHARTS</div>
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:14}}>WHAT EVERYTHING ON THE SCANNER CARD MEANS</div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:10,marginBottom:24}}>
        {[
          ['Candlestick Chart','The mini chart on each card. Green candles = price went UP during that minute. Red candles = price went DOWN. The body (thick part) shows open-to-close range. The wicks (thin lines) show the high and low. Click EXPAND to open the interactive chart.','*',C.accent],
          ['VWAP (yellow dashed line)','Volume Weighted Average Price. This is the MOST important line. When price crosses ABOVE VWAP = bullish (buy calls). When price crosses BELOW VWAP = bearish (buy puts). VWAP reclaims are your bread-and-butter setup.','*',C.yellow],
          ['EMA 9 (cyan line)','9-period Exponential Moving Average. This tracks short-term momentum. When EMA9 is above EMA21 = uptrend. When it crosses above = bullish signal (shown as green arrow on expanded chart).','*',C.accent],
          ['EMA 21 (purple line)','21-period Exponential Moving Average. This tracks the medium-term trend. The relationship between EMA9 and EMA21 tells you the trend direction. Both rising = strong uptrend.','*',C.purple],
          ['RSI (bar below chart)','Relative Strength Index (0-100). Below 30 = oversold (bounce likely, call setup). Above 70 = overbought (pullback likely, put setup). Between 45-65 = momentum zone (trending, good for entries).','*',C.green],
          ['MACD','Moving Average Convergence Divergence. Positive = buyers in control. Negative = sellers in control. The bigger the number, the stronger the momentum.','*',C.accent],
          ['AI Confidence Bar','Scores every setup 0-95% using all 10 factors: VWAP, EMA, RSI, MACD, Bollinger, volume, SPY/QQQ correlation, multi-timeframe, time window, momentum. HIGH CONVICTION (80%+) = trade. WEAK = skip.','*',C.green],
          ['Traffic Light','GREEN = all conditions met, trade setup ready. YELLOW = some conditions met, wait for confirmation. RED = conditions not met, do NOT trade. This is your GO/NO-GO signal.','*',C.yellow],
          ['Volume','Shown as bars at the bottom of expanded chart. High volume (2x+ average) = strong conviction behind the move. Low volume = weak move, likely to reverse. Volume confirms the signal.','*',C.muted],
          ['Bollinger Bands (purple dashed)','Toggle on in expanded chart. Upper band = overbought zone. Lower band = oversold zone. Price touching lower band = potential bounce (call). Price touching upper band = potential pullback (put).','*',C.purple],
        ].map(([title,desc,icon,col]) => (
          <div key={title} className="card" style={{borderLeft:`3px solid ${col}`}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <span className='mono' style={{fontSize:12,color:col,fontWeight:700}}>{icon}</span>
              <span style={{fontSize:13,fontWeight:700,color:col}}>{title}</span>
            </div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.7}}>{desc}</div>
          </div>
        ))}
      </div>

      {/* Interactive chart tips */}
      <div className="card" style={{marginBottom:24}}>
        <div className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2,marginBottom:12}}>EXPANDED CHART CONTROLS</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:10}}>Click EXPAND on any card to open the interactive chart. Here is what you can do:</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:8}}>
          {[
            ['Hover','Move mouse over candles to see OHLCV data (Open, High, Low, Close, Volume) for each minute'],
            ['Scroll zoom','Mouse wheel zooms in/out. See more or fewer candles'],
            ['Keyboard E','Toggle EMA lines on/off'],
            ['Keyboard V','Toggle VWAP line on/off'],
            ['Keyboard B','Toggle Bollinger Bands on/off'],
            ['Keyboard H','Draw a horizontal price level line. Click chart to place'],
            ['Keyboard T','Draw a trendline. Click start point, then click end point'],
            ['Keyboard C','Clear all drawings'],
            ['Keyboard +/-','Zoom in/out'],
            ['Keyboard ESC','Close the chart'],
            ['Zoom buttons','Click 30/60/90/120/180 to set candle count'],
            ['Setup lines','Entry, stop, T1, T2 levels shown as colored dashed lines'],
          ].map(([key,desc]) => (
            <div key={key} style={{display:'flex',gap:8,alignItems:'flex-start'}}>
              <span className="mono" style={{fontSize:10,color:C.accent,fontWeight:700,minWidth:70,flexShrink:0,padding:'2px 6px',borderRadius:3,background:`${C.accent}15`}}>{key}</span>
              <span style={{fontSize:11,color:C.muted,lineHeight:1.5}}>{desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Trade setup explained */}
      <div className="card" style={{border:`1px solid ${C.green}33`,marginBottom:24}}>
        <div className="mono" style={{fontSize:9,color:C.green,letterSpacing:2,marginBottom:12}}>UNDERSTANDING THE TRADE SETUP</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14,lineHeight:1.7}}>When a signal fires, the colored banner at the bottom of each card shows your complete trade plan:</div>
        {[
          ['ENTRY PRICE','The exact price where you should enter the trade. For CALLS: enter when stock is at or above this price. For PUTS: enter when stock is at or below this price.',C.accent],
          ['STOP LOSS','Your maximum loss level. If price hits this, EXIT IMMEDIATELY. No exceptions. This protects your capital. Set an alert in Webull at this price.',C.red],
          ['TARGET 1 (T1)','Your first profit target. When price hits T1, sell 50% of your contracts. This locks in profit and reduces risk. Move your stop to breakeven after taking T1.',C.green],
          ['TARGET 2 (T2)','Your second profit target. Let the remaining 50% ride to T2. This is where the big gains come from. Trail your stop behind EMA9.',C.green],
          ['R:R RATIO','Risk-to-Reward ratio. 1.5:1 means you risk $1 to make $1.50. 3:1 means risk $1 to make $3. Never take a trade with less than 1.5:1 R:R.',C.yellow],
          ['CONFIDENCE %','How many of the 10 scoring factors align. 80%+ = HIGH CONVICTION (best trades). 65-79% = MODERATE (acceptable). Below 65% = skip the trade.',C.accent],
        ].map(([term,desc,col]) => (
          <div key={term} style={{display:'flex',gap:12,marginBottom:12,alignItems:'flex-start'}}>
            <span className="mono" style={{fontSize:10,color:col,fontWeight:700,minWidth:90,flexShrink:0}}>{term}</span>
            <span style={{fontSize:11,color:C.muted,lineHeight:1.7}}>{desc}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

//  APEX Main App 



// "" SIGNAL ACCURACY TRACKER ""
function SignalAccuracyTab({ ind, symbols }) {
  const [journalData, setJournalData] = useState({ today:[], week:[], month:[], all:[] });
  useEffect(() => {
    fetch('/api/markov/journal').then(r=>r.json()).then(d=>{
      const trades = d.trades || d.data || [];
      const now = Date.now();
      const todayStr = new Date().toISOString().slice(0,10);
      const today = trades.filter(t => (t.date||'').startsWith(todayStr) || (t.created_at||'').startsWith(todayStr));
      const week = trades.filter(t => { try { return now - new Date(t.entry_time||t.entryTime||t.created_at).getTime() < 7*24*60*60*1000; } catch{return false;} });
      const month = trades.filter(t => { try { return now - new Date(t.entry_time||t.entryTime||t.created_at).getTime() < 30*24*60*60*1000; } catch{return false;} });
      setJournalData({ today, week, month, all: trades });
    }).catch(()=>{});
  }, []);
  const calcStats = (arr) => {
    if (!arr.length) return { total: 0, wins: 0, rate: 0, avgConf: 0 };
    const wins = arr.filter(a => a.result === 'WIN' || a.result === 'win').length;
    return { total: arr.length, wins, rate: Math.round(wins/arr.length*100), avgConf: Math.round(arr.reduce((s,a)=>s+(a.score||a.confidence||0),0)/arr.length) };
  };
  const todayS = calcStats(journalData.today);
  const weekS = calcStats(journalData.week);
  const monthS = calcStats(journalData.month);
  const journalStats = { trades: journalData.today.length, wins: todayS.wins, losses: todayS.total-todayS.wins, pnl: Math.round(journalData.today.reduce((a,t)=>a+(t.pnl||0),0)), winRate: todayS.rate };
  
  // Live accuracy from current signals
  const liveSignals = symbols.map(sym => {
    const d = ind[sym];
    if (!d?.signal || d.signal.type === 'NEUTRAL') return null;
    return { sym, type: d.signal.type, conf: d.ml?.score || 0, price: d.price };
  }).filter(Boolean);

  return (
    <div style={{padding:'16px 20px',maxWidth:900,margin:'0 auto'}}>
      <div style={{fontFamily:'Bebas Neue',fontSize:24,color:C.accent,letterSpacing:3,marginBottom:4}}>SIGNAL PERFORMANCE</div>
          {journalStats.trades>0 && <div className="mono" style={{fontSize:11,color:journalStats.pnl>=0?'#22c55e':'#ef4444',marginBottom:8}}>
            Today P&L: {journalStats.pnl>=0?'+':''}${journalStats.pnl} | {journalStats.winRate}% WR | {journalStats.wins}W {journalStats.losses}L
          </div>}
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:20}}>TRACK AI ACCURACY OVER TIME</div>

      {/* Stats cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:20}}>
        {[['TODAY',todayS,'24h'],['THIS WEEK',weekS,'7d'],['THIS MONTH',monthS,'30d']].map(([label,s,per])=>(
          <div key={label} className="card">
            <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:12}}>{label}</div>
            <div style={{display:'flex',alignItems:'baseline',gap:6,marginBottom:8}}>
              <span style={{fontSize:32,fontWeight:800,color:s.rate>=60?C.green:s.rate>=50?C.yellow:s.total?C.red:C.muted}}>{s.total ? s.rate+'%' : '--'}</span>
              <span style={{fontSize:12,color:C.muted}}>win rate</span>
            </div>
            <div style={{display:'flex',gap:12}}>
              <div><span className="mono" style={{fontSize:14,color:C.text,fontWeight:700}}>{s.wins}</span><span style={{fontSize:10,color:C.muted}}> wins</span></div>
              <div><span className="mono" style={{fontSize:14,color:C.text,fontWeight:700}}>{s.total-s.wins}</span><span style={{fontSize:10,color:C.muted}}> losses</span></div>
              <div><span className="mono" style={{fontSize:14,color:C.text,fontWeight:700}}>{s.total}</span><span style={{fontSize:10,color:C.muted}}> total</span></div>
            </div>
            {s.total > 0 && <div style={{marginTop:8,height:4,borderRadius:2,background:`${C.muted}33`,overflow:'hidden'}}><div style={{height:'100%',width:s.rate+'%',background:s.rate>=60?C.green:s.rate>=50?C.yellow:C.red,borderRadius:2,transition:'width .5s'}}/></div>}
          </div>
        ))}
      </div>

      {/* Live signals */}
      <div className="card" style={{marginBottom:16}}>
        <div className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2,marginBottom:12}}>ACTIVE SIGNALS RIGHT NOW</div>
        {liveSignals.length === 0 ? (
          <div style={{fontSize:13,color:C.muted,padding:20,textAlign:'center'}}>No active signals. Waiting for conditions to align...</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {liveSignals.map((s,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'8px 12px',borderRadius:6,background:C.dim,border:`1px solid ${C.border}`}}>
                <span className="mono" style={{fontSize:13,fontWeight:700,color:C.accent,minWidth:50}}>{s.sym}</span>
                <span className="mono" style={{fontSize:11,fontWeight:700,color:s.type==='CALL'?C.green:C.red,padding:'2px 8px',borderRadius:4,background:s.type==='CALL'?`${C.green}18`:`${C.red}18`}}>{s.type}</span>
                <span className="mono" style={{fontSize:11,color:C.text}}>${s.price?.toFixed(2)}</span>
                <span style={{flex:1}}/>
                <span className="mono" style={{fontSize:11,fontWeight:700,color:s.conf>=80?C.green:s.conf>=60?C.yellow:C.muted}}>{s.conf}% AI</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent signal history */}
      <div className="card">
        <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:12}}>RECENT SIGNALS ({history.length})</div>
        {history.length === 0 ? (
          <div style={{fontSize:13,color:C.muted,padding:20,textAlign:'center'}}>Signal history builds automatically as APEX fires signals throughout the day. Check back after market hours.</div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:4,maxHeight:300,overflowY:'auto'}}>
            {history.slice(-20).reverse().map((h,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 10px',borderRadius:4,background:i%2===0?C.dim:'transparent',fontSize:11}}>
                <span className="mono" style={{color:C.muted,minWidth:65}}>{new Date(h.time).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}</span>
                <span className="mono" style={{color:C.accent,minWidth:40,fontWeight:700}}>{h.symbol}</span>
                <span className="mono" style={{color:h.type==='CALL'?C.green:C.red,minWidth:35}}>{h.type}</span>
                <span className="mono" style={{color:C.text,minWidth:55}}>${h.entry}</span>
                <span style={{flex:1}}/>
                <span className="mono" style={{fontWeight:700,color:h.result==='win'?C.green:h.result==='loss'?C.red:C.muted}}>{h.result ? h.result.toUpperCase() : 'PENDING'}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}




// "" OPEN POSITIONS TRACKER ""
function PositionsTracker() {
  const [positions, setPositions] = useState([]);
  const [liveBalance, setLiveBalance] = useState(null);
  React.useEffect(() => {
    const fetchBalance = () => {
      fetch('/api/balance').then(r=>r.json()).then(d=>{
        if(d.ok) setLiveBalance(d);
      }).catch(()=>{});
    };
    fetchBalance();
    const t = setInterval(fetchBalance, 30000);
    return () => clearInterval(t);
  }, []);
  const [loading, setLoading] = useState(false);

  const refresh = () => {
    setLoading(true);
    fetch(`${API}/api/account/positions`).then(r=>r.json()).then(d=>{
      const pos = d?.positions?.position;
      const arr = pos ? (Array.isArray(pos) ? pos : [pos]) : [];
      setPositions(arr);
      setLoading(false);
    }).catch(()=>setLoading(false));
  };

  useEffect(() => { refresh(); const t = setInterval(refresh, 30000); return () => clearInterval(t); }, []);

  if (positions.length === 0) return null;

  const totalPnl = positions.reduce((a, p) => a + ((p.last_price - p.cost_basis) * p.quantity), 0);

  return (
    <div style={{background:C.panel,border:`1px solid ${totalPnl>=0?C.green:C.red}33`,borderRadius:8,padding:'12px 16px',marginBottom:14}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2}}>OPEN POSITIONS ({positions.length})</div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span className="mono" style={{fontSize:14,fontWeight:700,color:totalPnl>=0?C.green:C.red}}>{totalPnl>=0?'+':''}${totalPnl.toFixed(0)}</span>
          <button onClick={refresh} className="mono" style={{padding:'2px 8px',borderRadius:4,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,fontSize:9,cursor:'pointer'}}>{loading?'...':'REFRESH'}</button>
        </div>
      </div>
      {positions.map((p, i) => {
        const pnl = (p.last_price - p.cost_basis) * p.quantity;
        const pnlPct = p.cost_basis > 0 ? ((p.last_price - p.cost_basis) / p.cost_basis * 100) : 0;
        return (
          <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:i<positions.length-1?`1px solid ${C.border}20`:'none',fontSize:11}}>
            <span className="mono" style={{color:C.accent,fontWeight:700,minWidth:45}}>{p.symbol?.split(' ')[0]}</span>
            <span className="mono" style={{color:C.muted,minWidth:30}}>{p.quantity}x</span>
            <span className="mono" style={{color:C.text,minWidth:55}}>@${p.cost_basis?.toFixed(2)}</span>
            <span className="mono" style={{color:C.muted}}>now ${p.last_price?.toFixed(2)}</span>
            <span style={{flex:1}}/>
            <span className="mono" style={{fontWeight:700,color:pnl>=0?C.green:C.red}}>{pnl>=0?'+':''}${pnl.toFixed(0)} ({pnlPct>=0?'+':''}{pnlPct.toFixed(1)}%)</span>
          </div>
        );
      })}
    </div>
  );
}

// "" ACTIVE SIGNAL BANNER ""
function SignalBanner({ ind, symbols, onTrade }) {
  const active = symbols.filter(s => ind[s]?.signal?.type !== 'NEUTRAL' && ind[s]?.ml?.score >= 70);
  if (active.length === 0) return (
    <div style={{background:C.dim,border:`1px solid ${C.border}`,borderRadius:8,padding:'12px 16px',marginBottom:14,display:'flex',alignItems:'center',gap:10}}>
      <div style={{width:10,height:10,borderRadius:'50%',background:C.yellow,animation:'pulse 2s infinite'}}/>
      <span className="mono" style={{fontSize:11,color:C.muted,letterSpacing:1}}>SCANNING... waiting for conditions to align</span>
    </div>
  );
  return (
    <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:14}}>
      {active.map(sym => {
        const d = ind[sym];
        const isCall = d.signal.type === 'CALL';
        const col = isCall ? C.green : C.red;
        return (
          <div key={sym} style={{background:`${col}12`,border:`1px solid ${col}44`,borderRadius:8,padding:'10px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',cursor:'pointer'}} onClick={() => onTrade && onTrade(sym)}>
            <div style={{width:10,height:10,borderRadius:'50%',background:col,animation:'pulse 1s infinite'}}/>
            <span className="mono" style={{fontSize:14,fontWeight:700,color:col}}>{isCall ? '+ CALL' : '- PUT'}</span>
            <span className="mono" style={{fontSize:14,fontWeight:700,color:C.text}}>{sym}</span>
            <span className="mono" style={{fontSize:13,color:C.text}}>${d.price?.toFixed(2)}</span>
            <span className="mono" style={{fontSize:11,color:col,padding:'2px 8px',borderRadius:4,background:`${col}18`}}>{d.ml?.score}% AI</span>
            {d.setup && <span className="mono" style={{fontSize:10,color:C.muted}}>Entry ${d.setup.entry} | Stop ${d.setup.stop} | T1 ${d.setup.t1}</span>}
            <button onClick={e=>{e.stopPropagation();onTrade&&onTrade(sym);}} style={{marginLeft:'auto',padding:'6px 16px',borderRadius:6,border:'none',background:col,color:'#000',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1}}>TRADE NOW</button>
          </div>
        );
      })}
    </div>
  );
}

// "" DAILY RISK MANAGER ""
function RiskManager({ trades }) {
  const today = new Date().toDateString();
  const todayTrades = (trades||[]).filter(t => new Date(t.date).toDateString() === today);
  const todayPnl = todayTrades.reduce((a,t) => a + (t.pnl||0), 0);
  const todayLosses = todayTrades.filter(t => t.result === 'LOSS').length;
  const maxDailyLoss = -500; // $500 daily loss limit
  const maxLosses = 2;
  const isLocked = todayPnl <= maxDailyLoss || todayLosses >= maxLosses;

  if (!isLocked) return null;
  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.9)',display:'flex',alignItems:'center',justifyContent:'center',backdropFilter:'blur(8px)'}}>
      <div style={{background:C.panel,border:`2px solid ${C.red}`,borderRadius:16,padding:'40px 36px',maxWidth:420,textAlign:'center'}}>
        <div style={{fontSize:48,marginBottom:16}}>!</div>
        <div style={{fontFamily:'Bebas Neue',fontSize:28,color:C.red,letterSpacing:3,marginBottom:8}}>TRADING LOCKED</div>
        <div style={{fontSize:14,color:C.muted,lineHeight:1.8,marginBottom:20}}>
          {todayLosses >= maxLosses
            ? `You hit ${maxLosses} losses today. The scanner is locked to protect your capital. Come back tomorrow with a fresh mind.`
            : `Daily P&L hit ${todayPnl.toFixed(0)} (limit: ${maxDailyLoss}). Trading is locked for today.`
          }
        </div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10,marginBottom:20}}>
          <div style={{background:C.dim,borderRadius:8,padding:'10px 8px'}}>
            <div className="mono" style={{fontSize:9,color:C.muted}}>TODAY P&L</div>
            <div className="mono" style={{fontSize:18,color:todayPnl>=0?C.green:C.red}}>${todayPnl.toFixed(0)}</div>
          </div>
          <div style={{background:C.dim,borderRadius:8,padding:'10px 8px'}}>
            <div className="mono" style={{fontSize:9,color:C.muted}}>LOSSES</div>
            <div className="mono" style={{fontSize:18,color:C.red}}>{todayLosses}</div>
          </div>
          <div style={{background:C.dim,borderRadius:8,padding:'10px 8px'}}>
            <div className="mono" style={{fontSize:9,color:C.muted}}>TRADES</div>
            <div className="mono" style={{fontSize:18,color:C.text}}>{todayTrades.length}</div>
          </div>
        </div>
        <div style={{fontSize:11,color:C.muted,fontStyle:'italic'}}>"The best trade is sometimes no trade."</div>
      </div>
    </div>
  );
}

// "" ONBOARDING TUTORIAL ""
function OnboardingTutorial({ onComplete }) {
  const [step, setStep] = useState(0);
  const steps = [
    { title: 'Welcome to APEX', icon: '*', desc: 'Your AI-powered options scanner. Let me show you around in 60 seconds.', highlight: 'This quick tour will make you a power user right away.' },
    { title: 'The Scanner', icon: '*', desc: 'Each card shows a ticker with live price, mini chart, RSI, VWAP, EMA, and MACD. The traffic light tells you when conditions are right.', highlight: 'GREEN = trade setup ready. YELLOW = wait. RED = stay out.' },
    { title: 'AI Confidence', icon: '*', desc: 'Every signal gets a confidence score from 0-95%. Only trade when you see HIGH CONVICTION (80%+). The AI analyzes 6 indicators simultaneously.', highlight: 'Higher score = more indicators aligned = better trade.' },
    { title: 'Trade Setups', icon: '*', desc: 'When a signal fires, you get exact entry price, stop loss, Target 1 (take 50%), and Target 2 (take the rest). No guessing.', highlight: 'Always take 50% profit at Target 1. Always use your stop.' },
    { title: 'Your Daily Routine', icon: '*', desc: 'Check BRIEFING before open. Watch the SCANNER from 9:45am-12:30pm. Log trades in JOURNAL. Review weekly.', highlight: 'The GUIDE tab has your complete daily playbook.' },
    { title: 'You\'re Ready!', icon: '*', desc: 'Start with 1 contract. Paper trade for 2 weeks. Only go live when your win rate exceeds 55%.', highlight: 'Click below to start scanning. Good luck!' },
  ];
  const s = steps[step];
  return (
    <div style={{position:'fixed',inset:0,zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,.85)',backdropFilter:'blur(8px)',animation:'fadeIn .3s ease'}}>
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:16,padding:'40px 36px',maxWidth:440,width:'90%',textAlign:'center',position:'relative'}}>
        <div style={{fontSize:48,marginBottom:16}}>{s.icon}</div>
        <div style={{fontFamily:'Bebas Neue',fontSize:26,color:C.text,letterSpacing:3,marginBottom:8}}>{s.title}</div>
        <div style={{fontSize:14,color:C.muted,lineHeight:1.8,marginBottom:16}}>{s.desc}</div>
        <div style={{background:`${C.accent}11`,border:`1px solid ${C.accent}22`,borderRadius:8,padding:'10px 16px',marginBottom:24}}>
          <div style={{fontSize:12,color:C.accent,fontWeight:600,lineHeight:1.6}}>{s.highlight}</div>
        </div>
        {/* Progress dots */}
        <div style={{display:'flex',justifyContent:'center',gap:6,marginBottom:20}}>
          {steps.map((_,i) => (
            <div key={i} style={{width:i===step?24:8,height:8,borderRadius:4,background:i===step?C.accent:i<step?`${C.green}88`:`${C.muted}44`,transition:'all .3s'}}/>
          ))}
        </div>
        <div style={{display:'flex',gap:10,justifyContent:'center'}}>
          {step > 0 && <button onClick={()=>setStep(step-1)} style={{padding:'10px 24px',borderRadius:8,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:'inherit'}}>Back</button>}
          {step < steps.length - 1 ? (
            <button onClick={()=>setStep(step+1)} style={{padding:'10px 32px',borderRadius:8,border:'none',background:C.accent,color:'#000',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit',letterSpacing:1}}>Next '</button>
          ) : (
            <button onClick={()=>{localStorage.setItem('apex_onboarded','1');onComplete();}} style={{padding:'10px 32px',borderRadius:8,border:'none',background:C.green,color:'#000',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit',letterSpacing:1}}>Start Trading '</button>
          )}
        </div>
        <button onClick={()=>{localStorage.setItem('apex_onboarded','1');onComplete();}} style={{position:'absolute',top:12,right:16,background:'none',border:'none',color:C.muted,fontSize:18,cursor:'pointer'}}></button>
        <div className="mono" style={{fontSize:9,color:C.muted,marginTop:16,letterSpacing:1}}>STEP {step+1} OF {steps.length}</div>
      </div>
    </div>
  );
}

// "" PLAN FEATURE GATES ""
const PLAN_LIMITS = {
  starter: {
    name: 'STARTER',
    maxSymbols: 5,
    scanner: true,
    signals: true,
    trafficLight: true,
    charts: true,
    chartDrawing: false,
    flow: false,
    tradier: false,
    webhooks: false,
    backtest: false,
    backtestDays: 0,
    journal: true,
    news: true,
    briefing: true,
    multiDevice: false,
    apiAccess: false,
  },
  pro: {
    name: 'PRO',
    maxSymbols: 12,
    scanner: true,
    signals: true,
    trafficLight: true,
    charts: true,
    chartDrawing: true,
    flow: true,
    tradier: true,
    webhooks: true,
    backtest: true,
    backtestDays: 30,
    journal: true,
    news: true,
    briefing: true,
    multiDevice: false,
    apiAccess: false,
  },
  annual: {
    name: 'ANNUAL',
    maxSymbols: 12,
    scanner: true,
    signals: true,
    trafficLight: true,
    charts: true,
    chartDrawing: true,
    flow: true,
    tradier: true,
    webhooks: true,
    backtest: true,
    backtestDays: 90,
    journal: true,
    news: true,
    briefing: true,
    multiDevice: true,
    apiAccess: true,
  },
  owner: {
    name: 'OWNER',
    maxSymbols: 20,
    scanner: true, signals: true, trafficLight: true, charts: true,
    chartDrawing: true, flow: true, tradier: true, webhooks: true,
    backtest: true, backtestDays: 90, journal: true, news: true,
    briefing: true, multiDevice: true, apiAccess: true,
  },
  trial: {
    name: 'TRIAL',
    maxSymbols: 12,
    scanner: true, signals: true, trafficLight: true, charts: true,
    chartDrawing: true, flow: true, tradier: true, webhooks: true,
    backtest: true, backtestDays: 30, journal: true, news: true,
    briefing: true, multiDevice: false, apiAccess: false,
  },
};
function getPlan(planId) { return PLAN_LIMITS[planId] || PLAN_LIMITS.starter; }

// Upgrade prompt component
function UpgradeGate({ feature, plan, children }) {
  if (!plan || !feature) return children;
  const limits = getPlan(plan);
  if (limits[feature]) return children;
  return (
    <div style={{padding:'60px 20px',textAlign:'center',maxWidth:400,margin:'0 auto'}}>
      <div style={{width:64,height:64,borderRadius:'50%',background:`${C.accent}15`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 16px',fontSize:28}}>?</div>
      <div style={{fontFamily:'Bebas Neue',fontSize:24,color:C.text,letterSpacing:3,marginBottom:8}}>UPGRADE TO UNLOCK</div>
      <div style={{fontSize:13,color:C.muted,marginBottom:20,lineHeight:1.7}}>
        This feature requires a <span style={{color:C.accent,fontWeight:700}}>{feature === 'flow' ? 'Pro' : feature === 'backtest' ? 'Pro' : feature === 'tradier' ? 'Pro' : feature === 'chartDrawing' ? 'Pro' : feature === 'multiDevice' ? 'Annual' : feature === 'apiAccess' ? 'Annual' : 'Pro'}</span> plan or higher.
      </div>
      <div style={{display:'flex',gap:10,justifyContent:'center',flexWrap:'wrap'}}>
        <div style={{background:`${C.accent}15`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'12px 20px',textAlign:'center'}}>
          <div className="mono" style={{fontSize:10,color:C.muted,letterSpacing:2,marginBottom:4}}>PRO</div>
          <div style={{fontSize:20,fontWeight:800,color:C.text}}>$49<span style={{fontSize:12,color:C.muted}}>/mo</span></div>
        </div>
        <div style={{background:`${C.accent}15`,border:`1px solid ${C.accent}33`,borderRadius:8,padding:'12px 20px',textAlign:'center'}}>
          <div className="mono" style={{fontSize:10,color:C.muted,letterSpacing:2,marginBottom:4}}>ANNUAL</div>
          <div style={{fontSize:20,fontWeight:800,color:C.text}}>$399<span style={{fontSize:12,color:C.muted}}>/yr</span></div>
        </div>
      </div>
      <div className="mono" style={{fontSize:10,color:C.muted,marginTop:16,letterSpacing:1}}>Contact support@apexscanner.io to upgrade</div>
    </div>
  );
}




// "" LOADING SKELETON ""
function CardSkeleton() {
  return (
    <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16,animation:'pulse 1.5s ease infinite'}}>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
        <div style={{width:60,height:18,background:C.dim,borderRadius:4}}/>
        <div style={{width:50,height:22,background:C.dim,borderRadius:4}}/>
      </div>
      <div style={{width:120,height:28,background:C.dim,borderRadius:4,marginBottom:8}}/>
      <div style={{width:'100%',height:80,background:C.dim,borderRadius:6,marginBottom:8}}/>
      <div style={{display:'flex',gap:8}}>
        <div style={{flex:1,height:12,background:C.dim,borderRadius:3}}/>
        <div style={{flex:1,height:12,background:C.dim,borderRadius:3}}/>
        <div style={{flex:1,height:12,background:C.dim,borderRadius:3}}/>
      </div>
    </div>
  );
}

// "" ERROR BOUNDARY ""
class CardErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err) { console.error('[APEX Card Error]', err); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{background:'#080e18',border:'1px solid #0f1f35',borderRadius:8,padding:20,textAlign:'center'}}>
          <div style={{fontSize:24,marginBottom:8}}></div>
          <div style={{fontSize:12,color:'#3d5166',marginBottom:8}}>Card failed to load</div>
          <button onClick={()=>this.setState({hasError:false})} style={{padding:'6px 16px',borderRadius:4,border:'1px solid #00c9e844',background:'transparent',color:'#00c9e8',fontSize:11,cursor:'pointer'}}>Retry</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// "" WATCHLIST ""
const WATCHLIST_PRESETS = {
  'Default': ['SPY','QQQ','NVDA','AMD','MU'],
  'Tech Heavy': ['NVDA','AMD','MU','AVGO','INTC','TSM'],
  'ETF Only': ['SPY','QQQ','IWM','DIA','XLF','XLE'],
  'Mega Cap': ['AAPL','MSFT','GOOGL','AMZN','META','TSLA'],
  'Momentum': ['NVDA','TSLA','AMD','PLTR','COIN','MSTR'],
  'Earnings Play': ['SPY','QQQ','NVDA','AAPL','MSFT','AMZN'],
};

const DEFAULT_SYMBOLS = ['SPY','QQQ','NVDA','AMD','MU'];

const BASES = { SPY:590, QQQ:520, NVDA:135, AMD:165, MU:105, TSLA:290, AAPL:210, META:600, MSFT:440, AMZN:210, GOOGL:175 };

function simCandles(sym, n=80) {
  const base = BASES[sym] || 100;
  const arr = []; let p = base;
  const now = Math.floor(Date.now()/1000);
  for(let i=n-1; i>=0; i--) {
    const o = p, c = Math.max(base*0.97, Math.min(base*1.03, o+(Math.random()-0.49)*base*0.0015));
    arr.push({t:now-i*60, o, h:Math.max(o,c)+base*0.0003, l:Math.min(o,c)-base*0.0003, c, v:Math.floor(300000+Math.random()*800000)});
    p = c;
  }
  return arr;
}

function OptionsFlowTab({ symbols }) {
  const [flows, setFlows] = useState([]);
  const [darkPool, setDarkPool] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [minPremium, setMinPremium] = useState(50000);
  const [view, setView] = useState('flow');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [voice, setVoice] = useState(false);
  const symsAll = [...symbols, 'TSLA','AAPL','META','MSFT','AMZN','GOOGL','PLTR','ARM'];
  const orderTypes = ['SWEEP','BLOCK','SPLIT','GOLDEN_SWEEP'];
  const orderWeights = [0.45,0.30,0.15,0.10];
  function genFlow(n=60){return Array.from({length:n},(_,i)=>{const sym=symsAll[Math.floor(Math.random()*symsAll.length)];const type=Math.random()>0.5?'CALL':'PUT';const premium=Math.floor(Math.random()<0.08?800000+Math.random()*2000000:40000+Math.random()*900000);const rnd=Math.random();let acc=0,oti=0;for(let j=0;j<orderWeights.length;j++){acc+=orderWeights[j];if(rnd<acc){oti=j;break;}}const orderType=premium>1000000?'GOLDEN_SWEEP':orderTypes[oti];const aboveAsk=Math.random()>0.55;const unusual=premium>500000||(aboveAsk&&premium>200000);const algoScore=Math.floor(unusual?60+Math.random()*40:20+Math.random()*50);const dte=Math.floor(Math.random()*60);return{id:i,sym,type,strike:Math.round((80+Math.random()*600)*2)/2,dte,premium,aboveAsk,unusual,orderType,algoScore,expiry:new Date(Date.now()+dte*86400000).toISOString().slice(0,10),size:Math.floor(10+Math.random()*2000),time:new Date(Date.now()-Math.random()*3600000).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit'}),exchange:['CBOE','PHLX','ISE','BOX','MIAX'][Math.floor(Math.random()*5)]};}).sort((a,b)=>b.premium-a.premium);}
  function genDarkPool(){return Array.from({length:20},(_,i)=>{const sym=symsAll[Math.floor(Math.random()*symsAll.length)];const price=50+Math.random()*700;const size=Math.floor(50000+Math.random()*5000000);return{id:i,sym,price:price.toFixed(2),size:size.toLocaleString(),value:Math.floor(price*size),strength:Math.floor(40+Math.random()*60),type:Math.random()>0.5?'BLOCK':'PRINT',time:new Date(Date.now()-Math.random()*7200000).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}),direction:Math.random()>0.5?'ABOVE':'BELOW'};}).sort((a,b)=>b.value-a.value);}
  const sectorFlow=[{sector:'Technology',bullish:68,bearish:32,premium:12400000,trend:''},{sector:'Financials',bullish:55,bearish:45,premium:8200000,trend:''},{sector:'Energy',bullish:38,bearish:62,premium:5600000,trend:''},{sector:'Healthcare',bullish:61,bearish:39,premium:4900000,trend:''},{sector:'Consumer',bullish:44,bearish:56,premium:3200000,trend:''},{sector:'Materials',bullish:72,bearish:28,premium:2800000,trend:''}];
  const congressTrades=[{name:'Nancy Pelosi',party:'D',sym:'NVDA',type:'CALL',value:'$250K-$500K',date:'2026-04-28',action:'BUY'},{name:'Dan Crenshaw',party:'R',sym:'LMT',type:'STOCK',value:'$15K-$50K',date:'2026-04-22',action:'BUY'},{name:'Tommy Tuberville',party:'R',sym:'SPY',type:'PUT',value:'$50K-$100K',date:'2026-04-15',action:'BUY'},{name:'Josh Gottheimer',party:'D',sym:'MSFT',type:'CALL',value:'$100K-$250K',date:'2026-04-10',action:'BUY'},{name:'Michael McCaul',party:'R',sym:'TSLA',type:'STOCK',value:'$250K-$500K',date:'2026-04-05',action:'SELL'}];
  useEffect(()=>{setFlows(genFlow());setDarkPool(genDarkPool());setLastUpdate(new Date().toLocaleTimeString());const t=setInterval(()=>{const nf={id:Date.now(),sym:symsAll[Math.floor(Math.random()*symsAll.length)],type:Math.random()>0.5?'CALL':'PUT',premium:Math.floor(50000+Math.random()*1500000),strike:Math.round((100+Math.random()*500)*2)/2,dte:Math.floor(Math.random()*45),aboveAsk:Math.random()>0.55,unusual:Math.random()>0.7,orderType:['SWEEP','BLOCK','SPLIT'][Math.floor(Math.random()*3)],algoScore:Math.floor(40+Math.random()*60),expiry:new Date(Date.now()+Math.floor(Math.random()*45)*86400000).toISOString().slice(0,10),size:Math.floor(10+Math.random()*500),time:new Date().toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit'}),exchange:['CBOE','PHLX','ISE'][Math.floor(Math.random()*3)]};if(nf.premium>1000000)nf.orderType='GOLDEN_SWEEP';setFlows(p=>[nf,...p].slice(0,80));setLastUpdate(new Date().toLocaleTimeString());},4000);return()=>clearInterval(t);},[]);
  const filtered=flows.filter(f=>{if(filter==='CALLS')return f.type==='CALL';if(filter==='PUTS')return f.type==='PUT';if(filter==='SWEEP')return f.orderType==='SWEEP';if(filter==='GOLDEN')return f.orderType==='GOLDEN_SWEEP';if(filter==='UNUSUAL')return f.unusual;if(filter==='ABOVE_ASK')return f.aboveAsk;return true;}).filter(f=>f.premium>=minPremium);
  const fmtP=p=>p>=1000000?`${(p/1000000).toFixed(1)}M`:`${(p/1000).toFixed(0)}K`;
  const orderCol=t=>({GOLDEN_SWEEP:'#FFD700',SWEEP:C.accent,BLOCK:C.purple,SPLIT:C.muted})[t]||C.muted;
  const bullP=flows.filter(f=>f.type==='CALL').reduce((a,f)=>a+f.premium,0);const bearP=flows.filter(f=>f.type==='PUT').reduce((a,f)=>a+f.premium,0);const totalP=bullP+bearP;const bullPct=totalP>0?Math.round(bullP/totalP*100):50;
  const exportCSV=()=>{const rows=filtered.map(f=>`${f.time},${f.sym},${f.type},${f.strike},${f.expiry},${f.dte},${(f.premium/1000).toFixed(0)}K,${f.size},${f.orderType},${f.algoScore},${f.aboveAsk?'YES':'NO'},${f.exchange}`);const csv=['Time,Sym,Type,Strike,Expiry,DTE,Premium,Size,Order,AlgoScore,AboveAsk,Exchange',...rows].join('\n');const a=document.createElement('a');a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv'}));a.download='apex_flow.csv';a.click();};
  return (
    <div style={{padding:'16px 20px',maxWidth:1600,margin:'0 auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16,flexWrap:'wrap',gap:10}}>
        <div>
          <div style={{fontFamily:'Bebas Neue',fontSize:26,color:C.text,letterSpacing:2}}>OPTIONS FLOW INTELLIGENCE</div>
          <div className="mono" style={{fontSize:9,color:C.muted}}>Smart money . Dark pool . GEX . Sector . Congress . {lastUpdate}</div>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button onClick={()=>setVoice(v=>!v)} style={{padding:'6px 12px',borderRadius:4,border:`1px solid ${voice?C.green:C.border}`,background:voice?`${C.green}18`:'transparent',color:voice?C.green:C.muted,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{voice?' VOICE ON':' VOICE OFF'}</button>
          <button onClick={exportCSV} style={{padding:'6px 12px',borderRadius:4,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>CSV</button>
          <button onClick={()=>{setFlows(genFlow());setDarkPool(genDarkPool());}} style={{padding:'6px 12px',borderRadius:4,border:`1px solid ${C.accent}44`,background:`${C.accent}10`,color:C.accent,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>REFRESH</button>
        </div>
      </div>
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:14,marginBottom:14}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:10,marginBottom:12}}>
          {[['TOTAL',fmtP(totalP),C.text],['BULL',fmtP(bullP),C.green],['BEAR',fmtP(bearP),C.red],['UNUSUAL',flows.filter(f=>f.unusual).length+'*',C.yellow],['GOLDEN',flows.filter(f=>f.orderType==='GOLDEN_SWEEP').length+'*','#FFD700'],['BIAS',bullPct>55?'BULLISH':bullPct<45?'BEARISH':'NEUTRAL',bullPct>55?C.green:bullPct<45?C.red:C.yellow]].map(([l,v,c])=>(
            <div key={l} style={{background:C.dim,borderRadius:6,padding:'8px 12px'}}>
              <div style={{fontSize:8,color:C.muted,fontFamily:'JetBrains Mono',letterSpacing:1,marginBottom:3}}>{l}</div>
              <div className="mono" style={{fontSize:16,fontWeight:700,color:c}}>{v}</div>
            </div>
          ))}
        </div>
        <div>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
            <span className="mono" style={{fontSize:9,color:C.green}}>BULL {bullPct}%</span>
            <span className="mono" style={{fontSize:9,color:C.red}}>{100-bullPct}% BEAR</span>
          </div>
          <div style={{height:6,background:`${C.red}33`,borderRadius:3,overflow:'hidden'}}>
            <div style={{height:'100%',width:`${bullPct}%`,background:`linear-gradient(90deg,${C.green},${C.accent})`,borderRadius:3,transition:'width .5s'}}/>
          </div>
        </div>
      </div>
      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
        {[['flow',' LIVE FLOW'],['darkpool',' DARK POOL'],['gex','GEX'],['sector',' SECTORS'],['congress',' CONGRESS']].map(([v,l])=>(
          <button key={v} onClick={()=>setView(v)} style={{padding:'7px 14px',borderRadius:4,border:`1px solid ${view===v?C.accent:C.border}`,background:view===v?`${C.accent}18`:'transparent',color:view===v?C.accent:C.muted,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{l}</button>
        ))}
      </div>
      {view==='flow'&&(<>
        <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
            {['ALL','CALLS','PUTS','SWEEP','BLOCK','GOLDEN','UNUSUAL','ABOVE_ASK'].map(f=>(<button key={f} onClick={()=>setFilter(f)} style={{padding:'4px 10px',borderRadius:3,border:`1px solid ${filter===f?(f==='CALLS'?C.green:f==='PUTS'?C.red:f==='GOLDEN'?'#FFD700':C.accent):C.border}`,background:filter===f?`${f==='CALLS'?C.green:f==='PUTS'?C.red:f==='GOLDEN'?'#FFD700':C.accent}18`:'transparent',color:filter===f?(f==='CALLS'?C.green:f==='PUTS'?C.red:f==='GOLDEN'?'#FFD700':C.accent):C.muted,fontSize:9,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{f}</button>))}
          </div>
        </div>
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
          <div style={{maxHeight:460,overflowY:'auto'}}>
            {filtered.length===0?(<div style={{padding:32,textAlign:'center',color:C.muted,fontSize:12}}>No flow matching filters</div>):filtered.map((f,i)=>{const tCol=f.type==='CALL'?C.green:C.red;const bg=f.orderType==='GOLDEN_SWEEP'?'rgba(255,215,0,.06)':f.unusual?`${C.yellow}04`:i%2===0?'transparent':'rgba(255,255,255,.01)';return(
              <div key={f.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:bg,borderBottom:`1px solid ${C.border}20`,borderLeft:`3px solid ${f.orderType==='GOLDEN_SWEEP'?'#FFD700':f.unusual?C.yellow:'transparent'}`}}>
                <span className="mono" style={{fontSize:9,color:C.muted,minWidth:62}}>{f.time}</span>
                <span className="mono" style={{fontSize:11,color:C.text,fontWeight:700,minWidth:45}}>{f.sym}</span>
                <span className="mono" style={{fontSize:11,color:tCol,fontWeight:700,minWidth:38}}>{f.type}</span>
                <span className="mono" style={{fontSize:11,color:C.text,minWidth:55}}>${f.strike}</span>
                <span className="mono" style={{fontSize:12,color:tCol,fontWeight:700,minWidth:65}}>{fmtP(f.premium)}</span>
                <span style={{padding:'1px 5px',borderRadius:3,background:`${orderCol(f.orderType)}15`,border:`1px solid ${orderCol(f.orderType)}33`}}><span className="mono" style={{fontSize:8,color:orderCol(f.orderType),fontWeight:700}}>{f.orderType==='GOLDEN_SWEEP'?'GOLDEN':f.orderType}</span></span>
                <span className="mono" style={{fontSize:9,color:f.algoScore>70?C.green:f.algoScore>50?C.yellow:C.red}}>{f.algoScore}</span>
                {f.aboveAsk&&<span style={{fontSize:8,padding:'1px 4px',borderRadius:2,background:`${C.yellow}18`,color:C.yellow,fontFamily:'JetBrains Mono'}}>ABOVE ASK</span>}
                {f.unusual&&<span style={{fontSize:8,padding:'1px 4px',borderRadius:2,background:`${C.red}18`,color:C.red,fontFamily:'JetBrains Mono'}}>UNUSUAL</span>}
              </div>);})}
          </div>
        </div>
        <div className="mono" style={{fontSize:9,color:C.muted,marginTop:6,textAlign:'right'}}>{filtered.length} trades . min {fmtP(minPremium)}</div>
      </>)}
      {view==='darkpool'&&(<div>
        <div style={{background:`${C.purple}10`,border:`1px solid ${C.purple}33`,borderRadius:8,padding:'10px 14px',marginBottom:14}}>
          <div className="mono" style={{fontSize:10,color:C.purple,letterSpacing:2}}>DARK POOL INTELLIGENCE</div>
        </div>
        <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,overflow:'hidden'}}>
          {darkPool.map((d,i)=>(<div key={d.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',borderBottom:`1px solid ${C.border}20`,borderLeft:`3px solid ${d.strength>70?C.purple:'transparent'}`}}>
            <span className="mono" style={{fontSize:9,color:C.muted,minWidth:50}}>{d.time}</span>
            <span className="mono" style={{fontSize:11,color:C.text,fontWeight:700,minWidth:45}}>{d.sym}</span>
            <span className="mono" style={{fontSize:11,color:C.text,minWidth:60}}>${d.price}</span>
            <span className="mono" style={{fontSize:10,color:C.muted,minWidth:80}}>{d.size}</span>
            <span className="mono" style={{fontSize:11,color:C.purple,fontWeight:700,minWidth:60}}>{fmtP(d.value)}</span>
            <span className="mono" style={{fontSize:9,color:d.direction==='ABOVE'?C.green:C.red}}>{d.direction==='ABOVE'?'BULLISH':'BEARISH'}</span>
          </div>))}
        </div>
      </div>)}
      {view==='gex'&&(<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
        {symbols.slice(0,4).map(sym=>{const base={SPY:557,QQQ:476,NVDA:121,AMD:166,MU:101}[sym]||100;const gexData=Array.from({length:12},(_,i)=>{const strike=Math.round((base*(0.94+i*0.01))*2)/2;const gex=(Math.random()-0.5)*2000000000;return{strike,gex};});const maxAbs=Math.max(...gexData.map(g=>Math.abs(g.gex)));return(<div key={sym} style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}><div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.text,letterSpacing:2,marginBottom:12}}>{sym} GEX</div>{gexData.map((g,i)=>{const pct=Math.abs(g.gex)/maxAbs*100;return(<div key={i} style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}><div className="mono" style={{fontSize:9,color:C.text,width:50,textAlign:'right'}}>${g.strike}</div><div style={{flex:1,height:10,background:C.dim,borderRadius:3,overflow:'hidden'}}><div style={{height:'100%',width:`${pct*0.7}%`,background:g.gex>0?C.green:C.red,borderRadius:3}}/></div><div className="mono" style={{fontSize:8,color:g.gex>0?C.green:C.red,width:50}}>{g.gex>0?'+':'-'}${(Math.abs(g.gex)/1000000).toFixed(0)}M</div></div>);})}</div>);})}
      </div>)}
      {view==='sector'&&(<div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(260px,1fr))',gap:14}}>{sectorFlow.map(s=>{const bc=s.bullish>55?C.green:s.bullish<45?C.red:C.yellow;return(<div key={s.sector} style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:14}}><div style={{fontSize:14,fontWeight:700,color:C.text,marginBottom:8}}>{s.sector}</div><div className="mono" style={{fontSize:18,fontWeight:700,color:bc,marginBottom:8}}>{fmtP(s.premium)}</div><div style={{height:5,background:`${C.red}33`,borderRadius:3,overflow:'hidden',marginBottom:5}}><div style={{height:'100%',width:`${s.bullish}%`,background:bc,borderRadius:3}}/></div><div style={{display:'flex',justifyContent:'space-between'}}><span className="mono" style={{fontSize:9,color:C.green}}>BULL {s.bullish}%</span><span className="mono" style={{fontSize:9,color:C.red}}>{s.bearish}% BEAR</span></div></div>);})}</div>)}
      {view==='congress'&&(<div><div style={{background:`${C.yellow}10`,border:`1px solid ${C.yellow}33`,borderRadius:8,padding:'10px 14px',marginBottom:14}}><div className="mono" style={{fontSize:10,color:C.yellow,letterSpacing:2}}>CONGRESSIONAL TRADING</div></div>{congressTrades.map((t,i)=>(<div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'11px 12px',background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,marginBottom:6}}><span style={{fontSize:12,fontWeight:700,color:C.text,minWidth:130}}>{t.name}</span><span style={{fontSize:9,padding:'2px 8px',borderRadius:3,background:t.party==='D'?'rgba(59,130,246,.2)':'rgba(239,68,68,.2)',color:t.party==='D'?'#60a5fa':'#f87171',fontFamily:'JetBrains Mono',fontWeight:700}}>{t.party}</span><span className="mono" style={{fontSize:12,color:C.text,fontWeight:700}}>{t.sym}</span><span className="mono" style={{fontSize:11,color:t.type==='CALL'?C.green:t.type==='PUT'?C.red:C.accent}}>{t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</span><span className="mono" style={{fontSize:11,color:C.yellow}}>{t.value}</span><span style={{fontSize:10,padding:'2px 8px',borderRadius:3,background:t.action==='BUY'?`${C.green}18`:`${C.red}18`,color:t.action==='BUY'?C.green:C.red,fontFamily:'JetBrains Mono',fontWeight:700}}>{t.action}</span></div>))}</div>)}
    </div>
  );
}

function TradeJournalTab() {
  const [trades, setTrades] = useState(() => JSON.parse(localStorage.getItem('apex_journal')||'[]'));
  const [form, setForm] = useState({sym:'SPY',type:'CALL',entry:'*',exit:'*',contracts:1,result:'WIN',setup:'VWAP_RECLAIM',notes:''});
  const [showForm, setShowForm] = useState(false);

  const save = () => {
    const t = {...form, id:Date.now(), date:new Date().toISOString(), pnl:
      form.result==='WIN' ? Math.abs(parseFloat(form.exit||0)-parseFloat(form.entry||0))*form.contracts*100 :
      -Math.abs(parseFloat(form.exit||0)-parseFloat(form.entry||0))*form.contracts*100
    };
    const next = [t, ...trades];
    setTrades(next); localStorage.setItem('apex_journal', JSON.stringify(next));
    setShowForm(false);
  };

  const wins = trades.filter(t=>t.result==='WIN');
  const totalPnl = trades.reduce((a,t)=>a+(t.pnl||0),0);
  const winRate = trades.length ? (wins.length/trades.length*100).toFixed(1) : 0;

  return (
    <div style={{padding:'16px 20px',maxWidth:1000,margin:'0 auto'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
        <div style={{fontFamily:'Bebas Neue',fontSize:26,color:C.text,letterSpacing:2}}>TRADE JOURNAL</div>
        <button onClick={()=>setShowForm(s=>!s)} style={{padding:'8px 18px',borderRadius:4,border:`1px solid ${C.accent}44`,background:`${C.accent}10`,color:C.accent,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>+ LOG TRADE</button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:16}}>
        {[['TOTAL TRADES',trades.length,C.accent],['WIN RATE',`${winRate}%`,parseFloat(winRate)>=50?C.green:C.red],['TOTAL P&L',`$${totalPnl.toFixed(0)}`,totalPnl>=0?C.green:C.red],['WINS',wins.length,C.green],['LOSSES',trades.length-wins.length,C.red]].map(([l,v,c])=>(
          <div key={l} style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 14px',textAlign:'center'}}>
            <div style={{fontSize:8,color:C.muted,fontFamily:'JetBrains Mono',letterSpacing:1,marginBottom:4}}>{l}</div>
            <div className="mono" style={{fontSize:20,fontWeight:700,color:c}}>{v}</div>
          </div>
        ))}
      </div>

      {showForm && (
        <div style={{background:C.panel,border:`1px solid ${C.accent}44`,borderRadius:8,padding:16,marginBottom:16}}>
          <div className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2,marginBottom:12}}>LOG TRADE</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:10,marginBottom:12}}>
            {[['Symbol','sym','text'],['Entry Price','entry','number'],['Exit Price','exit','number'],['Contracts','contracts','number'],['Notes','notes','text']].map(([l,k,t])=>(
              <div key={k}>
                <div style={{fontSize:10,color:C.muted,marginBottom:4}}>{l}</div>
                <input value={form[k]} onChange={e=>setForm(f=>({...f,[k]:t==='number'?parseFloat(e.target.value)||0:e.target.value.toUpperCase()}))}
                  style={{width:'100%',padding:'7px 10px',borderRadius:4,background:C.dim,border:`1px solid ${C.border}`,color:C.text,fontSize:12,fontFamily:'JetBrains Mono'}}/>
              </div>
            ))}
            <div>
              <div style={{fontSize:10,color:C.muted,marginBottom:4}}>Type</div>
              <div style={{display:'flex',gap:5}}>
                {['CALL','PUT'].map(t=><button key={t} onClick={()=>setForm(f=>({...f,type:t}))} style={{flex:1,padding:'7px 0',borderRadius:4,border:`1px solid ${form.type===t?C.accent:C.border}`,background:form.type===t?`${C.accent}18`:'transparent',color:form.type===t?C.accent:C.muted,fontSize:11,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{t}</button>)}
              </div>
            </div>
            <div>
              <div style={{fontSize:10,color:C.muted,marginBottom:4}}>Result</div>
              <div style={{display:'flex',gap:5}}>
                {['WIN','LOSS'].map(r=><button key={r} onClick={()=>setForm(f=>({...f,result:r}))} style={{flex:1,padding:'7px 0',borderRadius:4,border:`1px solid ${form.result===r?(r==='WIN'?C.green:C.red):C.border}`,background:form.result===r?`${r==='WIN'?C.green:C.red}18`:'transparent',color:form.result===r?(r==='WIN'?C.green:C.red):C.muted,fontSize:11,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{r}</button>)}
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:10}}>
            <button onClick={()=>setShowForm(false)} style={{flex:1,padding:'10px 0',borderRadius:4,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,fontSize:12,cursor:'pointer',fontFamily:'JetBrains Mono'}}>CANCEL</button>
            <button onClick={save} style={{flex:2,padding:'10px 0',borderRadius:4,border:'none',background:C.accent,color:'#000',fontSize:12,fontWeight:900,cursor:'pointer',fontFamily:'JetBrains Mono'}}>SAVE TRADE</button>
          </div>
        </div>
      )}

      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {trades.length===0?(
          <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:32,textAlign:'center',color:C.muted,fontSize:12}}>No trades logged yet. Click + LOG TRADE after each trade.</div>
        ):trades.map(t=>(
          <div key={t.id} style={{background:C.panel,border:`1px solid ${t.result==='WIN'?C.green+'44':C.red+'44'}`,borderRadius:8,padding:'10px 14px',borderLeft:`3px solid ${t.result==='WIN'?C.green:C.red}`}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:4}}>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <span className="mono" style={{fontSize:13,fontWeight:700,color:C.accent}}>{t.sym}</span>
                <span className="mono" style={{fontSize:11,color:t.type==='CALL'?C.green:C.red}}>{t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</span>
                <span style={{fontSize:10,color:C.muted}}>{t.setup}</span>
              </div>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                <span className="mono" style={{fontSize:13,fontWeight:700,color:t.result==='WIN'?C.green:C.red}}>{t.result==='WIN'?'+':'-'}${Math.abs(t.pnl||0).toFixed(0)}</span>
                <button onClick={()=>{const n=trades.filter(x=>x.id!==t.id);setTrades(n);localStorage.setItem('apex_journal',JSON.stringify(n));}} style={{background:'none',border:'none',color:C.red,cursor:'pointer',fontSize:14}}>x</button>
              </div>
            </div>
            {t.notes&&<div style={{fontSize:11,color:C.muted,fontStyle:'italic'}}>{t.notes}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

//  Alerts Tab 
function AlertsTab({ alerts, ind, setOrderSig }) {
  if(alerts.length===0) return (
    <div style={{padding:'16px 20px'}}><div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:32,textAlign:'center',color:C.muted,fontSize:12}}>No alerts yet - signals fire when indicators cross key levels during market hours</div></div>
  );
  return (
    <div style={{padding:'16px 20px',maxWidth:900,margin:'0 auto'}}>
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:12}}>SIGNAL HISTORY + AI ANALYSIS ({alerts.length} alerts)</div>
      {[...alerts].reverse().map(a=>(
        <div key={a.id} style={{background:C.panel,border:`1px solid ${C.border}`,borderLeft:`3px solid ${a.color}`,borderRadius:8,padding:14,marginBottom:8,animation:'fadeIn .3s ease'}}>
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
            <span className="mono" style={{fontSize:13,fontWeight:700,color:a.color}}>{a.symbol} - {a.type}</span>
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              {a.ml&&<span className="mono" style={{fontSize:9,padding:'2px 8px',borderRadius:3,background:`${a.ml.color}18`,color:a.ml.color}}>{a.ml.score}% {a.ml.grade}</span>}
              <span className="mono" style={{fontSize:10,color:C.muted}}>{a.time}</span>
            </div>
          </div>
          <div style={{fontSize:12,color:C.text,marginBottom:a.setup?10:0}}>{a.message}</div>
          {a.setup&&(
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:6,marginBottom:10}}>
              {[['ENTRY',`$${a.setup.entry}`,a.color],['T1',`$${a.setup.t1}`,C.green],['T2',`$${a.setup.t2}`,C.green],['STOP',`$${a.setup.stop}`,C.red]].map(([l,v,c])=>(
                <div key={l} style={{background:C.dim,borderRadius:4,padding:'6px 8px',textAlign:'center'}}>
                  <div style={{fontSize:8,color:C.muted,marginBottom:2}}>{l}</div>
                  <div className="mono" style={{fontSize:11,fontWeight:700,color:c}}>{v}</div>
                </div>
              ))}
            </div>
          )}
          {a.price&&<button onClick={()=>setOrderSig({sym:a.symbol,type:a.type,price:a.price,setup:a.setup,ind:ind[a.symbol]})} style={{width:'100%',padding:'7px 0',borderRadius:4,border:`1px solid ${a.color}44`,background:`${a.color}11`,color:a.color,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1}}>VIEW OPTIONS CHAIN -> WEBULL</button>}
        </div>
      ))}
    </div>
  );
}

//  Settings Tab 




const STOCK_DB=[['SPY','SPDR S&P 500 ETF','ETF'],['QQQ','Invesco QQQ Trust','ETF'],['IWM','iShares Russell 2000','ETF'],['DIA','SPDR Dow Jones','ETF'],['XLF','Financial Select Sector','ETF'],['XLE','Energy Select Sector','ETF'],['XLK','Technology Select Sector','ETF'],['GLD','SPDR Gold Shares','ETF'],['TLT','iShares 20+ Year Treasury','ETF'],['AAPL','Apple Inc','Stock'],['MSFT','Microsoft Corp','Stock'],['GOOGL','Alphabet Inc','Stock'],['AMZN','Amazon.com Inc','Stock'],['META','Meta Platforms Inc','Stock'],['NVDA','NVIDIA Corp','Stock'],['TSLA','Tesla Inc','Stock'],['AMD','Advanced Micro Devices','Stock'],['MU','Micron Technology','Stock'],['INTC','Intel Corp','Stock'],['AVGO','Broadcom Inc','Stock'],['TSM','Taiwan Semiconductor','Stock'],['NFLX','Netflix Inc','Stock'],['CRM','Salesforce Inc','Stock'],['ORCL','Oracle Corp','Stock'],['PLTR','Palantir Technologies','Stock'],['COIN','Coinbase Global','Stock'],['ARM','Arm Holdings','Stock'],['SMCI','Super Micro Computer','Stock'],['QCOM','Qualcomm Inc','Stock'],['JPM','JPMorgan Chase','Stock'],['GS','Goldman Sachs','Stock'],['V','Visa Inc','Stock'],['MA','Mastercard Inc','Stock'],['WMT','Walmart Inc','Stock'],['COST','Costco Wholesale','Stock'],['HD','Home Depot Inc','Stock'],['DIS','Walt Disney Co','Stock'],['PFE','Pfizer Inc','Stock'],['JNJ','Johnson & Johnson','Stock'],['UNH','UnitedHealth Group','Stock'],['LLY','Eli Lilly & Co','Stock'],['XOM','Exxon Mobil Corp','Stock'],['CVX','Chevron Corp','Stock'],['BA','Boeing Co','Stock'],['LMT','Lockheed Martin','Stock'],['F','Ford Motor Co','Stock'],['GM','General Motors','Stock'],['RIVN','Rivian Automotive','Stock'],['NIO','NIO Inc','Stock'],['BABA','Alibaba Group','Stock'],['UBER','Uber Technologies','Stock'],['ABNB','Airbnb Inc','Stock'],['SNAP','Snap Inc','Stock'],['GME','GameStop Corp','Stock'],['AMC','AMC Entertainment','Stock'],['HOOD','Robinhood Markets','Stock'],['SOFI','SoFi Technologies','Stock'],['SNOW','Snowflake Inc','Stock'],['CRWD','CrowdStrike Holdings','Stock'],['PANW','Palo Alto Networks','Stock'],['NET','Cloudflare Inc','Stock'],['SHOP','Shopify Inc','Stock'],['ROKU','Roku Inc','Stock'],['SPOT','Spotify Technology','Stock'],['MSTR','MicroStrategy Inc','Stock'],['DDOG','Datadog Inc','Stock']];
function SymbolSearch({onSelect,symbols}){const[query,setQuery]=useState('');const[focused,setFocused]=useState(false);const results=query.length>0?STOCK_DB.filter(([s,n])=>s.includes(query)||n.toUpperCase().includes(query)).slice(0,10):[];return(<div style={{position:'relative'}}><div style={{display:'flex',alignItems:'center',gap:8,background:C.dim,border:'1px solid '+(focused?C.accent:C.border),borderRadius:8,padding:'0 12px'}}><span style={{color:C.muted,fontSize:14}}>Q</span><input value={query} onChange={e=>setQuery(e.target.value.toUpperCase())} onFocus={()=>setFocused(true)} onBlur={()=>setTimeout(()=>setFocused(false),200)} placeholder="Search stocks & ETFs..." style={{flex:1,padding:'12px 0',background:'transparent',border:'none',color:C.text,fontSize:14,fontFamily:'JetBrains Mono',outline:'none'}}/>{query&&<button onClick={()=>setQuery('')} style={{background:'none',border:'none',color:C.muted,cursor:'pointer'}}>x</button>}</div>{results.length>0&&focused&&<div style={{position:'absolute',top:'100%',left:0,right:0,background:C.panel,border:'1px solid '+C.border,borderRadius:'0 0 8px 8px',maxHeight:300,overflowY:'auto',zIndex:100,boxShadow:'0 8px 30px rgba(0,0,0,.4)'}}>{results.map(([sym,name,type],i)=>{const added=symbols.includes(sym);return<div key={sym+i} onClick={()=>{if(!added){onSelect(sym);setQuery('');}}} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 14px',cursor:added?'default':'pointer',borderBottom:'1px solid '+C.border+'20'}}><div style={{width:28,height:28,borderRadius:6,background:C.accent+'15',display:'flex',alignItems:'center',justifyContent:'center'}}><span className="mono" style={{fontSize:9,color:C.accent,fontWeight:700}}>{sym.substring(0,2)}</span></div><span className="mono" style={{fontSize:13,fontWeight:700,color:added?C.accent:C.text}}>{sym}</span><span style={{fontSize:11,color:C.muted,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{name}</span><span className="mono" style={{fontSize:9,color:C.muted,padding:'2px 6px',borderRadius:3,background:C.dim}}>{type}</span>{added?<span className="mono" style={{fontSize:9,color:C.accent}}>ADDED</span>:<span className="mono" style={{fontSize:9,color:C.green}}>+ ADD</span>}</div>})}</div>}</div>);}


// "" AUTO-TRADE PANEL ""
function AutoTradePanel({ config, setConfig }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/auto-trade/config`).then(r=>r.json()).then(d=>setConfig(d)).catch(()=>{});
    fetch(`${API}/api/auto-trade/history`).then(r=>r.json()).then(d=>setHistory(d.trades||[])).catch(()=>{});
  }, []);

  const toggle = async (key, val) => {
    const updated = {...config, [key]: val};
    setConfig(updated);
    await fetch(`${API}/api/auto-trade/config`, {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(updated)});
  };

  return (
    <div style={{background:C.panel,border:`1px solid ${config.enabled?C.green:C.border}`,borderRadius:8,padding:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div>
          <div className="mono" style={{fontSize:9,color:config.enabled?C.green:C.accent,letterSpacing:2}}>APEX AUTOPILOT ENGINE</div>
          <div style={{fontSize:11,color:C.muted,marginTop:2}}>{config.enabled?'Executing trades automatically when signals fire':'Disabled - signals shown but not executed'}</div>
        </div>
        <button onClick={()=>toggle('enabled',!config.enabled)} style={{padding:'8px 20px',borderRadius:6,border:'none',background:config.enabled?C.red:C.green,color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>{config.enabled?'STOP':'START'}</button>
      </div>

      {config.enabled && (
        <div style={{background:`${C.red}08`,border:`1px solid ${C.red}33`,borderRadius:6,padding:'8px 12px',marginBottom:14}}>
          <div className="mono" style={{fontSize:9,color:C.red}}> AUTO-TRADING IS {config.sandbox?'SANDBOX (PAPER)':'LIVE (REAL MONEY)'}</div>
        </div>
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:10,marginBottom:14}}>
        <div style={{background:C.dim,borderRadius:6,padding:'8px 12px'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>MODE</div>
          <button onClick={()=>toggle('sandbox',!config.sandbox)} className="mono" style={{fontSize:12,fontWeight:700,color:config.sandbox?C.yellow:C.red,background:'none',border:'none',cursor:'pointer',padding:0}}>{config.sandbox?'SANDBOX':'LIVE'}</button>
        </div>
        <div style={{background:C.dim,borderRadius:6,padding:'8px 12px'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>MIN CONFIDENCE</div>
          <select value={config.minConfidence} onChange={e=>toggle('minConfidence',parseInt(e.target.value))} style={{background:'transparent',border:'none',color:C.text,fontSize:14,fontWeight:700,fontFamily:'JetBrains Mono'}}>{[70,75,80,85,90].map(v=><option key={v} value={v}>{v}%</option>)}</select>
        </div>
        <div style={{background:C.dim,borderRadius:6,padding:'8px 12px'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>MAX DAILY TRADES</div>
          <select value={config.maxDailyTrades} onChange={e=>toggle('maxDailyTrades',parseInt(e.target.value))} style={{background:'transparent',border:'none',color:C.text,fontSize:14,fontWeight:700,fontFamily:'JetBrains Mono'}}>{[1,2,3,5,10].map(v=><option key={v} value={v}>{v}</option>)}</select>
        </div>
        <div style={{background:C.dim,borderRadius:6,padding:'8px 12px'}}>
          <div className="mono" style={{fontSize:8,color:C.muted}}>CONTRACTS</div>
          <select value={config.contractSize} onChange={e=>toggle('contractSize',parseInt(e.target.value))} style={{background:'transparent',border:'none',color:C.text,fontSize:14,fontWeight:700,fontFamily:'JetBrains Mono'}}>{[1,2,3,5,10].map(v=><option key={v} value={v}>{v}</option>)}</select>
        </div>
      </div>

      {history.length > 0 && (
        <div>
          <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:8}}>TODAY'S AUTO-TRADES ({history.length})</div>
          {history.slice(0,10).map((t,i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'6px 0',borderBottom:`1px solid ${C.border}20`,fontSize:11}}>
              <span className="mono" style={{color:C.muted,minWidth:60}}>{new Date(t.time).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'})}</span>
              <span className="mono" style={{color:C.accent,fontWeight:700,minWidth:40}}>{t.symbol}</span>
              <span className="mono" style={{color:t.type==='CALL'?C.green:C.red,minWidth:35}}>{t.optionType||(t.direction==="BULL"?"CALL":"PUT")}</span>
              <span className="mono" style={{color:C.text}}>${t.strike} @ ${t.price}</span>
              <span className="mono" style={{color:C.yellow,marginLeft:'auto'}}>{t.sandbox?'SANDBOX':'LIVE'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// "" TELEGRAM CONFIG COMPONENT ""


function TelegramConfig() {
  const [token, setToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [status, setStatus] = useState(null);
  const [msg, setMsg] = useState('');
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/telegram/status`).then(r => r.json()).then(d => setStatus(d)).catch(() => {});
  }, []);

  const save = async () => {
    setMsg('');
    const r = await fetch(`${API}/api/telegram/config`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ botToken: token, chatId })
    });
    const d = await r.json();
    if (d.ok) { setMsg('Saved!'); setStatus({ configured: true, hasToken: true, hasChatId: true }); }
  };

  const test = async () => {
    setMsg('Sending...');
    const r = await fetch(`${API}/api/telegram/test`, { method: 'POST' });
    const d = await r.json();
    setMsg(d.ok ? 'Test sent! Check Telegram.' : 'Failed: ' + (d.error || 'Unknown error'));
  };

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
        <div style={{width:8,height:8,borderRadius:'50%',background:status?.configured?C.green:C.red}}/>
        <span className="mono" style={{fontSize:10,color:status?.configured?C.green:C.muted}}>{status?.configured ? 'CONNECTED' : 'NOT CONFIGURED'}</span>
        {status?.configured && <button onClick={test} className="mono" style={{marginLeft:'auto',padding:'4px 12px',borderRadius:4,border:`1px solid ${C.accent}44`,background:`${C.accent}11`,color:C.accent,fontSize:10,cursor:'pointer'}}>SEND TEST</button>}
      </div>

      <button onClick={()=>setShowSetup(!showSetup)} style={{padding:'8px 16px',borderRadius:4,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,fontSize:11,cursor:'pointer',fontFamily:'JetBrains Mono',marginBottom:showSetup?14:0,width:'100%',textAlign:'left'}}>{showSetup ? '- Hide Setup Guide' : '- Setup Guide (3 minutes)'}</button>

      {showSetup && (
        <div style={{marginTop:8}}>
          <div style={{background:C.dim,border:`1px solid ${C.border}`,borderRadius:8,padding:14,marginBottom:14}}>
            <div className="mono" style={{fontSize:9,color:C.yellow,letterSpacing:2,marginBottom:10}}>HOW TO SET UP TELEGRAM ALERTS</div>
            {[
              ['1', 'Open Telegram and search for @BotFather'],
              ['2', 'Send /newbot and follow the prompts to create your bot'],
              ['3', 'BotFather gives you a token like 123456:ABC-xyz " paste it below'],
              ['4', 'Open your new bot in Telegram and send it any message'],
              ['5', 'Search for @userinfobot in Telegram " it tells you your Chat ID'],
              ['6', 'Paste your Chat ID below and click Save'],
            ].map(([num, text]) => (
              <div key={num} style={{display:'flex',gap:10,alignItems:'flex-start',marginBottom:8}}>
                <div style={{width:20,height:20,borderRadius:'50%',background:`${C.accent}22`,border:`1px solid ${C.accent}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span className="mono" style={{fontSize:9,color:C.accent,fontWeight:700}}>{num}</span>
                </div>
                <div style={{fontSize:11,color:C.text,paddingTop:2,lineHeight:1.5}}>{text}</div>
              </div>
            ))}
          </div>

          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            <div>
              <label className="mono" style={{fontSize:9,color:C.muted,letterSpacing:1,display:'block',marginBottom:4}}>BOT TOKEN</label>
              <input value={token} onChange={e=>setToken(e.target.value)} placeholder="123456:ABC-DEF..." style={{width:'100%',padding:'10px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:C.dim,color:C.text,fontSize:12,fontFamily:'JetBrains Mono',outline:'none'}}/>
            </div>
            <div>
              <label className="mono" style={{fontSize:9,color:C.muted,letterSpacing:1,display:'block',marginBottom:4}}>CHAT ID</label>
              <input value={chatId} onChange={e=>setChatId(e.target.value)} placeholder="123456789" style={{width:'100%',padding:'10px 12px',borderRadius:6,border:`1px solid ${C.border}`,background:C.dim,color:C.text,fontSize:12,fontFamily:'JetBrains Mono',outline:'none'}}/>
            </div>
            <button onClick={save} style={{padding:'10px 20px',borderRadius:6,border:'none',background:C.accent,color:'#000',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>Save Configuration</button>
          </div>
          {msg && <div className="mono" style={{fontSize:11,color:msg.includes('Failed')?C.red:C.green,marginTop:8}}>{msg}</div>}
        </div>
      )}
    </div>
  );
}

function SettingsTab({ plan, userPlan, symbols, setSymbols, sound, setSound, theme, setTheme }) {
  const DEF = ['SPY','QQQ','NVDA','AMD','MU'];

  const addTicker = () => {
    const t = newTick.trim().toUpperCase();
    if(t && !symbols.includes(t) && symbols.length<12) { setSymbols(s=>[...s,t]); setNewTick(''); }
  };

  return (
    <div style={{padding:'16px 20px',maxWidth:800,margin:'0 auto',display:'flex',flexDirection:'column',gap:14}}>
      {/* Theme */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:12}}>THEME</div>
        <div style={{display:'flex',gap:8}}>
          {Object.keys(THEMES).map(t=>(
            <button key={t} onClick={()=>{setTheme(t);C=THEMES[t]||THEMES.dark;localStorage.setItem('apex_theme',t);setRefreshKey(k=>k+1);}} style={{flex:1,padding:'10px 0',borderRadius:4,border:`1px solid ${theme===t?C.accent:C.border}`,background:theme===t?`${C.accent}22`:'transparent',color:theme===t?C.accent:C.muted,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1}}>
              {t.toUpperCase()}
            </button>
          ))}
        </div>
      </div>
      {/* Sound */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:12}}>SOUND ALERTS</div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          <button onClick={()=>setSound(s=>!s)} style={{padding:'8px 18px',borderRadius:4,border:`1px solid ${sound?C.green:C.border}`,background:sound?`${C.green}18`:'transparent',color:sound?C.green:C.muted,fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{sound?' SOUND ON':' SOUND OFF'}</button>
          {[['TEST CALL','CALL'],['TEST PUT','PUT']].map(([l,t])=>(
            <button key={l} onClick={()=>beep(t)} style={{padding:'8px 14px',borderRadius:4,border:`1px solid ${C.border}`,background:'transparent',color:C.muted,fontSize:10,cursor:'pointer',fontFamily:'JetBrains Mono'}}>{l}</button>
          ))}
        </div>
      </div>
      {/* Watchlist */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:8}}>QUICK PRESETS</div>
            <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:12}}>ACTIVE ({symbols.length}/12)</div>
        <div style={{display:'flex',flexWrap:'wrap',gap:8,marginBottom:12}}>
            <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:8}}>ACTIVE ({symbols.length}/12)</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:14}}>{Object.entries(WATCHLIST_PRESETS).map(([name,ticks])=>(<button key={name} onClick={()=>setSymbols(ticks)} style={{padding:"5px 12px",borderRadius:4,border:"1px solid "+(JSON.stringify(symbols)===JSON.stringify(ticks)?C.accent:C.border),background:JSON.stringify(symbols)===JSON.stringify(ticks)?C.accent+"18":"transparent",color:JSON.stringify(symbols)===JSON.stringify(ticks)?C.accent:C.muted,fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"JetBrains Mono",letterSpacing:1}}>{name}</button>))}</div>
            {symbols.map(sym=>(
            <div key={sym} style={{display:'flex',alignItems:'center',gap:6,padding:'5px 12px',borderRadius:4,background:C.dim,border:`1px solid ${C.border}`}}>
              <span className="mono" style={{fontSize:12,fontWeight:700,color:C.accent}}>{sym}</span>
              {symbols.length>1&&<button onClick={()=>setSymbols(s=>s.filter(x=>x!==sym))} style={{background:'none',border:'none',color:C.red,cursor:'pointer',fontSize:14,lineHeight:1}}>x</button>}
            </div>
          ))}
        </div>
        <SymbolSearch onSelect={(sym)=>{if(!symbols.includes(sym)&&symbols.length<12)setSymbols(s=>[...s,sym]);}} symbols={symbols}/>
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <input id="manualTick" placeholder="Or type ticker..." maxLength={6} style={{flex:1,padding:"8px 12px",borderRadius:4,background:C.dim,border:"1px solid "+C.border,color:C.text,fontSize:11,fontFamily:"JetBrains Mono"}} onKeyDown={e=>{if(e.key==="Enter"){const v=e.target.value.toUpperCase().trim();if(v&&!symbols.includes(v)&&symbols.length<12){setSymbols(s=>[...s,v]);e.target.value="";}}}} />
          <button onClick={()=>{const el=document.getElementById("manualTick");const v=(el?.value||"").toUpperCase().trim();if(v&&!symbols.includes(v)&&symbols.length<12){setSymbols(s=>[...s,v]);el.value="";}}} style={{padding:"8px 16px",borderRadius:4,border:"1px solid "+C.accent+"44",background:C.accent+"11",color:C.accent,fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"JetBrains Mono"}}>ADD</button>
        </div>
        <div style={{display:"flex",gap:8,marginTop:8}}>
          <button onClick={()=>setSymbols(DEFAULT_SYMBOLS)} style={{padding:"8px 12px",borderRadius:4,border:`1px solid ${C.border}`,background:"transparent",color:C.muted,fontSize:10,cursor:"pointer",fontFamily:"JetBrains Mono"}}>RESET</button>
        </div>
      </div>
      
      {/* Beta Feedback */}
      <FeedbackForm/>

      {/* Telegram Alerts */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2,marginBottom:12}}>TELEGRAM PUSH ALERTS</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14,lineHeight:1.7}}>Get trade signals sent to your phone via Telegram. Alerts fire instantly when APEX detects a setup.</div>
        <TelegramConfig />
      </div>
      {/* TradingView Connection Guide */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16}}>
        <div className="mono" style={{fontSize:9,color:C.yellow,letterSpacing:2,marginBottom:12}}>TRADINGVIEW CONNECTION GUIDE</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {[
            ['1','Open TradingView.com and load any chart (SPY, QQQ, etc.)'],
            ['2','Click the Alerts bell icon at the top of the chart'],
            ['3','Click Create Alert'],
            ['4','Set your condition (example: EMA Cross, VWAP cross, RSI above 70)'],
            ['5','Scroll down to Alert Actions section'],
            ['6','Check the box: Webhook URL'],
            ['7','Paste this URL into the webhook field:'],
            ['8','Set the Message field to this exact text:'],
            ['9','Click Save - APEX receives the alert instantly when triggered'],
          ].map(([num,text])=>(
            <div key={num} style={{display:'flex',gap:10,alignItems:'flex-start'}}>
              <div style={{width:22,height:22,borderRadius:'50%',background:`${C.accent}22`,border:`1px solid ${C.accent}`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <span className="mono" style={{fontSize:10,color:C.accent,fontWeight:700}}>{num}</span>
              </div>
              <div style={{fontSize:12,color:C.text,paddingTop:3,lineHeight:1.5}}>{text}</div>
            </div>
          ))}
          <div style={{background:C.dim,border:`1px solid ${C.accent}33`,borderRadius:6,padding:12,marginTop:4}}>
            <div style={{fontSize:9,color:C.muted,marginBottom:4,fontFamily:'JetBrains Mono'}}>STEP 7 - PASTE THIS URL:</div>
            <div className="mono" style={{fontSize:11,color:C.accent,wordBreak:'break-all'}}>http://10.0.0.221:3000/api/webhook/tradingview</div>
          </div>
          <div style={{background:C.dim,border:`1px solid ${C.yellow}33`,borderRadius:6,padding:12}}>
            <div style={{fontSize:9,color:C.muted,marginBottom:4,fontFamily:'JetBrains Mono'}}>STEP 8 - PASTE THIS MESSAGE:</div>
            <div className="mono" style={{fontSize:10,color:C.yellow,wordBreak:"break-all"}}>Action:buy Symbol:ticker Price:close</div>
          </div>
          <div style={{background:`${C.green}08`,border:`1px solid ${C.green}22`,borderRadius:6,padding:10}}>
            <div style={{fontSize:11,color:C.green,fontWeight:700,marginBottom:4}}>What happens when TradingView fires an alert:</div>
            <div style={{fontSize:11,color:C.muted,lineHeight:1.7}}>
              APEX receives the signal instantly . The ticker appears in your Alerts tab . A sound plays . You see the entry/exit setup . You decide whether to trade it
            </div>
          </div>
        </div>
      </div>
      {/* Info */}
      <div style={{background:C.panel,border:`1px solid ${C.border}`,borderRadius:8,padding:16,display:'flex',alignItems:'center',gap:16}}>
        <Logo size={44}/>
        <div>
          <div style={{fontFamily:'Bebas Neue',fontSize:20,color:C.accent,letterSpacing:4}}>APEX Scanner Pro</div>
          <div className="mono" style={{fontSize:10,color:C.muted,marginTop:2}}>v8.0 . AI + Flow + Dark Pool + GEX + Congress</div>
          <div className="mono" style={{fontSize:9,color:C.muted,marginTop:2}}>Finnhub . Tradier . TradingView . Webull</div>
        </div>
      </div>
    </div>
  );
}

//  News Tab 
function NewsTab({ news, ticker }) {
  const demo=[{symbol:'SPY',headline:'Pre-market EMA cross detected - watch VWAP',source:'APEX',sentiment:'bear',time:'09:28'},{symbol:'QQQ',headline:'VWAP test in progress - rejection or reclaim?',source:'APEX',sentiment:'neutral',time:'09:25'},{symbol:'NVDA',headline:'RSI approaching oversold - call reversal watch',source:'APEX',sentiment:'bull',time:'09:22'}];
  const items = news.length>0 ? news : demo;
  return (
    <div style={{padding:'16px 20px',maxWidth:900,margin:'0 auto'}}>
      <div className="mono" style={{fontSize:9,color:C.muted,letterSpacing:2,marginBottom:12}}>{news.length>0?`${news.length} LIVE ARTICLES`:'DEMO NEWS - ADD FINNHUB KEY FOR LIVE'}</div>
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {items.map((n,i)=>(
          <div key={i} onClick={()=>n.url&&window.open(n.url,'_blank')} style={{background:C.panel,border:`1px solid ${C.border}`,borderLeft:`3px solid ${n.sentiment==='bull'?C.green:n.sentiment==='bear'?C.red:C.muted}`,borderRadius:8,padding:'10px 14px',cursor:n.url?'pointer':'default'}}>
            <div style={{display:'flex',justifyContent:'space-between',gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                  <span className="mono" style={{fontSize:11,fontWeight:700,color:C.accent}}>{n.symbol}</span>
                  <span style={{fontSize:9,color:C.muted}}>{n.source} . {n.time}</span>
                </div>
                <div style={{fontSize:12,color:C.text,lineHeight:1.5}}>{n.headline}</div>
              </div>
              <span className="mono" style={{fontSize:9,padding:'2px 8px',borderRadius:3,fontWeight:700,alignSelf:'flex-start',background:n.sentiment==='bull'?`${C.green}18`:n.sentiment==='bear'?`${C.red}18`:`${C.muted}18`,color:n.sentiment==='bull'?C.green:n.sentiment==='bear'?C.red:C.muted}}>{n.sentiment?.toUpperCase()}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

//  MAIN APP 
export default function App() {
  const [tab, setTab] = useState('scanner');
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(()=>localStorage.getItem('apex_disclaimer')==='accepted');
  const [showOnboarding, setShowOnboarding] = useState(()=>!localStorage.getItem('apex_onboarded'));
  const [autoTrade, setAutoTrade] = useState({enabled:false,sandbox:true,minConfidence:80,maxDailyTrades:5,contractSize:1,riskPct:3,maxDailyLoss:300,accountSize:5000});
  useEffect(()=>{fetch(API+'/api/auto-trade/config').then(r=>r.json()).then(d=>setAutoTrade(d)).catch(()=>{});},[]);
  const [dailyPnl, setDailyPnl] = useState(()=>{try{const j=JSON.parse(localStorage.getItem('apex_journal')||'[]');const today=new Date().toDateString();return j.filter(t=>new Date(t.date).toDateString()===today).reduce((a,t)=>a+(t.pnl||0),0);}catch{return 0;}});
  const [todayTrades, setTodayTrades] = useState(()=>{try{const j=JSON.parse(localStorage.getItem('apex_journal')||'[]');const today=new Date().toDateString();return j.filter(t=>new Date(t.date).toDateString()===today);}catch{return [];}});
  const todayWins = todayTrades.filter(t=>t.result==='WIN').length;
  const todayLosses = todayTrades.filter(t=>t.result==='LOSS').length;
  const [theme, setTheme] = useState(()=>localStorage.getItem('apex_theme')||'dark');
  const [refreshKey, setRefreshKey] = useState(0);
  const [tradeMode, setTradeMode] = useState('manual');
  const [showModeHelp, setShowModeHelp] = useState(false);
  const [license, setLicense] = useState(()=>localStorage.getItem('apex_license')||null);
  const [userPlan, setUserPlan] = useState(()=>localStorage.getItem('apex_plan')||'owner');
  const [useSandbox, setUseSandboxHeader] = useState(true);
  useEffect(()=>{ const syncMode=()=>fetch(API+'/api/bot/mode').then(r=>r.json()).then(d=>setUseSandboxHeader(d.useSandbox)).catch(()=>{}); syncMode(); const t=setInterval(syncMode,30000); return ()=>clearInterval(t); },[]);
  const [symbols, setSymbols] = useState(()=>{try{const s=JSON.parse(localStorage.getItem('apex_symbols'));return s&&s.length?s:DEFAULT_SYMBOLS;}catch{return DEFAULT_SYMBOLS;}});
  useEffect(()=>{
    localStorage.setItem('apex_symbols',JSON.stringify(symbols));
    // Generate candles for any new symbol
    symbols.forEach(s=>{
      if(!candlesRef.current[s]) candlesRef.current[s]=simCandles(s);
    });
    // Subscribe new symbols to WebSocket
    if(wsRef.current && wsRef.current.readyState === 1) {
      symbols.forEach(s=>{
        try { wsRef.current.send(JSON.stringify({type:'subscribe',symbol:s})); } catch(e){}
      });
    }
  },[symbols]);
  const [ind, setInd] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [orderSig, setOrderSig] = useState(null);
  const [tradierSig, setTradierSig] = useState(null);
  const [time, setTime] = useState(new Date());
  const [ticker, setTicker] = useState(0);
  const [news, setNews] = useState([]);
  const [wsLive, setWsLive] = useState(false);
  const [sound, setSound] = useState(true);

  // Update C colors when theme changes
  C = THEMES[theme] || THEMES.dark;

  const candlesRef = useRef({});
  const wsRef = useRef(null);
  const prevRef = useRef({});
  const alertId = useRef(0);
  const soundRef = useRef(true);
  const symRef = useRef(DEFAULT_SYMBOLS);
  const realPricesLoaded = useRef(false);

  useEffect(()=>{ soundRef.current=sound; },[sound]);
  useEffect(()=>{ symRef.current=symbols; },[symbols]);
  useEffect(()=>{ document.body.style.background=C.bg; document.body.style.color=C.text; },[theme]);

  useEffect(()=>{
    symbols.forEach(s=>{ if(!candlesRef.current[s]) candlesRef.current[s]=simCandles(s); });

    // Init real prices immediately
    const initPrices = async () => {
      try {
        const r = await fetch(`${API}/api/quotes?symbols=${symRef.current.join(',')}&sandbox=false`);
        const d = await r.json();
        if(!d.quotes?.length) return;
        d.quotes.forEach(q=>{
          const sym=q.symbol, p=q.last||0;
          if(!p||p<=0) return;
          const now=Math.floor(Date.now()/1000), arr=[];
          let price=p;
          for(let i=79;i>=1;i--){
            const move=(Math.random()-0.5)*p*0.0008;
            price=Math.max(p*0.985,Math.min(p*1.015,price-move));
            const o=price, c=price+(Math.random()-0.5)*p*0.0006;
            arr.unshift({t:now-i*60,o:parseFloat(o.toFixed(2)),h:parseFloat((Math.max(o,c)+p*0.0002).toFixed(2)),l:parseFloat((Math.min(o,c)-p*0.0002).toFixed(2)),c:parseFloat(c.toFixed(2)),v:Math.floor(200000+Math.random()*600000)});
          }
          arr.push({t:now,o:parseFloat((q.open||p).toFixed(2)),h:parseFloat((q.high||p).toFixed(2)),l:parseFloat((q.low||p).toFixed(2)),c:parseFloat(p.toFixed(2)),v:q.volume||0});
          candlesRef.current[sym]=arr;
        });
        realPricesLoaded.current=true;
        setWsLive(true);
      } catch(e){ console.log('[APEX] Price init error:',e.message); }
    };
    initPrices();

    // Quote poll every 2 seconds
    const quotePoll = setInterval(async()=>{
      try {
        const r=await fetch(`${API}/api/quotes?symbols=${symRef.current.join(',')}&sandbox=false`);
        const d=await r.json();
        if(!d.quotes?.length) return;
        d.quotes.forEach(q=>{
          const sym=q.symbol, p=q.last||0;
          if(!p||p<=0) return;
          const arr=candlesRef.current[sym];
          if(!arr?.length) return;
          const last=arr[arr.length-1], now=Math.floor(Date.now()/1000);
          if(now-last.t>=60){
            candlesRef.current[sym]=[...arr.slice(-199),{t:now,o:q.open||last.c,h:q.high||Math.max(last.c,p),l:q.low||Math.min(last.c,p),c:p,v:q.volume||0}];
          } else {
            const u=[...arr]; u[u.length-1]={...last,c:p,h:Math.max(last.h,p),l:Math.min(last.l,p),v:q.volume||last.v||0};
            candlesRef.current[sym]=u;
          }
        });
        realPricesLoaded.current=true;
        setWsLive(true);
      } catch {}
    },5000);

    // Indicator calc + alert detection
    let tick=0;
    const timer=setInterval(()=>{
      tick++;
      const syms=symRef.current;

      // Sim tick only when real prices not loaded
      if(!realPricesLoaded.current){
        syms.forEach(sym=>{
          const arr=candlesRef.current[sym]; if(!arr?.length) return;
          const last=arr[arr.length-1], now=Math.floor(Date.now()/1000), base=BASES[sym]||100;
          const nc=Math.max(base*0.97,Math.min(base*1.03,last.c+(Math.random()-0.49)*base*0.0006));
          if(now-last.t>=60){candlesRef.current[sym]=[...arr.slice(-199),{t:now,o:last.c,h:Math.max(last.c,nc)+base*0.0003,l:Math.min(last.c,nc)-base*0.0003,c:nc,v:Math.floor(200000+Math.random()*600000)}];}
          else{const u=[...arr];u[u.length-1]={...last,c:nc,h:Math.max(last.h,nc),l:Math.min(last.l,nc)};candlesRef.current[sym]=u;}
        });
      }

      setTime(new Date());

      if(tick%1===0){
        const next={};
        syms.forEach(s=>{const c=candlesRef.current[s];if(c?.length)next[s]=calc(c);});
        setInd(next);
        syms.forEach(sym=>{
          const cur=next[sym]?.signal?.type, prev=prevRef.current[sym];
          if(cur&&cur!=='NEUTRAL'&&prev!==cur){
            const ml=next[sym]?.ml, setup=next[sym]?.setup;
            const msgs={CALL:['VWAP reclaimed - call setup active','EMA9 crossed above EMA21','RSI bounce - momentum building'],PUT:['VWAP rejected - put setup active','EMA death cross confirmed','RSI overbought - rollover setup']};
            const id=++alertId.current;
            setAlerts(p=>[...p.slice(-19),{id,symbol:sym,type:cur,message:msgs[cur]?.[Math.floor(Math.random()*3)]||'Signal',color:next[sym]?.signal?.color,time:new Date().toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit'}),setup,ml,price:next[sym]?.price}]);
            if(soundRef.current)beep(cur);
            // Generate AI reasoning for alert
            const aiPrompt = sym+' '+cur+' at $'+next[sym]?.price?.toFixed(2)+' | VWAP $'+next[sym]?.vwap?.toFixed(2)+' | RSI '+next[sym]?.rsi?.toFixed(0)+' | AI '+next[sym]?.ml?.score+'%';
            console.log('[APEX AI]', aiPrompt);
          // Auto-trade execution
          if(autoTrade?.enabled && tradeMode==='auto' && ml?.score >= (autoTrade.minConfidence||80)) {
            fetch(`${API}/api/auto-trade/execute`, {
              method:'POST', headers:{'Content-Type':'application/json'},
              body: JSON.stringify({symbol:sym, type:cur, confidence:ml.score, entry:setup?.entry, stop:setup?.stop, t1:setup?.t1, sandbox:autoTrade.sandbox})
            }).then(r=>r.json()).then(d=>{
              if(d.ok) console.log('[AUTO-TRADE] Executed:', d.trade);
              else console.log('[AUTO-TRADE] Skipped:', d.reason);
            }).catch(e=>console.log('[AUTO-TRADE] Error:', e.message));
          }
            setTimeout(()=>setAlerts(p=>p.filter(a=>a.id!==id)),120000);
          }
          prevRef.current[sym]=cur;
        });
      }
      if(tick%16===0)setTicker(n=>n+1);
    },500);

    // Finnhub WebSocket
    fetch(`${API}/api/finnhub/token`).then(r=>r.json()).then(d=>{
      if(!d.token)return;
      wsRef.current=null;const ws=new WebSocket(`wss://ws.finnhub.io?token=${d.token}`);
      ws.onopen=()=>{wsRef.current=ws;setWsLive(true);symRef.current.forEach(s=>ws.send(JSON.stringify({type:'subscribe',symbol:s})));};
      ws.onmessage=e=>{
        try{
          const msg=JSON.parse(e.data);if(msg.type!=='trade'||!msg.data)return;
          msg.data.forEach(tr=>{
            const sym=tr.s;if(!symRef.current.includes(sym))return;
            const arr=candlesRef.current[sym];if(!arr?.length)return;
            const last=arr[arr.length-1],now=Math.floor(tr.t/1000),p=tr.p;
            if(now-last.t>=60){candlesRef.current[sym]=[...arr.slice(-199),{t:now,o:last.c,h:p,l:p,c:p,v:tr.v||0}];}
            else{const u=[...arr];u[u.length-1]={...last,c:p,h:Math.max(last.h,p),l:Math.min(last.l,p),v:(last.v||0)+(tr.v||0)};candlesRef.current[sym]=u;}
            realPricesLoaded.current=true;
          });
        }catch{}
      };
      ws.onerror=()=>{}; ws.onclose=()=>{};
    }).catch(()=>{});

    // News
    const fetchNews=()=>{
      Promise.all(symRef.current.map(sym=>
        fetch(`${API}/api/news?symbol=${sym}`).then(r=>r.json()).then(d=>(d.articles||[]).slice(0,3).map(n=>({symbol:sym,headline:n.headline,source:n.source,url:n.url,time:new Date(n.datetime*1000).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}),sentiment:/beat|surge|rally|bullish|above|record/i.test(n.headline)?'bull':/miss|fall|drop|weak|bearish|below|cut/i.test(n.headline)?'bear':'neutral'}))).catch(()=>[])
      )).then(all=>setNews(all.flat().slice(0,25)));
    };
    fetchNews();
    const newsTimer=setInterval(fetchNews,3*60*1000);

    return ()=>{ clearInterval(timer); clearInterval(newsTimer); clearInterval(quotePoll); if(wsRef.current){wsRef.current.close();} };
  },[]);

  const getCandles = s=>candlesRef.current[s]||[];
  const marketOpen = (() => {
  const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const day = et.getDay();
  const mins = et.getHours() * 60 + et.getMinutes();
  return day >= 1 && day <= 5 && mins >= 570 && mins < 960;
})();
  const tickerItems=news.length>0?news:[{symbol:'APEX',headline:'Scanning for signals... waiting for market conditions to align'}];
  const tickerText=`${tickerItems[ticker%tickerItems.length]?.symbol}: ${tickerItems[ticker%tickerItems.length]?.headline}`;

  if(!license) return <LicenseGate onUnlock={k=>{setLicense(k.key||k);setUserPlan(k.plan||'owner');localStorage.setItem('apex_license',k.key||k);localStorage.setItem('apex_plan',k.plan||'owner');}}/>;
  const plan = getPlan(userPlan);

  // Show onboarding for first-time users


  const TABS=[
    {id:'scanner',label:'SCANNER'},
    {id:'positions',label:'POSITIONS'},
    {id:'briefing',label:'BRIEFING'},
    {id:'journal',label:'JOURNAL'},
    {id:'alerts',label:`ALERTS${alerts.length?` (${alerts.length})`:''}`},
    {id:'accuracy',label:'STATS'},
    {id:'smartmoney',label:'SMART MONEY'},
    {id:'news',label:'NEWS'},
    {id:'settings',label:'SETTINGS'},
    {id:'guide',label:'LEARN'},
  ];

  return (
    <div style={{background:C.bg,minHeight:'100vh',transition:'background .3s'}}>
      <style>{CSS}</style>
      {/* Header */}
      <div style={{background:C.panel,borderBottom:'1px solid '+C.border,padding:'8px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',position:'sticky',top:0,zIndex:200,boxShadow:'0 2px 8px rgba(0,0,0,.12)',flexWrap:'wrap',gap:8}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <Logo size={34}/>
          <div>
            <div style={{fontFamily:'Bebas Neue',fontSize:22,color:C.text,letterSpacing:4,lineHeight:1}}>APEX</div>
            <div className="mono" style={{fontSize:8,color:C.muted,letterSpacing:3}}>SCANNER v8 . AI</div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
          {[[wsLive?C.green:C.yellow,wsLive?'LIVE . FINNHUB':'SIMULATED'],[marketOpen?C.green:C.red,marketOpen?'OPEN':'CLOSED']].map(([col,label],i)=>(<div key={i} style={{display:'flex',alignItems:'center',gap:5}}><div style={{width:6,height:6,borderRadius:'50%',background:col,animation:'pulse 2s infinite'}}/><span className='mono' style={{fontSize:9,color:C.text,letterSpacing:1,fontWeight:600}}>{label}</span></div>))}
          <button onClick={async()=>{ const ns=!useSandbox; if(!ns){if(!window.confirm('Switch to LIVE TRADING? Real money on account 6YB79458.'))return;} setUseSandboxHeader(ns); try{await fetch(API+'/api/bot/mode',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({useSandbox:ns})});}catch(e){} }} style={{padding:'3px 10px',borderRadius:4,border:'1px solid '+(useSandbox?C.accent:C.red),background:useSandbox?C.accent+'18':C.red+'18',color:useSandbox?C.accent:C.red,fontSize:9,cursor:'pointer',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:1}}>● {useSandbox?'PAPER':'LIVE'}</button>
          <span className="mono" style={{fontSize:9,background:userPlan==='owner'?'#7c4dff22':userPlan==='annual'?'#00c9e822':userPlan==='pro'?'#00c9e822':userPlan==='trial'?'#ffc40022':'#e2e8f0',color:userPlan==='owner'?'#7c4dff':userPlan==='annual'?'#00c9e8':'#64748b',padding:'2px 8px',borderRadius:4,letterSpacing:2,fontWeight:700,border:'1px solid '+(userPlan==='owner'?'#7c4dff44':'#00c9e844')}}>{plan.name}</span>
          <span className="mono" style={{fontSize:13,color:C.text,fontWeight:800}}>{time.toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}</span>
          {/* Theme toggles */}
          <div style={{display:'flex',gap:3,flexWrap:'wrap',maxWidth:220}}>
            {Object.entries(THEMES).map(([t,th])=>{
              const dotCol = {dark:'#f5f5f5',midnight:'#0078d7',slate:'#22d3ee',charcoal:'#0a84ff'}[t]||'#fff';
              const isActive = theme===t;
              return (
                <button key={t} onClick={()=>{setTheme(t);C=THEMES[t]||THEMES.dark;localStorage.setItem('apex_theme',t);setRefreshKey(k=>k+1);}}
                  style={{display:'flex',alignItems:'center',gap:4,padding:'3px 7px',borderRadius:4,
                    border:`1px solid ${isActive?'#ffffff':' rgba(255,255,255,.2)'}`,
                    background:isActive?'#1e293b':'#e2e8f0',
                    cursor:'pointer',transition:'all .15s'}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:dotCol,flexShrink:0}}/>
                  <span style={{color:isActive?'#ffffff':'#1e293b',fontSize:8,fontWeight:700,fontFamily:'JetBrains Mono',letterSpacing:.5,whiteSpace:'nowrap'}}>{t.toUpperCase()}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* News ticker */}
      <div style={{background:'#0a0a0a',borderBottom:'1px solid #222',padding:'6px 20px',display:'flex',alignItems:'center',gap:10}}>
        <span className="mono" style={{fontSize:10,color:'#00e676',letterSpacing:2,whiteSpace:'nowrap',fontWeight:700}}>LIVE</span>
        <div style={{width:6,height:6,borderRadius:'50%',background:'#00e676',animation:'pulse 2s infinite'}}/>
        <div style={{width:1,height:12,background:'#333'}}/>
        <div style={{flex:1,overflow:'hidden'}}>
          {tickerItems.length > 0 && (() => {
            const item = tickerItems[ticker % tickerItems.length];
            const url = item?.url || item?.link;
            return (
              <div key={ticker} style={{display:'flex',alignItems:'center',gap:8,animation:'fadeIn .4s ease',cursor:url?'pointer':'default'}} onClick={()=>{if(url)window.open(url,'_blank');}}>
                <span className="mono" style={{fontSize:11,color:'#00bcd4',fontWeight:700,flexShrink:0}}>{item?.symbol||'APEX'}</span>
                <span style={{fontSize:13,color:'#e0e0e0',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontWeight:500}}>{item?.headline||item?.title||'Scanning...'}</span>
                {url && <span className="mono" style={{fontSize:8,color:'#00bcd4',flexShrink:0,opacity:.7}}>READ</span>}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Tabs */}
      <div style={{background:C.panel,borderBottom:'1px solid '+C.border,display:'flex',overflowX:'auto',position:'sticky',top:53,zIndex:190,boxShadow:'0 1px 4px rgba(0,0,0,.08)'}}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{background:'none',border:'none',borderBottom:`2px solid ${tab===t.id?C.accent:'transparent'}`,color:tab===t.id?C.accent:C.muted,padding:'10px 16px',cursor:'pointer',fontSize:11,fontWeight:700,letterSpacing:2,whiteSpace:'nowrap'}}>{t.label}</button>
        ))}
      </div>

      {/* Content */}
      {tab==='briefing'&&<PreMarketBriefing symbols={symbols} ind={ind}/>}
      
      {tab==='scanner'&&(<div style={{padding:'16px 20px',maxWidth:1600,margin:'0 auto',paddingBottom:40}}>
          <OpenCountdown/>

      {/* Trading Mode Bar */}
      <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:'8px 12px',marginBottom:12,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
        <span className="mono" style={{fontSize:9,color:C.muted,letterSpacing:1}}>MODE:</span>
        {[
          ['manual','MANUAL','Signals only - you trade manually',C.green],
          ['semi','SEMI-AUTO','Click to send orders to Tradier',C.yellow],
          ['auto','AUTOPILOT','AI trades for you (Pro/Annual)',C.red],
        ].map(([id,label,desc,col])=>(
          <button key={id} onClick={()=>{setTradeMode(id);if(id==='auto'){
              if(userPlan==='starter'||userPlan==='trial'){
                alert('APEX Autopilot is available on Pro and Annual plans.\n\nUpgrade to Pro ($49/mo) to unlock AI-powered auto-trading.\n\nGo to Settings to upgrade your plan.');
                return;
              }
              setAutoTrade(p=>({...p,enabled:true}));
              fetch(API+'/api/auto-trade/config',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({enabled:true})});
            }else{setAutoTrade(p=>({...p,enabled:false}));fetch(API+'/api/auto-trade/config',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({enabled:false})})}}} style={{padding:'6px 14px',borderRadius:20,border:'1px solid '+(tradeMode===id?col:C.border),background:tradeMode===id?col+'18':'transparent',color:tradeMode===id?col:C.muted,fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'JetBrains Mono',letterSpacing:1,transition:'all .2s'}}>{label}</button>
        ))}
        <span style={{fontSize:10,color:C.muted,marginLeft:8}}>{tradeMode==='manual'?'Signals shown. Trade in your broker app.':tradeMode==='semi'?'Click SEND ORDER on any signal card.':'APEX Autopilot active. AI executing trades automatically.'}</span>
        {tradeMode==='auto'&&<span className="mono" style={{fontSize:9,color:'#fff',marginLeft:'auto',padding:'4px 12px',borderRadius:4,background:'linear-gradient(135deg,'+C.red+','+C.purple+')',letterSpacing:1,boxShadow:'0 0 12px '+C.red+'33'}}>AUTOPILOT ON</span>}
        <button onClick={()=>setShowModeHelp(h=>!h)} className="mono" style={{padding:'4px 8px',borderRadius:4,border:'1px solid '+C.border,background:'transparent',color:C.muted,fontSize:8,cursor:'pointer',letterSpacing:1}}>?</button>
      </div>
      {showModeHelp && (
        <div style={{background:C.panel,border:'1px solid '+C.border,borderRadius:8,padding:14,marginBottom:12}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <span className="mono" style={{fontSize:9,color:C.accent,letterSpacing:2}}>HOW EACH MODE WORKS</span>
            <button onClick={()=>setShowModeHelp(false)} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:14}}>x</button>
          </div>
          {tradeMode==='manual' && (
            <div style={{fontSize:11,color:C.text,lineHeight:1.8}}>
              <b style={{color:C.green}}>MANUAL MODE:</b> APEX shows you the signal with entry, stop, and target prices. You open your own broker app (Webull, Robinhood, etc) and enter the trade yourself. When Target 1 hits, sell 50%. Move stop to breakeven. Let the rest ride to Target 2. You control everything.
            </div>
          )}
          {tradeMode==='semi' && (
            <div style={{fontSize:11,color:C.text,lineHeight:1.8}}>
              <b style={{color:C.yellow}}>SEMI-AUTO MODE:</b> When a signal fires, click the SEND ORDER button on the card. The order goes to Tradier or Alpaca with the correct option and price pre-filled. Your position shows in the POSITIONS tab with live P&L. Click EXIT to close. You approve every trade.
            </div>
          )}
          {tradeMode==='auto' && (
            <div style={{fontSize:11,color:C.text,lineHeight:1.8}}>
              <b style={{color:C.red}}>FULL AUTO MODE:</b> APEX executes trades automatically when signals meet your threshold (default 80%+ confidence). Configure max daily trades, contract size, and loss limits in Settings. You get Telegram alerts for every auto-trade. Stops automatically after hitting daily limits.
              <div style={{background:C.red+'10',border:'1px solid '+C.red+'33',borderRadius:4,padding:'6px 10px',marginTop:6}}>
                <span style={{fontSize:10,color:C.red}}>Start in SANDBOX (paper trading). Only go live after 2+ weeks of proven profitability.</span>
              </div>
            </div>
          )}
        </div>
      )}

          <TradingHUD C={C} />
          <MarkovStatusBar C={C} />
          <DailyDashboard pnl={dailyPnl} wins={todayWins} losses={todayLosses} trades={todayTrades}/>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(min(300px,100%),1fr))',gap:14}}>
            {symbols.map(sym=>(
              <CardErrorBoundary key={sym+'eb'}><Card sym={sym} candles={getCandles(sym)} ind={ind[sym]}
                onOrderClick={setOrderSig} onTradierClick={setTradierSig}/></CardErrorBoundary>
            ))}
          </div>
        </div>
      )}
      
      {tab==='smartmoney'&&<SmartMoneyTab/>}
      {tab==='journal'&&<><ApexJournal C={C}/><TradeJournalTab/><AutoJournal/></>}
      {tab==='alerts'&&<AlertsTab alerts={alerts} ind={ind} setOrderSig={setOrderSig}/>}
      {tab==='news'&&<NewsTab news={news} ticker={ticker}/>}
      {tab==='guide'&&<LearnTab/>}
      {tab==='accuracy'&&<SignalAccuracyTab ind={ind} symbols={symbols}/>}
      {tab==='settings'&&<SettingsTab plan={plan} userPlan={userPlan} symbols={symbols} setSymbols={setSymbols} sound={sound} setSound={setSound} theme={theme} setTheme={setTheme}/>}

      {/* Modals */}
      {tradierSig&&<TradierOrderPanel sym={tradierSig.sym} type={tradierSig.type} price={tradierSig.price} setup={tradierSig.setup} ind={tradierSig.ind} onClose={()=>setTradierSig(null)}/>}
      {orderSig&&<WebullOrderPanel sym={orderSig.sym} type={orderSig.type} price={orderSig.price} setup={orderSig.setup} onClose={()=>setOrderSig(null)}/>}

      {(() => { try { const jt=JSON.parse(localStorage.getItem('apex_journal')||'[]'); return <RiskManager trades={jt}/>; } catch { return null; } })()}
      {!disclaimerAccepted && <DisclaimerModal onAccept={()=>{
        setDisclaimerAccepted(true);
        localStorage.setItem('apex_disclaimer','accepted');
        // Log acceptance to server
        const email = localStorage.getItem('apex_license')||'unknown';
        fetch(API+'/api/disclaimer/accept',{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify({user:email,accepted:true,timestamp:new Date().toISOString(),userAgent:navigator.userAgent})
        }).catch(()=>{});
      }}/>}
      {showOnboarding && <OnboardingTutorial onComplete={()=>setShowOnboarding(false)}/>}
      {/* Alert notifications - bottom right, auto-dismiss */}
      {alerts.length > 0 && (
        <div style={{position:'fixed',bottom:16,right:16,zIndex:9999,maxWidth:360}}>
          {alerts.slice(-1).map(a=>(
            <div key={a.id} style={{background:C.panel,border:`1px solid ${a.color}33`,borderLeft:`3px solid ${a.color}`,borderRadius:6,padding:'4px 8px',display:'flex',alignItems:'center',gap:6,boxShadow:'0 2px 8px rgba(0,0,0,.4)',fontSize:9,animation:'fadeIn .3s ease',opacity:0.95}}
              ref={el=>{if(el){setTimeout(()=>{setAlerts(p=>p.filter(x=>x.id!==a.id));},10000);}}}>
              <span className="mono" style={{color:a.color,fontWeight:700,fontSize:9}}>{a.symbol} {a.type}</span>
              <span style={{color:C.muted,flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontSize:9}}>{a.message}</span>
              <span className="mono" style={{color:C.accent,fontSize:8}}>{a.ml?.score||'--'}%</span>
              <button onClick={(e)=>{e.stopPropagation();setAlerts(p=>p.filter(x=>x.id!==a.id));}} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:10,padding:'0 2px'}}>x</button>
            </div>
          ))}</div>
      )}
</div>
  );
}

































